<template>
    <div style="width: 98%; margin: auto;">
        <!-- Loading Overlay -->
        <loading :active="items_being_submitted"
                :can-cancel="false"
                :is-full-page="true">
        </loading>

        <!-- Mode Selector -->
        <b-card class="mb-3 shadow-sm">
            <div class="d-flex align-items-center">
                <div class="mr-4">
                    <b-form-radio-group
                        v-model="productEntryMode"
                        :options="[
                            { text: 'Collection Products', value: 'collection' },
                            { text: 'Simple Products', value: 'simple' }
                        ]"
                        buttons
                        button-variant="outline-primary"
                        size="lg"
                        class="mode-selector">
                    </b-form-radio-group>
                </div>
                <div v-if="productEntryMode === 'simple'" class="text-warning">
                    <b-icon-info-circle class="mr-1"></b-icon-info-circle>
                    Simple mode: Manual SKU & Description, no collection required
                </div>
                <div v-else class="text-primary">
                    <b-icon-info-circle class="mr-1"></b-icon-info-circle>
                    Collection mode: Auto-generated SKUs, hierarchical products
                </div>
            </div>
        </b-card>

        <!-- Barcode Scanner Modal -->
        <b-modal 
            id="modal-product-barcode-scanner" 
            title="Scan Product Barcode" 
            size="lg" 
            hide-footer
            @shown="startProductScanner"
            @hidden="stopProductScanner">
            <div class="scanner-container">
                <QuaggaScanner 
                    ref="productQuaggaScanner"
                    @scan="handleProductBarcodeScan"
                    :facingMode="productCurrentCamera"
                />
                
                <div class="mt-3 text-center">
                    <div class="mt-2">
                        <b-button 
                            size="sm" 
                            variant="outline-secondary" 
                            @click="toggleProductCamera">
                            Switch Camera
                        </b-button>
                        <b-button 
                            size="sm" 
                            variant="outline-danger" 
                            @click="stopProductScanner" 
                            class="ml-2">
                            Cancel
                        </b-button>
                    </div>
                </div>
            </div>
        </b-modal>

        <!-- Group Quantity Configuration Modal -->
        <b-modal 
            id="modal-group-quantity-config"
            title="Configure Group Quantities"
            size="lg"
            @ok="applyGroupQuantities"
            @hidden="resetGroupQuantityModal"
            centered>
            
            <div class="quantity-config-container">
                <div class="alert alert-info mb-3">
                    <b-icon-info-circle class="mr-2"></b-icon-info-circle>
                    Set the quantity for each item in this group. You can create multiple variations from the same combination.
                    <br><small>Example: 1 Table + 4 Chairs, 1 Table + 6 Chairs</small>
                </div>
                
                <div v-for="(product, idx) in groupQuantityConfig.products" 
                     :key="idx"
                     class="product-quantity-item mb-3 p-3 border rounded">
                    
                    <div class="d-flex justify-content-between align-items-center">
                        <div class="d-flex align-items-center">
                            <div v-if="product.image_url" class="mr-2">
                                <img :src="product.image_url" style="width: 40px; height: 40px; object-fit: cover; border-radius: 4px;">
                            </div>
                            <div>
                                <span class="font-weight-bold">{{ product.subcategory_name }}</span>
                                <small class="text-muted d-block">SKU: {{ product.product_sku }}</small>
                            </div>
                        </div>
                        <div class="d-flex align-items-center">
                            <label class="mr-2 mb-0">Qty:</label>
                            <b-input 
                                v-model.number="product.quantity" 
                                type="number" 
                                min="1"
                                step="1"
                                size="sm"
                                style="width: 80px;"
                                @input="updateQuantityTotals">
                            </b-input>
                        </div>
                    </div>
                    
                    <div class="mt-2 d-flex justify-content-between text-muted small">
                        <span>FOB: ${{ formatNumber(product.fob_price) }}</span>
                        <span>× {{ product.quantity }}</span>
                        <span class="font-weight-bold">= ${{ formatNumber(product.fob_price * product.quantity) }}</span>
                    </div>
                </div>
                
                <div class="totals-section mt-3 p-3 bg-light rounded">
                    <div class="d-flex justify-content-between">
                        <span class="font-weight-bold">Total FOB Price:</span>
                        <span class="font-weight-bold text-primary">${{ formatNumber(groupQuantityConfig.totalFob) }}</span>
                    </div>
                    <div class="d-flex justify-content-between">
                        <span class="font-weight-bold">Total Selling Price:</span>
                        <span class="font-weight-bold text-success">${{ formatNumber(groupQuantityConfig.totalSelling) }}</span>
                    </div>
                    <div class="d-flex justify-content-between">
                        <span class="font-weight-bold">Total Cubes:</span>
                        <span class="font-weight-bold text-info">{{ groupQuantityConfig.totalCubes }}</span>
                    </div>
                </div>
                
                <!-- Multiple Variations Section -->
                <div class="variations-section mt-4">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                        <label class="font-weight-bold mb-0">Variations to Create:</label>
                        <b-button 
                            size="sm" 
                            variant="outline-success"
                            @click="addVariation">
                            <b-icon-plus></b-icon-plus> Add Variation
                        </b-button>
                    </div>
                    
                    <div v-for="(variation, varIdx) in groupQuantityConfig.variations" 
                         :key="varIdx"
                         class="variation-item p-2 border rounded mb-2">
                        <div class="d-flex justify-content-between align-items-center">
                            <div>
                                <span class="badge badge-secondary mr-2">Variation {{ varIdx + 1 }}</span>
                                <span class="small">
                                    {{ getVariationDescription(variation) }}
                                </span>
                            </div>
                            <div>
                                <b-button 
                                    size="sm" 
                                    variant="outline-primary"
                                    @click="duplicateVariation(varIdx)"
                                    class="mr-1">
                                    <b-icon-files></b-icon-files>
                                </b-button>
                                <b-button 
                                    size="sm" 
                                    variant="outline-danger"
                                    @click="removeVariation(varIdx)"
                                    v-if="groupQuantityConfig.variations.length > 1">
                                    <b-icon-x></b-icon-x>
                                </b-button>
                            </div>
                        </div>
                    </div>
                    
                    <small class="text-muted d-block mt-2">
                        Each variation will create a separate product group with the specified quantities.
                    </small>
                </div>
            </div>
            
            <template slot="modal-footer">
                <b-button variant="secondary" @click="$bvModal.hide('modal-group-quantity-config')">
                    Cancel
                </b-button>
                <b-button variant="primary" @click="applyGroupQuantities" :disabled="!isGroupQuantityValid">
                    <b-icon-check></b-icon-check> Create {{ groupQuantityConfig.variations.length }} Group(s)
                </b-button>
            </template>
        </b-modal>

        <!-- Success Modal -->
        <b-modal v-model="showSuccessModal" title="Success!" ok-only @ok="resetAfterSuccess" size="lg" centered>
            <div class="text-center py-4">
                <div class="success-animation">
                    <b-icon-check-circle variant="success" font-scale="5"></b-icon-check-circle>
                </div>
                <h4 class="mt-4 mb-3">All Data Successfully Saved!</h4>
                <b-row class="mt-4">
                    <b-col>
                        <div class="stats-card">
                            <h2 class="text-primary">{{ savedCollectionsCount }}</h2>
                            <small>Collections</small>
                        </div>
                    </b-col>
                    <b-col>
                        <div class="stats-card">
                            <h2 class="text-success">{{ savedParentProductsCount }}</h2>
                            <small>Parents</small>
                        </div>
                    </b-col>
                    <b-col>
                        <div class="stats-card">
                            <h2 class="text-info">{{ savedChildProductsCount }}</h2>
                            <small>Children</small>
                        </div>
                    </b-col>
                    <b-col>
                        <div class="stats-card">
                            <h2 class="text-warning">{{ savedGroupsCount }}</h2>
                            <small>Groups</small>
                        </div>
                    </b-col>
                    <b-col>
                        <div class="stats-card">
                            <h2 class="text-warning">{{ savedSimpleProductsCount }}</h2>
                            <small>Simple</small>
                        </div>
                    </b-col>
                </b-row>
            </div>
        </b-modal>

        <!-- Low Inventory Alert Modal -->
        <b-modal 
            v-model="showLowInventoryModal" 
            title="Low Inventory Alert Configuration" 
            size="lg"
            @ok="saveLowInventoryAlert"
            @hidden="resetLowInventoryModal"
            centered>
            
            <div v-if="currentAlertProduct" class="low-inventory-form">
                <div class="form-group mb-3">
                    <label class="form-label required">Product</label>
                    <b-input 
                        :value="currentAlertProduct.product_sku || 'N/A'" 
                        readonly 
                        class="bg-light">
                    </b-input>
                    <small class="text-muted">{{ currentAlertProduct.product_description || 'No description' }}</small>
                </div>
                
                <div class="form-group mb-3">
                    <label class="form-label required">Alert Type</label>
                    <b-form-radio-group 
                        v-model="alertConfig.alert_type"
                        :options="alertTypeOptions"
                        buttons
                        button-variant="outline-primary"
                        size="sm">
                    </b-form-radio-group>
                </div>
                
                <div class="form-group mb-3">
                    <label class="form-label required">Threshold Quantity</label>
                    <b-input 
                        v-model.number="alertConfig.threshold_quantity"
                        type="number"
                        min="1"
                        step="1"
                        placeholder="Enter minimum quantity before alert"
                        :state="alertConfig.threshold_quantity > 0 ? null : false">
                    </b-input>
                    <small class="text-muted">Alert will trigger when inventory falls below this quantity</small>
                </div>
                
                <div class="form-group mb-3">
                    <label class="form-label">Notification Method</label>
                    <b-form-checkbox-group v-model="alertConfig.notification_methods">
                        <b-form-checkbox value="email">Email Alert</b-form-checkbox>
                        <b-form-checkbox value="dashboard">Dashboard Notification</b-form-checkbox>
                        <b-form-checkbox value="sms">SMS Alert</b-form-checkbox>
                    </b-form-checkbox-group>
                </div>
                
                <div class="form-group mb-3" v-if="alertConfig.notification_methods.includes('email')">
                    <label class="form-label">Alert Email(s)</label>
                    <b-input 
                        v-model="alertConfig.alert_emails"
                        placeholder="Enter email addresses (comma separated)">
                    </b-input>
                    <small class="text-muted">Leave empty to use default admin email</small>
                </div>
                
                <div class="form-group mb-3">
                    <label class="form-label">Reminder Frequency</label>
                    <b-form-select 
                        v-model="alertConfig.reminder_frequency"
                        :options="reminderOptions"
                        size="sm">
                    </b-form-select>
                    <small class="text-muted">How often to send reminders for this product</small>
                </div>

                <div v-if="currentAlertProduct && currentAlertProduct.reminder_count !== undefined" 
                     class="alert alert-info mt-2">
                    <b-icon-info-circle></b-icon-info-circle>
                    <strong>Reminder Status:</strong><br>
                    Reminders sent: {{ currentAlertProduct.reminder_count }}<br>
                    Last reminder: {{ formatReminderDate(currentAlertProduct.last_reminder_sent_at) }}<br>
                    Next reminder: {{ formatReminderDate(currentAlertProduct.next_reminder_date) }}
                </div>
                
                <div class="form-group mb-3">
                    <label class="form-label">Additional Notes</label>
                    <b-textarea 
                        v-model="alertConfig.notes"
                        rows="2"
                        placeholder="Optional notes for inventory management">
                    </b-textarea>
                </div>
                
                <div v-if="currentInventoryLevel !== null" class="alert alert-info">
                    <b-icon-info-circle></b-icon-info-circle>
                    Current inventory level: <strong>{{ currentInventoryLevel }} units</strong>
                    <span v-if="currentInventoryLevel <= alertConfig.threshold_quantity" class="text-danger">
                        - Alert will trigger immediately!
                    </span>
                </div>
            </div>
            
            <div v-else class="text-center py-4">
                <b-icon-exclamation-triangle-fill variant="warning" font-scale="3"></b-icon-exclamation-triangle-fill>
                <h5 class="mt-3">Loading product information...</h5>
            </div>
            
            <template slot="modal-footer">
                <b-button variant="secondary" @click="showLowInventoryModal = false">
                    Cancel
                </b-button>
                <b-button variant="warning" @click="saveLowInventoryAlert" :disabled="!isAlertConfigValid || !currentAlertProduct">
                    <b-icon-bell></b-icon-bell> Save Alert
                </b-button>
            </template>
        </b-modal>

        <!-- Main Content Area -->
        <div class="row">
            <!-- Collections Section (Left Side - Collection Mode) -->
            <div v-if="productEntryMode === 'collection'" class="col-lg-7">
                <!-- Collections Accordion -->
                <div class="collections-container">
                    <div v-for="(collection, colIdx) in collections" :key="collection.id" class="mb-3">
                        <b-card :class="['collection-card', collection.saved ? 'border-success' : 'border-primary']">
                            <!-- Collection Header -->
                            <div slot="header" class="d-flex justify-content-between align-items-center">
                                <div class="d-flex align-items-center">
                                    <div class="collection-icon mr-3">
                                        <b-icon-collection-fill :variant="collection.saved ? 'success' : 'primary'" font-scale="1.5"></b-icon-collection-fill>
                                    </div>
                                    <div>
                                        <h5 class="mb-0">
                                            {{ collection.name || 'New Collection' }}
                                            <b-badge v-if="collection.saved" variant="success" class="ml-2">
                                                <b-icon-check></b-icon-check> Saved
                                            </b-badge>
                                        </h5>
                                        <small class="text-muted">{{ getCategoryName(collection.category_id) || 'No category selected' }}</small>
                                    </div>
                                </div>
                                <div>
                                    <b-badge v-if="getParentProductsCount(collection) > 0" variant="info" class="mr-2">
                                        {{ getParentProductsCount(collection) }} Products
                                    </b-badge>
                                    <b-dropdown size="sm" variant="link" no-caret right>
                                        <template slot="button-content">
                                            <b-icon-three-dots-vertical></b-icon-three-dots-vertical>
                                        </template>
                                        <b-dropdown-item @click="toggleCollectionProducts(collection)">
                                            <b-icon-eye></b-icon-eye> {{ collection.showProducts ? 'Hide' : 'Show' }} Products
                                        </b-dropdown-item>
                                        <b-dropdown-item @click="duplicateCollection(colIdx)" :disabled="!collection.saved">
                                            <b-icon-files></b-icon-files> Duplicate
                                        </b-dropdown-item>
                                        <b-dropdown-divider></b-dropdown-divider>
                                        <b-dropdown-item @click="removeCollection(colIdx)" variant="danger">
                                            <b-icon-trash></b-icon-trash> Delete
                                        </b-dropdown-item>
                                    </b-dropdown>
                                </div>
                            </div>

                            <!-- Collection Form -->
                            <div class="collection-form">
                                <!-- Required Fields Row -->
                                <div class="form-row align-items-end">
                                    <div class="col-md-2 mb-2">
                                        <label class="form-label required">Collection Name</label>
                                        <b-input 
                                            v-model="collection.name" 
                                            size="sm" 
                                            placeholder="e.g., Modern Living"
                                            :disabled="collection.saved"
                                            class="form-control-modern">
                                        </b-input>
                                    </div>
                                    <div class="col-md-2 mb-2">
                                        <label class="form-label required">Vendor</label>
                                        <b-form-select 
                                            v-model="collection.vendor_id" 
                                            :options="vendors" 
                                            size="sm" 
                                            :disabled="collection.saved"
                                            class="form-control-modern">
                                            <option :value="null">Select Vendor</option>
                                        </b-form-select>
                                    </div>
                                    <div class="col-md-2 mb-2">
                                        <label class="form-label required">Category</label>
                                        <b-form-select 
                                            v-model="collection.category_id" 
                                            :options="categories" 
                                            size="sm" 
                                            @change="loadCollectionCategoryAttributes(collection)"
                                            :disabled="collection.saved"
                                            class="form-control-modern">
                                            <option :value="null">Select Category</option>
                                        </b-form-select>
                                    </div>
                                    <div class="col-md-2 mb-2">
                                        <label class="form-label required">Type</label>
                                        <b-form-select 
                                            v-model="collection.type_id" 
                                            :options="collection.typeOptions || []" 
                                            size="sm" 
                                            :disabled="!collection.category_id || collection.saved"
                                            class="form-control-modern">
                                            <option :value="null">Select Type</option>
                                        </b-form-select>
                                    </div>
                                    <div class="col-md-2 mb-2">
                                        <label class="form-label">Color</label>
                                        <b-form-select 
                                            v-model="collection.color_id" 
                                            :options="collection.colorOptions || []" 
                                            size="sm" 
                                            :disabled="!collection.category_id || collection.saved"
                                            class="form-control-modern">
                                            <option :value="null">Select Color (Optional)</option>
                                        </b-form-select>
                                    </div>
                                    <div class="col-md-2 mb-2">
                                        <label class="form-label">Material</label>
                                        <b-form-select 
                                            v-model="collection.material_id" 
                                            :options="collection.materialOptions || []" 
                                            size="sm" 
                                            :disabled="!collection.category_id || collection.saved"
                                            class="form-control-modern">
                                            <option :value="null">Select Material (Optional)</option>
                                        </b-form-select>
                                    </div>
                                </div>

                                <!-- Action Buttons -->
                                <div class="d-flex justify-content-between align-items-center mt-3">
                                    <div>
                                        <b-button 
                                            size="sm" 
                                            variant="outline-secondary" 
                                            @click="resetCollection(collection)"
                                            :disabled="collection.saved"
                                            class="mr-2">
                                            <b-icon-arrow-counterclockwise></b-icon-arrow-counterclockwise> Reset
                                        </b-button>
                                        <b-button 
                                            size="md" 
                                            variant="primary" 
                                            @click="saveCollection(collection)"
                                            :disabled="!isCollectionValid(collection) || collection.saved"
                                            class="px-4">
                                            <b-icon-check-circle></b-icon-check-circle> 
                                            {{ collection.saved ? 'Saved' : 'Save Collection' }}
                                        </b-button>
                                    </div>
                                </div>
                            </div>

                            <!-- Products Section -->
                            <div v-if="collection.showProducts && collection.saved" class="mt-4 pt-3 border-top">
                                <div class="d-flex justify-content-between align-items-center mb-3">
                                    <h5 class="mb-0 d-flex align-items-center">
                                        <b-icon-box-seam class="mr-2 text-success"></b-icon-box-seam>
                                        Parent Products
                                        <span class="badge-counter">{{ getParentProductsCount(collection) }}</span>
                                    </h5>
                                    <b-button 
                                        variant="success" 
                                        size="sm"
                                        @click="addParentProductToCollection(collection)"
                                        class="px-3">
                                        <b-icon-plus></b-icon-plus> Add Parent Product
                                    </b-button>
                                </div>

                                <!-- Parent Products List -->
                                <div v-if="getParentProductsCount(collection) === 0" 
                                     class="empty-state p-4 text-center">
                                    <b-icon-box-seam class="empty-icon text-success"></b-icon-box-seam>
                                    <h6>No Parent Products</h6>
                                    <p class="text-muted mb-2">Start by adding a parent product to this collection</p>
                                    <b-button variant="outline-success" size="sm" @click="addParentProductToCollection(collection)">
                                        <b-icon-plus></b-icon-plus> Add Parent Product
                                    </b-button>
                                </div>

                                <div v-for="(parentProduct, parentIdx) in getFilteredProducts(collection, true)" 
                                     :key="parentProduct.id"
                                     class="parent-product-card mb-3">
                                    
                                    <!-- Parent Product Header -->
                                    <div class="product-header">
                                        <div class="d-flex align-items-center">
                                            <div class="product-status mr-3">
                                                <b-icon-box-seam-fill :variant="parentProduct.saved ? 'success' : 'secondary'" font-scale="1.2"></b-icon-box-seam-fill>
                                            </div>
                                            <div>
                                                <div class="d-flex align-items-center">
                                                    <h6 class="mb-0 mr-2">Parent Product {{ parentIdx + 1 }}</h6>
                                                    <b-badge v-if="parentProduct.saved" variant="success" pill>
                                                        <b-icon-check></b-icon-check> Saved
                                                    </b-badge>
                                                    <b-badge v-else variant="warning" pill>Unsaved</b-badge>
                                                </div>
                                                <small class="text-muted">
                                                    SKU: <code>{{ parentProduct.product_sku || 'Not generated' }}</code>
                                                </small>
                                            </div>
                                        </div>
                                        <div class="product-actions">
                                            <b-button 
                                                v-b-toggle="'parent-'+parentProduct.id"
                                                variant="link" 
                                                size="sm"
                                                class="text-decoration-none mr-1">
                                                <b-icon-chevron-down></b-icon-chevron-down>
                                            </b-button>
                                            <b-button 
                                                size="sm" 
                                                variant="outline-danger" 
                                                @click="removeParentProduct(collection, parentIdx)"
                                                class="px-2">
                                                <b-icon-trash></b-icon-trash>
                                            </b-button>
                                        </div>
                                    </div>

                                    <!-- Parent Product Details -->
                                    <b-collapse :id="'parent-'+parentProduct.id" visible>
                                        <div class="product-details">
                                            <!-- Unified Product Form -->
                                            <div class="unified-product-form mt-2 p-3">
                                                <!-- Basic Info Section -->
                                                <div class="form-section mb-4">
                                                    <h6 class="section-title">
                                                        <b-icon-info-circle class="mr-2"></b-icon-info-circle>
                                                        Basic Information
                                                    </h6>
                                                    <div class="form-grid">
                                                        <div class="form-row">
                                                            <div class="col-md-2">
                                                                <label class="form-label required">Subcategory</label>
                                                                <b-form-select 
                                                                    v-model="parentProduct.subcategory_id" 
                                                                    :options="collection.subcategoryOptions || []" 
                                                                    size="sm"
                                                                    @change="updateProductAutoGeneratedFields(collection, parentProduct)"
                                                                    :state="parentProduct.subcategory_id ? null : false">
                                                                    <option :value="null">Select Subcategory</option>
                                                                </b-form-select>
                                                            </div>
                                                            <div class="col-md-2" v-if="isBedroomCategory(collection.category_id) && isBedSubcategory(parentProduct.subcategory_id)">
                                                                <label class="form-label">Size</label>
                                                                <b-form-select 
                                                                    v-model="parentProduct.size_id" 
                                                                    :options="collection.sizeOptions || []" 
                                                                    size="sm"
                                                                    @change="updateProductAutoGeneratedFields(collection, parentProduct)">
                                                                    <option :value="null">Select Size</option>
                                                                </b-form-select>
                                                            </div>
                                                            <div class="col-md-2" v-if="isBedroomCategory(collection.category_id) && isBedSubcategory(parentProduct.subcategory_id)">
                                                                <label class="form-label">Bed Type</label>
                                                                <b-form-select 
                                                                    v-model="parentProduct.bed_type_id" 
                                                                    :options="collection.bedTypeOptions || []" 
                                                                    size="sm"
                                                                    @change="updateProductAutoGeneratedFields(collection, parentProduct)">
                                                                    <option :value="null">Select Bed Type</option>
                                                                </b-form-select>
                                                            </div>
                                                            <div class="col-md-2">
                                                                <label class="form-label">Style</label>
                                                                <b-form-select 
                                                                    v-model="parentProduct.style_id" 
                                                                    :options="collection.styleOptions || []" 
                                                                    size="sm"
                                                                    @change="updateProductAutoGeneratedFields(collection, parentProduct)">
                                                                    <option :value="null">Select Style</option>
                                                                </b-form-select>
                                                            </div>
                                                            <div class="col-md-2" v-if="isSectionalSubcategory(parentProduct.subcategory_id)">
                                                                <label class="form-label">Sectional Shape</label>
                                                                <b-form-select 
                                                                    v-model="parentProduct.sectional_shape_id" 
                                                                    :options="collection.sectionalShapeOptions || []" 
                                                                    size="sm"
                                                                    @change="updateProductAutoGeneratedFields(collection, parentProduct)">
                                                                    <option :value="null">Select Shape</option>
                                                                </b-form-select>
                                                            </div>
                                                            <div class="col-4">
                                                                <label class="form-label">Functionalities</label>
                                                                <div class="functionality-selector">
                                                                    <div class="d-flex align-items-center">
                                                                        <b-form-select
                                                                            v-model="selectedFunctionality"
                                                                            :options="collection.functionalityOptions || []"
                                                                            size="sm"
                                                                            class="flex-grow-1 mr-2"
                                                                            @change="addFunctionality(parentProduct)">
                                                                            <option :value="null">-- Select functionality to add --</option>
                                                                        </b-form-select>
                                                                    </div>
                                                                    <div class="d-flex flex-wrap align-items-center mb-2 mt-2">
                                                                        <b-badge 
                                                                            v-for="(funcId, index) in parentProduct.functionality_ids" 
                                                                            :key="index"
                                                                            variant="info"
                                                                            class="mr-2 mb-2 p-2 d-flex align-items-center functionality-chip">
                                                                            {{ getFunctionalityText(collection, funcId) }}
                                                                            <b-icon-x 
                                                                                class="ml-1" 
                                                                                @click="removeFunctionality(parentProduct, index)"
                                                                                style="cursor: pointer; font-size: 1.2em;">
                                                                            </b-icon-x>
                                                                        </b-badge>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div class="form-row mt-2">
                                                            <div class="col-md-1">
                                                                <label class="form-label">Height</label>
                                                                <b-input 
                                                                    v-model="parentProduct.height" 
                                                                    size="sm" 
                                                                    type="number" 
                                                                    step="0.01"
                                                                    placeholder="0.00">
                                                                </b-input>
                                                            </div>
                                                            <div class="col-md-1">
                                                                <label class="form-label">Length</label>
                                                                <b-input 
                                                                    v-model="parentProduct.length" 
                                                                    size="sm" 
                                                                    type="number" 
                                                                    step="0.01"
                                                                    placeholder="0.00">
                                                                </b-input>
                                                            </div>
                                                            <div class="col-md-1">
                                                                <label class="form-label">Depth</label>
                                                                <b-input 
                                                                    v-model="parentProduct.depth" 
                                                                    size="sm" 
                                                                    type="number" 
                                                                    step="0.01"
                                                                    placeholder="0.00">
                                                                </b-input>
                                                            </div>
                                                            <div class="col-md-1">
                                                                <label class="form-label">Weight</label>
                                                                <b-input 
                                                                    v-model="parentProduct.weight" 
                                                                    size="sm" 
                                                                    type="number" 
                                                                    step="0.1"
                                                                    placeholder="0.0">
                                                                </b-input>
                                                            </div>
                                                            <div class="col-md-1">
                                                                <label class="form-label">Assmbly</label>
                                                                <b-input 
                                                                    v-model="parentProduct.assembly_time" 
                                                                    size="sm" 
                                                                    type="number"
                                                                    placeholder="Minutes">
                                                                </b-input>
                                                            </div>
                                                            <div class="col-md-1">
                                                                <label class="form-label required">Qty/Box</label>
                                                                <b-input 
                                                                    v-model="parentProduct.quantity_per_box" 
                                                                    type="number" 
                                                                    size="sm" 
                                                                    min="1"
                                                                    :state="parentProduct.quantity_per_box > 0 ? null : false">
                                                                </b-input>
                                                            </div>
                                                            <div class="col-md-2">
                                                                <label class="form-label required">FOB Price ($)</label>
                                                                <b-input 
                                                                    v-model="parentProduct.fob_price" 
                                                                    type="number" 
                                                                    size="sm" 
                                                                    step="0.01"
                                                                    placeholder="0.00"
                                                                    :state="parentProduct.fob_price > 0 ? null : false">
                                                                </b-input>
                                                            </div>
                                                            <div class="col-md-2">
                                                                <label class="form-label">Selling Price ($)</label>
                                                                <b-input 
                                                                    v-model="parentProduct.selling_price" 
                                                                    type="number" 
                                                                    size="sm" 
                                                                    step="0.01"
                                                                    placeholder="0.00"
                                                                    :readonly="parentProduct.saved">
                                                                </b-input>
                                                            </div>
                                                            <div class="col-md-1">
                                                                <label class="form-label required">Cubes</label>
                                                                <b-input 
                                                                    v-model="parentProduct.cubes" 
                                                                    type="number" 
                                                                    size="sm" 
                                                                    step="0.001"
                                                                    placeholder="0.000"
                                                                    :state="parentProduct.cubes > 0 ? null : false">
                                                                </b-input>
                                                            </div>
                                                            <div class="col-md-1">
                                                                <label class="form-label">Spiff ($)</label>
                                                                <b-input 
                                                                    v-model="parentProduct.spiff" 
                                                                    size="sm" 
                                                                    type="number"
                                                                    step="0.01"
                                                                    placeholder="0.00">
                                                                </b-input>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <!-- SKU & Description Section -->
                                                <div class="form-section mb-4">
                                                    <h6 class="section-title">
                                                        <b-icon-tag class="mr-2"></b-icon-tag>
                                                        SKU & Description
                                                    </h6>
                                                    <div class="form-grid">
                                                        <div class="form-row">
                                                            <div class="col-md-3">
                                                                <label class="form-label required">Product SKU</label>
                                                                <div class="d-flex">
                                                                    <b-input 
                                                                        v-model="parentProduct.product_sku" 
                                                                        size="sm" 
                                                                        class="flex-grow-1"
                                                                        :readonly="parentProduct.saved"
                                                                        :state="parentProduct.product_sku ? null : false"
                                                                        placeholder="Auto-generated">
                                                                    </b-input>
                                                                    <b-button 
                                                                        size="sm" 
                                                                        variant="outline-primary" 
                                                                        @click="generateProductSku(collection, parentProduct)"
                                                                        :disabled="parentProduct.saved"
                                                                        class="ml-2"
                                                                        title="Generate SKU">
                                                                        <b-icon-arrow-repeat></b-icon-arrow-repeat>
                                                                    </b-button>
                                                                </div>
                                                            </div>
                                                            <div class="col-md-3">
                                                                <label class="form-label">Vendor SKU</label>
                                                                <b-input 
                                                                    v-model="parentProduct.vendor_sku" 
                                                                    size="sm" 
                                                                    placeholder="Enter vendor SKU">
                                                                </b-input>
                                                            </div>
                                                            <div class="col-md-3">
                                                                <label class="form-label required">Product Description</label>
                                                                <div class="d-flex">
                                                                    <b-textarea 
                                                                        v-model="parentProduct.product_description" 
                                                                        size="sm" 
                                                                        rows="2"
                                                                        class="flex-grow-1"
                                                                        style="height:31px;"
                                                                        :readonly="parentProduct.saved"
                                                                        :state="parentProduct.product_description ? null : false"
                                                                        placeholder="description">
                                                                    </b-textarea>
                                                                    <b-button 
                                                                        size="sm" 
                                                                        variant="outline-primary" 
                                                                        @click="generateProductDescription(collection, parentProduct)"
                                                                        :disabled="parentProduct.saved"
                                                                         style="height:31px;"
                                                                        class="ml-2 align-self-start"
                                                                        title="Generate Description">
                                                                        <b-icon-arrow-repeat></b-icon-arrow-repeat>
                                                                    </b-button>
                                                                </div>
                                                            </div>
                                                            <div class="col-md-3">
                                                                <label class="form-label">Detail Description</label>
                                                                <div class="d-flex">
                                                                    <b-textarea 
                                                                        v-model="parentProduct.detail_description" 
                                                                        size="sm" 
                                                                        rows="2"
                                                                        class="flex-grow-1"
                                                                        :readonly="parentProduct.saved"
                                                                        placeholder="Detail description">
                                                                    </b-textarea>
                                                                    <b-button 
                                                                        size="sm" 
                                                                        variant="outline-info" 
                                                                        @click="generateDetailDescription(collection, parentProduct)"
                                                                        :disabled="parentProduct.saved"
                                                                        class="ml-2 align-self-start"
                                                                        title="Generate Detail Description">
                                                                        <b-icon-arrow-repeat></b-icon-arrow-repeat>
                                                                    </b-button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div class="product-image-section mb-3" v-if="parentProduct.saved">
                                                    <div class="d-flex align-items-start">
                                                        <div class="product-image-preview mr-3" style="width: 100px; height: 100px;">
                                                            <div v-if="parentProduct.image_url" class="position-relative">
                                                                <img :src="parentProduct.image_url" 
                                                                    class="img-thumbnail" 
                                                                    style="width: 100px; height: 100px; object-fit: cover; cursor: pointer;"
                                                                    @click="openImagePreview(parentProduct.image_url)">
                                                                <b-button 
                                                                    size="sm" 
                                                                    variant="danger" 
                                                                    class="position-absolute top-0 right-0"
                                                                    style="top: -8px; right: -8px; border-radius: 50%; padding: 2px 6px;"
                                                                    @click.stop="deleteProductImage(parentProduct)">
                                                                    <b-icon-x font-scale="0.8"></b-icon-x>
                                                                </b-button>
                                                            </div>
                                                            <div v-else class="image-placeholder border rounded d-flex align-items-center justify-content-center"
                                                                style="width: 100px; height: 100px; background: #f8f9fa; cursor: pointer;"
                                                                @click="uploadProductImage(parentProduct, 'parent')">
                                                                <b-icon-image font-scale="2" class="text-muted"></b-icon-image>
                                                            </div>
                                                        </div>
                                                        <div class="image-upload-buttons">
                                                            <b-button 
                                                                variant="outline-primary" 
                                                                size="sm"
                                                                @click="uploadProductImage(parentProduct, 'parent')"
                                                                :disabled="parentProduct.uploading || !parentProduct.saved"
                                                                class="mb-2">
                                                                <b-spinner v-if="parentProduct.uploading" small></b-spinner>
                                                                <b-icon-cloud-upload v-else></b-icon-cloud-upload>
                                                                {{ parentProduct.uploading ? 'Uploading...' : 'Upload Image' }}
                                                            </b-button>
                                                            <small class="text-muted d-block" v-if="!parentProduct.saved">
                                                                Save product first to upload image
                                                            </small>
                                                            <small class="text-muted d-block text-danger" v-if="parentProduct.upload_error">
                                                                {{ parentProduct.upload_error }}
                                                            </small>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <!-- Child Products Toggle and Actions -->
                                            <div class="d-flex justify-content-between align-items-center mt-3">
                                                <b-form-checkbox 
                                                    v-model="parentProduct.has_child_products" 
                                                    size="lg"
                                                    switch
                                                    @change="toggleChildProductsConfiguration(parentProduct)">
                                                    Include Child Products
                                                </b-form-checkbox>
                                                
                                                <div class="d-flex align-items-center">
                                                    <b-form-checkbox 
                                                        v-model="parentProduct.includeInGroup"
                                                        size="sm"
                                                        :disabled="!parentProduct.saved"
                                                        @change="updateGroupSelections"
                                                        class="mr-3">
                                                        Include in Group
                                                    </b-form-checkbox>
                                                    
                                                    <b-button 
                                                        size="sm" 
                                                        variant="success" 
                                                        @click="saveParentProduct(collection, parentProduct)"
                                                        :disabled="!isParentProductValid(parentProduct) || parentProduct.saved"
                                                        class="px-4 mr-2">
                                                        <b-icon-check-circle></b-icon-check-circle>
                                                        {{ parentProduct.saved ? 'Saved' : 'Save Parent' }}
                                                    </b-button>
                                                    
                                                    <b-button 
                                                        v-if="parentProduct.saved && parentProduct.product_id"
                                                        size="sm" 
                                                        variant="warning" 
                                                        @click="showLowInventoryAlert(parentProduct, 'parent')"
                                                        class="px-3">
                                                        <b-icon-exclamation-triangle></b-icon-exclamation-triangle>
                                                        Alert
                                                    </b-button>
                                                </div>
                                            </div>

                                            <!-- Child Products Section -->
                                            <div v-if="parentProduct.showChildProducts && parentProduct.has_child_products" 
                                                 class="mt-4 pt-3 border-top">
                                                <div class="d-flex justify-content-between align-items-center mb-3">
                                                    <h6 class="mb-0 d-flex align-items-center">
                                                        <b-icon-boxes class="mr-2 text-info"></b-icon-boxes>
                                                        Child Components
                                                        <span class="badge-counter">{{ parentProduct.child_products.length }}</span>
                                                    </h6>
                                                    <b-button 
                                                        variant="info" 
                                                        size="sm"
                                                        @click="addChildProductToParent(collection, parentProduct)"
                                                        class="px-3">
                                                        <b-icon-plus></b-icon-plus> Add Component
                                                    </b-button>
                                                </div>

                                                <div v-if="parentProduct.child_products.length === 0" 
                                                     class="empty-state p-3 text-center bg-light rounded">
                                                    <b-icon-boxes class="empty-icon text-info"></b-icon-boxes>
                                                    <p class="mb-0">No components added yet</p>
                                                </div>

                                                <div v-for="(childProduct, childIdx) in parentProduct.child_products" 
                                                     :key="childProduct.id"
                                                     class="child-product-card mb-2">
                                                    
                                                    <div class="product-header">
                                                        <div class="d-flex align-items-center">
                                                            <div class="product-status mr-3">
                                                                <b-icon-box :variant="childProduct.saved ? 'success' : 'info'" font-scale="1.2"></b-icon-box>
                                                            </div>
                                                            <div>
                                                                <div class="d-flex align-items-center">
                                                                    <span class="font-weight-bold mr-2">{{ childProduct.product_description || 'Component ' + (childIdx + 1) }}</span>
                                                                    <b-badge v-if="childProduct.saved" variant="success" pill>
                                                                        <b-icon-check></b-icon-check> Saved
                                                                    </b-badge>
                                                                </div>
                                                                <small class="text-muted">
                                                                    SKU: <code>{{ childProduct.product_sku }}</code>
                                                                </small>
                                                            </div>
                                                        </div>
                                                        <b-button 
                                                            size="sm" 
                                                            variant="outline-danger" 
                                                            @click="removeChildProduct(parentProduct, childIdx)"
                                                            class="px-2">
                                                            <b-icon-x></b-icon-x>
                                                        </b-button>
                                                    </div>

                                                    <!-- Child Product Unified Form -->
                                                    <div class="product-details">
                                                        <div class="form-section mb-3">
                                                            <h6 class="section-title">
                                                                <b-icon-info-circle class="mr-2"></b-icon-info-circle>
                                                                Component Details
                                                            </h6>
                                                            <div class="form-row">
                                                                <div class="col-md-4">
                                                                    <label class="form-label required">Component Type</label>
                                                                    <b-form-select 
                                                                        v-model="childProduct.subsubcategory_id" 
                                                                        :options="collection.subsubcategoryOptions || []" 
                                                                        size="sm" 
                                                                        @change="updateChildProductSku(collection, parentProduct, childProduct)"
                                                                        :state="childProduct.subsubcategory_id ? null : false"
                                                                        :disabled="childProduct.saved">
                                                                        <option :value="null">Select Component</option>
                                                                    </b-form-select>
                                                                </div>
                                                                <div class="col-md-2">
                                                                    <label class="form-label required">Qty/Box</label>
                                                                    <b-input 
                                                                        v-model="childProduct.quantity_per_box" 
                                                                        type="number" 
                                                                        size="sm" 
                                                                        min="1"
                                                                        :state="childProduct.quantity_per_box > 0 ? null : false"
                                                                        :readonly="childProduct.saved">
                                                                    </b-input>
                                                                </div>
                                                                <div class="col-md-2">
                                                                    <label class="form-label required">FOB Price</label>
                                                                    <b-input 
                                                                        v-model="childProduct.fob_price" 
                                                                        type="number" 
                                                                        size="sm" 
                                                                        step="0.01"
                                                                        :state="childProduct.fob_price > 0 ? null : false"
                                                                        :readonly="childProduct.saved">
                                                                    </b-input>
                                                                </div>
                                                                <div class="col-md-2">
                                                                    <label class="form-label">Selling Price</label>
                                                                    <b-input 
                                                                        v-model="childProduct.selling_price" 
                                                                        type="number" 
                                                                        size="sm" 
                                                                        step="0.01"
                                                                        :readonly="childProduct.saved">
                                                                    </b-input>
                                                                </div>
                                                                <div class="col-md-2">
                                                                    <label class="form-label required">Cubes</label>
                                                                    <b-input 
                                                                        v-model="childProduct.cubes" 
                                                                        type="number" 
                                                                        size="sm" 
                                                                        step="0.001"
                                                                        :state="childProduct.cubes > 0 ? null : false"
                                                                        :readonly="childProduct.saved">
                                                                    </b-input>
                                                                </div>
                                                                <div class="col-md-2">
                                                                    <label class="form-label">Spiff</label>
                                                                    <b-input 
                                                                        v-model="childProduct.spiff" 
                                                                        size="sm" 
                                                                        type="number"
                                                                        step="0.01"
                                                                        placeholder="0.00"
                                                                        :readonly="childProduct.saved">
                                                                    </b-input>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <!-- SKU & Vendor Section -->
                                                        <div class="form-section mb-3">
                                                            <h6 class="section-title">
                                                                <b-icon-tag class="mr-2"></b-icon-tag>
                                                                SKU & Vendor Information
                                                            </h6>
                                                            <div class="form-row">
                                                                <div class="col-md-4">
                                                                    <label class="form-label">SKU</label>
                                                                    <b-input 
                                                                        v-model="childProduct.product_sku" 
                                                                        size="sm" 
                                                                        readonly
                                                                        class="bg-light">
                                                                    </b-input>
                                                                </div>
                                                                <div class="col-md-4">
                                                                    <label class="form-label">Vendor SKU</label>
                                                                    <b-input 
                                                                        v-model="childProduct.vendor_sku" 
                                                                        size="sm" 
                                                                        placeholder="Optional"
                                                                        :readonly="childProduct.saved">
                                                                    </b-input>
                                                                </div>
                                                                <div class="col-md-4 d-flex align-items-center">
                                                                    <b-form-checkbox 
                                                                        v-model="childProduct.low_inventory_notify" 
                                                                        size="sm"
                                                                        switch
                                                                        :disabled="childProduct.saved"
                                                                        class="mt-3">
                                                                        Low Inventory Alert
                                                                    </b-form-checkbox>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <!-- Measurements Section -->
                                                        <div class="form-section">
                                                            <h6 class="section-title">
                                                                <b-icon-rulers class="mr-2"></b-icon-rulers>
                                                                Measurements (Optional)
                                                            </h6>
                                                            <div class="form-row">
                                                                <div class="col-md-2">
                                                                    <label class="form-label">Height</label>
                                                                    <b-input 
                                                                        v-model="childProduct.height" 
                                                                        size="sm" 
                                                                        type="number" 
                                                                        step="0.01"
                                                                        placeholder="0.00"
                                                                        :readonly="childProduct.saved">
                                                                    </b-input>
                                                                </div>
                                                                <div class="col-md-2">
                                                                    <label class="form-label">Length</label>
                                                                    <b-input 
                                                                        v-model="childProduct.length" 
                                                                        size="sm" 
                                                                        type="number" 
                                                                        step="0.01"
                                                                        placeholder="0.00"
                                                                        :readonly="childProduct.saved">
                                                                    </b-input>
                                                                </div>
                                                                <div class="col-md-2">
                                                                    <label class="form-label">Depth</label>
                                                                    <b-input 
                                                                        v-model="childProduct.depth" 
                                                                        size="sm" 
                                                                        type="number" 
                                                                        step="0.01"
                                                                        placeholder="0.00"
                                                                        :readonly="childProduct.saved">
                                                                    </b-input>
                                                                </div>
                                                                <div class="col-md-3">
                                                                    <label class="form-label">Unit</label>
                                                                    <b-form-select 
                                                                        v-model="childProduct.measurement_unit" 
                                                                        size="sm"
                                                                        :disabled="childProduct.saved"
                                                                        :options="[
                                                                            {text: 'Inches', value: 'in'},
                                                                            {text: 'Centimeters', value: 'cm'}
                                                                        ]">
                                                                    </b-form-select>
                                                                </div>
                                                                <div class="col-md-3">
                                                                    <label class="form-label">Weight</label>
                                                                    <b-input 
                                                                        v-model="childProduct.weight" 
                                                                        size="sm" 
                                                                        type="number" 
                                                                        step="0.1"
                                                                        placeholder="0.0"
                                                                        :readonly="childProduct.saved">
                                                                    </b-input>
                                                                </div>
                                                            </div>
                                                        </div>
                                                         <div class="child-product-image-section mb-2" v-if="childProduct.saved">
                                                            <div class="d-flex align-items-center">
                                                                <div class="child-image-preview mr-2" style="width: 50px; height: 50px;">
                                                                    <div v-if="childProduct.image_url">
                                                                        <img :src="childProduct.image_url" 
                                                                             class="img-thumbnail" 
                                                                             style="width: 50px; height: 50px; object-fit: cover; cursor: pointer;"
                                                                             @click="openImagePreview(childProduct.image_url)">
                                                                    </div>
                                                                    <div v-else class="border rounded d-flex align-items-center justify-content-center"
                                                                         style="width: 50px; height: 50px; background: #f8f9fa; cursor: pointer;"
                                                                         @click="uploadProductImage(childProduct, 'child')">
                                                                        <b-icon-image font-scale="1" class="text-muted"></b-icon-image>
                                                                    </div>
                                                                </div>
                                                                <div>
                                                                    <b-button 
                                                                        size="xs" 
                                                                        variant="outline-primary" 
                                                                        @click="uploadProductImage(childProduct, 'child')"
                                                                        :disabled="childProduct.uploading || !childProduct.saved">
                                                                        <b-spinner v-if="childProduct.uploading" small></b-spinner>
                                                                        <b-icon-cloud-upload v-else font-scale="0.8"></b-icon-cloud-upload>
                                                                        {{ childProduct.uploading ? '...' : 'Upload Image' }}
                                                                    </b-button>
                                                                    <b-button 
                                                                        v-if="childProduct.image_url"
                                                                        size="xs" 
                                                                        variant="outline-danger" 
                                                                        @click="deleteProductImage(childProduct)"
                                                                        class="ml-1">
                                                                        <b-icon-x font-scale="0.8"></b-icon-x>
                                                                    </b-button>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <!-- Save Button and Alert Button -->
                                                        <div class="text-right mt-3">
                                                            <b-button 
                                                                v-if="childProduct.saved && childProduct.product_id"
                                                                size="sm" 
                                                                variant="warning" 
                                                                @click="showLowInventoryAlert(childProduct, 'child')"
                                                                class="mr-2">
                                                                <b-icon-exclamation-triangle></b-icon-exclamation-triangle>
                                                                Low Inventory Alert
                                                            </b-button>
                                                            
                                                            <b-button 
                                                                size="sm" 
                                                                variant="info" 
                                                                @click="saveChildProduct(collection, parentProduct, childProduct)"
                                                                :disabled="!isChildProductValid(childProduct) || childProduct.saved"
                                                                class="px-4">
                                                                <b-icon-check-circle></b-icon-check-circle>
                                                                {{ childProduct.saved ? 'Saved' : 'Save Component' }}
                                                            </b-button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </b-collapse>
                                </div>
                            </div>
                        </b-card>
                    </div>
                </div>
            </div>

            <!-- Simple Products Panel (Left Side - Simple Mode) -->
            <div v-if="productEntryMode === 'simple'" class="col-lg-7">
                <div class="simple-products-container">
                    <b-card class="border-warning shadow-sm">
                        <div slot="header" class="d-flex justify-content-between align-items-center bg-warning text-white">
                            <h5 class="mb-0 d-flex align-items-center">
                                <b-icon-box-seam class="mr-2"></b-icon-box-seam>
                                Simple Products (No Collection)
                            </h5>
                            <b-badge variant="light">{{ simpleProducts.length }} Products</b-badge>
                        </div>

                        <div class="p-3">
                            <!-- Add Simple Product Button -->
                            <div class="text-center mb-4">
                                <b-button variant="warning" @click="addSimpleProduct" size="lg" class="px-5">
                                    <b-icon-plus-circle></b-icon-plus-circle> Add Simple Product
                                </b-button>
                            </div>

                            <!-- Simple Products List -->
                            <div v-for="(product, index) in simpleProducts" :key="product.id" class="simple-product-card mb-4">
                                <div class="card-header d-flex justify-content-between align-items-center">
                                    <div class="d-flex align-items-center">
                                        <b-icon-box-seam-fill variant="warning" class="mr-2" font-scale="1.5"></b-icon-box-seam-fill>
                                        <h6 class="mb-0">Simple Product #{{ index + 1 }}</h6>
                                        <b-badge :variant="product.saved ? 'success' : 'warning'" class="ml-2">
                                            {{ product.saved ? 'Saved' : 'Unsaved' }}
                                        </b-badge>
                                    </div>
                                    <b-button size="sm" variant="outline-danger" @click="removeSimpleProduct(index)">
                                        <b-icon-trash></b-icon-trash>
                                    </b-button>
                                </div>

                                <div class="card-body">
                                    <!-- Image Upload Section for Simple Product -->
                                    <div class="simple-product-image-section mb-3">
                                        <div class="d-flex align-items-start">
                                            <div class="image-preview mr-3" style="width: 80px; height: 80px;">
                                                <div v-if="product.image_url" class="position-relative">
                                                    <img :src="product.image_url" 
                                                         class="img-thumbnail" 
                                                         style="width: 80px; height: 80px; object-fit: cover; cursor: pointer;"
                                                         @click="openImagePreview(product.image_url)">
                                                    <b-button 
                                                        size="sm" 
                                                        variant="danger" 
                                                        class="position-absolute top-0 right-0"
                                                        style="top: -8px; right: -8px; border-radius: 50%; padding: 2px 6px;"
                                                        @click.stop="deleteProductImage(product)">
                                                        <b-icon-x font-scale="0.8"></b-icon-x>
                                                    </b-button>
                                                </div>
                                                <div v-else class="border rounded d-flex align-items-center justify-content-center"
                                                     style="width: 80px; height: 80px; background: #f8f9fa; cursor: pointer;"
                                                     @click="uploadProductImage(product, 'simple')">
                                                    <b-icon-image font-scale="2" class="text-muted"></b-icon-image>
                                                </div>
                                            </div>
                                            <div>
                                                <b-button 
                                                    variant="outline-primary" 
                                                    size="sm"
                                                    @click="uploadProductImage(product, 'simple')"
                                                    :disabled="product.uploading || !product.saved">
                                                    <b-spinner v-if="product.uploading" small></b-spinner>
                                                    <b-icon-cloud-upload v-else></b-icon-cloud-upload>
                                                    {{ product.uploading ? 'Uploading...' : 'Upload Image' }}
                                                </b-button>
                                                <small class="text-muted d-block" v-if="!product.saved">
                                                    Save product first to upload image
                                                </small>
                                                <small class="text-muted d-block text-danger" v-if="product.upload_error">
                                                    {{ product.upload_error }}
                                                </small>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <!-- Barcode Scanner Section -->
                                    <div class="barcode-scanner-section mb-3 p-2 border rounded">
                                        <div class="d-flex align-items-center">
                                            <b-icon-upc-scan class="mr-2" font-scale="1.5"></b-icon-upc-scan>
                                            <h6 class="mb-0">Scan Barcode</h6>
                                        </div>
                                        <div class="mt-2">
                                            <b-input-group size="sm">
                                                <b-form-input
                                                    v-model="product.scannedBarcode"
                                                    placeholder="Scan or type barcode..."
                                                    @keyup.enter="lookupBarcodeForProduct(product)"
                                                    :disabled="product.saved"
                                                ></b-form-input>
                                                <b-input-group-append>
                                                    <b-button 
                                                        variant="primary" 
                                                        @click="lookupBarcodeForProduct(product)"
                                                        :disabled="product.saved || !product.scannedBarcode"
                                                    >
                                                        <b-icon-search></b-icon-search> Lookup
                                                    </b-button>
                                                    <b-button 
                                                        variant="success" 
                                                        @click="openScannerForProduct(product)"
                                                        :disabled="product.saved"
                                                        title="Use camera to scan barcode"
                                                    >
                                                        <b-icon-camera></b-icon-camera>
                                                    </b-button>
                                                </b-input-group-append>
                                            </b-input-group>
                                            <small class="text-muted">Scan a barcode to auto-fill SKU and product details</small>
                                        </div>
                                    </div>

                                    <!-- Vendor Selection -->
                                    <div class="form-row">
                                        <div class="col-md-3 mb-3">
                                            <label class="form-label required">Vendor</label>
                                            <b-form-select v-model="product.vendor_id" :options="vendors" :state="product.vendor_id ? null : false">
                                                <option :value="null">Select Vendor</option>
                                            </b-form-select>
                                        </div>
                                        <div class="col-md-3 mb-3">
                                            <label class="form-label required">Category</label>
                                            <b-form-select v-model="product.category_id" :options="categories" @change="loadSimpleProductCategoryAttributes(product)" :state="product.category_id ? null : false">
                                                <option :value="null">Select Category</option>
                                            </b-form-select>
                                        </div>
                                        <div class="col-md-3 mb-3">
                                            <label class="form-label required">Product SKU</label>
                                            <b-input v-model="product.product_sku" placeholder="Enter SKU manually" :state="product.product_sku ? null : false" :readonly="product.saved"></b-input>
                                            <small class="text-muted">Enter unique SKU manually</small>
                                        </div>
                                    </div>

                                    <div class="form-row">
                                        <div class="col-md-4 mb-3">
                                            <label class="form-label required">Product Description</label>
                                            <b-input v-model="product.product_description" placeholder="Enter description" :state="product.product_description ? null : false" :readonly="product.saved"></b-input>
                                        </div>
                                        <div class="col-md-2 mb-3">
                                            <label class="form-label required">FOB Price ($)</label>
                                            <b-input v-model="product.fob_price" type="number" step="0.01" placeholder="0.00" :state="product.fob_price > 0 ? null : false" :readonly="product.saved"></b-input>
                                        </div>
                                        <div class="col-md-2 mb-3">
                                            <label class="form-label">Selling Price ($)</label>
                                            <b-input v-model="product.selling_price" type="number" step="0.01" placeholder="0.00" :readonly="product.saved"></b-input>
                                        </div>
                                        <div class="col-md-2 mb-3">
                                            <label class="form-label required">Cubes</label>
                                            <b-input v-model="product.cubes" type="number" step="0.001" placeholder="0.000" :state="product.cubes > 0 ? null : false" :readonly="product.saved"></b-input>
                                        </div>
                                        <div class="col-md-2 mb-3">
                                            <label class="form-label">Spiff ($)</label>
                                            <b-input v-model="product.spiff" type="number" step="0.01" placeholder="0.00" :readonly="product.saved"></b-input>
                                        </div>
                                        <div class="col-md-2 mb-3">
                                            <label class="form-label">Qty/Box</label>
                                            <b-input v-model="product.quantity_per_box" type="number" min="1" placeholder="1" :readonly="product.saved"></b-input>
                                        </div>
                                    </div>

                                    <!-- Pricing and Inventory -->
                                    <div class="form-row">
                                        <div v-if="product.saved" class="inventory-section mt-4 pt-3 border-top">
                                            <h6 class="section-title">
                                                <b-icon-box-seam class="mr-2"></b-icon-box-seam>
                                                Inventory Management
                                            </h6>
                                            
                                            <div class="form-row align-items-end">
                                                <div class="col-md-4 mb-2">
                                                    <label class="form-label required">Location</label>
                                                    <b-form-select 
                                                        v-model="product.selectedLocation"
                                                        :options="getLocationOptions()"
                                                        size="sm"
                                                        :state="product.selectedLocation ? null : false">
                                                        <option :value="null">Select Location</option>
                                                    </b-form-select>
                                                    <small class="text-muted" v-if="!isAdmin">
                                                        Location restricted to your assigned warehouse
                                                    </small>
                                                </div>
                                                <div class="col-md-3 mb-2">
                                                    <label class="form-label required">Quantity to Add</label>
                                                    <b-input 
                                                        v-model="product.inventoryQuantity"
                                                        type="number"
                                                        min="1"
                                                        step="1"
                                                        size="sm"
                                                        placeholder="Enter quantity"
                                                        :state="product.inventoryQuantity > 0 ? null : false">
                                                    </b-input>
                                                </div>
                                                <div class="col-md-2 mb-2">
                                                    <label class="form-label">&nbsp;</label>
                                                    <b-button 
                                                        variant="success" 
                                                        size="sm"
                                                        block
                                                        @click="addInventoryToProduct(product)"
                                                        :disabled="!canAddInventory(product)"
                                                        class="px-3">
                                                        <b-icon-plus-circle></b-icon-plus-circle>
                                                        Add to Inventory
                                                    </b-button>
                                                </div>
                                            </div>
                                            
                                            <!-- Inventory History Display -->
                                            <div v-if="product.inventoryHistory && product.inventoryHistory.length > 0" class="mt-3">
                                                <small class="text-muted">Recent Inventory Additions:</small>
                                                <div class="inventory-history mt-1">
                                                    <div v-for="(inv, idx) in product.inventoryHistory.slice(0, 3)" :key="idx"
                                                        class="d-flex justify-content-between align-items-center small p-1 border-bottom">
                                                        <span>{{ inv.location_name }}</span>
                                                        <span class="font-weight-bold text-success">+{{ inv.quantity }}</span>
                                                        <span class="text-muted">{{ formatDate(inv.created_at) }}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Optional Fields -->
                                    <b-button v-b-toggle="'simple-opt-'+product.id" variant="link" size="sm" class="text-decoration-none p-0 mb-2">
                                        <b-icon-chevron-down></b-icon-chevron-down> Optional Fields
                                    </b-button>
                                    <b-collapse :id="'simple-opt-'+product.id">
                                        <div class="form-row mt-2">
                                            <div class="col-md-4 mb-2">
                                                <label class="form-label">Type</label>
                                                <b-form-select v-model="product.type_id" :options="product.typeOptions || []">
                                                    <option :value="null">Select Type</option>
                                                </b-form-select>
                                            </div>
                                            <div class="col-md-4 mb-2">
                                                <label class="form-label">Color</label>
                                                <b-form-select v-model="product.color_id" :options="product.colorOptions || []">
                                                    <option :value="null">Select Color</option>
                                                </b-form-select>
                                            </div>
                                            <div class="col-md-4 mb-2">
                                                <label class="form-label">Material</label>
                                                <b-form-select v-model="product.material_id" :options="product.materialOptions || []">
                                                    <option :value="null">Select Material</option>
                                                </b-form-select>
                                            </div>
                                        </div>
                                        <div class="form-row">
                                            <div class="col-md-4 mb-2">
                                                <label class="form-label">Size</label>
                                                <b-form-select v-model="product.size_id" :options="product.sizeOptions || []">
                                                    <option :value="null">Select Size</option>
                                                </b-form-select>
                                            </div>
                                            <div class="col-md-4 mb-2">
                                                <label class="form-label">Style</label>
                                                <b-form-select v-model="product.style_id" :options="product.styleOptions || []">
                                                    <option :value="null">Select Style</option>
                                                </b-form-select>
                                            </div>
                                            <div class="col-md-4 mb-2">
                                                <label class="form-label">Vendor SKU</label>
                                                <b-input v-model="product.vendor_sku" placeholder="Vendor SKU"></b-input>
                                            </div>
                                        </div>
                                    </b-collapse>

                                    <!-- Save Button and Alert Button -->
                                    <div class="text-right mt-3">
                                        <b-button 
                                            v-if="product.saved && product.product_id"
                                            size="sm" 
                                            variant="warning" 
                                            @click="showLowInventoryAlert(product, 'simple')"
                                            class="mr-2">
                                            <b-icon-exclamation-triangle></b-icon-exclamation-triangle>
                                            Low Inventory Alert
                                        </b-button>
                                        
                                        <b-button 
                                            variant="warning" 
                                            @click="saveSimpleProduct(product)" 
                                            :disabled="!isSimpleProductValid(product) || product.saved" 
                                            class="px-5">
                                            <b-icon-check-circle></b-icon-check-circle>
                                            {{ product.saved ? 'Saved' : 'Save Simple Product' }}
                                        </b-button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </b-card>
                </div>
            </div>

            <!-- Groups Panel (Right Side - Always Visible) -->
            <div class="col-lg-5">
                <b-card :class="['groups-panel', showGroupPanel ? 'panel-visible' : '']">
                    <div slot="header" class="d-flex justify-content-between align-items-center">
                        <h5 class="mb-0 d-flex align-items-center">
                            <b-icon-collection-fill class="mr-2 text-warning"></b-icon-collection-fill>
                            Product Grouping
                        </h5>
                        <b-badge variant="warning" class="p-2">
                            {{ groupsToCreate.length }} Queued
                        </b-badge>
                    </div>

                    <!-- Group Creation Wizard -->
                    <div class="group-wizard">
                        <!-- Step 1: Select Products -->
                        <div class="wizard-step">
                            <div class="step-indicator">
                                <span class="step-number">1</span>
                                <span class="step-title">Select Products</span>
                            </div>
                            <div class="step-content">
                                <!-- Collection Filter -->
                                <div class="mb-3">
                                    <label class="form-label">Filter by Collection</label>
                                    <b-form-select 
                                        v-model="groupFilter.collectionId"
                                        size="sm"
                                        :options="getCollectionOptions()"
                                        @change="updateGroupSelections">
                                        <option :value="null">All Collections</option>
                                    </b-form-select>
                                </div>

                                <!-- Available Products -->
                                <div class="available-products">
                                    <div v-for="collection in getFilteredCollections()" :key="collection.id" 
                                         class="collection-group mb-3">
                                        <div class="collection-header" @click="toggleCollectionGroup(collection.id)">
                                            <b-icon-chevron-right v-if="!expandedCollections[collection.id]" class="mr-2"></b-icon-chevron-right>
                                            <b-icon-chevron-down v-else class="mr-2"></b-icon-chevron-down>
                                            <span class="font-weight-bold">{{ collection.name }}</span>
                                            <span class="badge-counter ml-2">{{ getAvailableProductsCount(collection) }}</span>
                                        </div>
                                        
                                        <div v-if="expandedCollections[collection.id]" class="collection-products mt-2">
                                            <div v-for="product in getAvailableProducts(collection)" 
                                                 :key="product.id"
                                                 class="product-select-item"
                                                 :class="{'selected': product.includeInGroup}">
                                                <b-form-checkbox 
                                                    v-model="product.includeInGroup"
                                                    @change="updateGroupSelections"
                                                    class="w-100">
                                                    <div class="d-flex justify-content-between align-items-center">
                                                        <div>
                                                            <div class="d-flex align-items-center">
                                                                <div v-if="product.image_url" class="mr-2">
                                                                    <img :src="product.image_url" style="width: 30px; height: 30px; object-fit: cover; border-radius: 4px;">
                                                                </div>
                                                                <div>
                                                                    <span class="font-weight-bold">{{ getSubcategoryName(product.subcategory_id) }}</span>
                                                                    <small class="text-muted d-block">SKU: {{ product.product_sku }}</small>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <b-badge variant="info" pill>{{ product.fob_price | currency }}</b-badge>
                                                    </div>
                                                </b-form-checkbox>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Step 2: Configure Groups -->
                        <div class="wizard-step" v-if="selectedForGrouping.length > 0">
                            <div class="step-indicator">
                                <span class="step-number">2</span>
                                <span class="step-title">Configure Groups</span>
                            </div>
                            <div class="step-content">
                                <!-- Selected Products Summary -->
                                <div class="selected-summary mb-3">
                                    <div class="d-flex justify-content-between">
                                        <span>Selected Products:</span>
                                        <span class="font-weight-bold">{{ selectedForGrouping.length }}</span>
                                    </div>
                                    <div class="d-flex justify-content-between">
                                        <span>Available Combinations:</span>
                                        <span class="font-weight-bold">{{ getCombinationCount() }}</span>
                                    </div>
                                </div>

                                <!-- Category Validation Message -->
                                <div v-if="selectedForGrouping.length > 0" class="mb-3">
                                    <div v-if="isMixedCategory()" class="alert alert-warning small p-2">
                                        <b-icon-exclamation-triangle-fill class="mr-1"></b-icon-exclamation-triangle-fill>
                                        Selected products must be from the same category.
                                    </div>
                                    <div v-else-if="getCurrentCategory()" class="alert alert-info small p-2">
                                        <b-icon-info-circle class="mr-1"></b-icon-info-circle>
                                        Category: {{ getCategoryName(getCurrentCategory()) }}
                                        <span v-if="isBedroomCategory(getCurrentCategory())" class="d-block mt-1 small">
                                            <b>Note:</b> For Bedroom category, only one bed can be selected per group.
                                        </span>
                                        <span v-if="isLivingRoomCategory(getCurrentCategory())" class="d-block mt-1 small">
                                            <b>Allowed groups:</b> SOFA+LOVESEAT, SOFA+LOVESEAT+CHAIR, SOFA+LOVESEAT+CHAIR+OTTOMAN
                                        </span>
                                    </div>
                                </div>

                                <!-- Group Size Selector -->
                                <div class="mb-3" v-if="!isMixedCategory()">
                                    <label class="form-label">Group Size</label>
                                    <b-form-radio-group 
                                        v-model="selectedGroupSize"
                                        :options="getGroupSizeOptions()"
                                        buttons
                                        button-variant="outline-primary"
                                        size="sm">
                                    </b-form-radio-group>
                                </div>

                                <!-- Generate Button -->
                                <b-button 
                                    block 
                                    variant="primary"
                                    @click="generateGroups"
                                    :disabled="!canGenerateGroups()"
                                    class="mb-3">
                                    <b-icon-magic></b-icon-magic> Generate Combinations
                                </b-button>

                                <!-- Generated Groups -->
                                <div v-if="generatedGroups.length > 0" class="generated-groups">
                                    <div class="d-flex justify-content-between align-items-center mb-2">
                                        <label class="form-label mb-0">Generated Groups</label>
                                        <div>
                                            <b-button size="xs" variant="link" @click="selectAllCombinations">
                                                Select All
                                            </b-button>
                                            <b-button size="xs" variant="link" @click="deselectAllCombinations">
                                                Deselect All
                                            </b-button>
                                        </div>
                                    </div>
                                    
                                    <div class="groups-list">
                                        <div v-for="(group, idx) in generatedGroups" :key="idx"
                                             class="group-item"
                                             :class="{'selected': group.selected}">
                                            <div class="d-flex">
                                                <b-form-checkbox 
                                                    v-model="group.selected"
                                                    class="mr-2">
                                                </b-form-checkbox>
                                                <div class="group-info flex-grow-1">
                                                    <div class="d-flex justify-content-between align-items-center">
                                                        <div>
                                                            <span class="font-weight-bold">{{ group.product_name }}</span>
                                                            <div class="small text-muted">{{ group.setType }}</div>
                                                        </div>
                                                        <b-badge variant="primary">{{ group.sku }}</b-badge>
                                                    </div>
                                                    
                                                    <!-- Show quantity breakdown -->
                                                    <div class="group-quantities mt-1" v-if="group.subcategoryWithQuantities">
                                                        <small class="text-muted">
                                                            {{ group.subcategoryWithQuantities.join(' + ') }}
                                                        </small>
                                                    </div>
                                                    
                                                    <!-- Editable Image Field -->
                                                    <div class="group-editable-image mt-2">
                                                        <div class="d-flex align-items-center">
                                                            <div class="mr-2" style="width: 50px; height: 50px;">
                                                                <div v-if="group.editable_image_url" class="position-relative">
                                                                    <img :src="group.editable_image_url" 
                                                                         style="width: 50px; height: 50px; object-fit: cover; border-radius: 4px; cursor: pointer;"
                                                                         @click="openImagePreview(group.editable_image_url)">
                                                                    <b-button 
                                                                        size="xs" 
                                                                        variant="danger" 
                                                                        class="position-absolute"
                                                                        style="top: -8px; right: -8px; border-radius: 50%; padding: 2px 6px;"
                                                                        @click.stop="group.editable_image_url = null">
                                                                        <b-icon-x font-scale="0.6"></b-icon-x>
                                                                    </b-button>
                                                                </div>
                                                                <div v-else class="border rounded d-flex align-items-center justify-content-center"
                                                                     style="width: 50px; height: 50px; background: #f8f9fa; cursor: pointer;"
                                                                     @click="uploadGroupImage(group)">
                                                                    <b-icon-image font-scale="1.5" class="text-muted"></b-icon-image>
                                                                </div>
                                                            </div>
                                                            <div class="flex-grow-1">
                                                                <b-button size="xs" variant="outline-primary" @click="uploadGroupImage(group)">
                                                                    {{ group.editable_image_url ? 'Change Image' : 'Add Image' }}
                                                                </b-button>
                                                                <small class="text-muted d-block">Group product image</small>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    
                                                    <!-- Editable FOB Price Field -->
                                                    <div class="group-editable-fob mt-2">
                                                        <label class="small text-muted mb-0">FOB Price ($)</label>
                                                        <b-input-group size="sm">
                                                            <b-input 
                                                                v-model="group.editable_fob_price"
                                                                type="number"
                                                                step="0.01"
                                                                min="0"
                                                                placeholder="FOB Price">
                                                            </b-input>
                                                            <b-input-group-append>
                                                                <b-button 
                                                                    size="sm" 
                                                                    variant="outline-secondary"
                                                                    @click="resetGroupFobPrice(group)"
                                                                    title="Reset to original">
                                                                    <b-icon-arrow-repeat></b-icon-arrow-repeat>
                                                                </b-button>
                                                            </b-input-group-append>
                                                        </b-input-group>
                                                        <small class="text-muted" v-if="group.original_fob_price !== group.editable_fob_price">
                                                            Original: ${{ formatNumber(group.original_fob_price) }}
                                                        </small>
                                                    </div>
                                                    
                                                    <!-- Editable Selling Price Field -->
                                                    <div class="group-editable-selling mt-2">
                                                        <label class="small text-muted mb-0">Selling Price ($)</label>
                                                        <b-input-group size="sm">
                                                            <b-input 
                                                                v-model="group.editable_selling_price"
                                                                type="number"
                                                                step="0.01"
                                                                min="0"
                                                                placeholder="Selling Price">
                                                            </b-input>
                                                            <b-input-group-append>
                                                                <b-button 
                                                                    size="sm" 
                                                                    variant="outline-secondary"
                                                                    @click="resetGroupSellingPrice(group)"
                                                                    title="Reset to original">
                                                                    <b-icon-arrow-repeat></b-icon-arrow-repeat>
                                                                </b-button>
                                                            </b-input-group-append>
                                                        </b-input-group>
                                                        <small class="text-muted" v-if="group.original_selling_price !== group.editable_selling_price">
                                                            Original: ${{ formatNumber(group.original_selling_price) }}
                                                        </small>
                                                    </div>
                                                    
                                                    <!-- Editable Vendor SKU Field -->
                                                    <div class="group-editable-vendor-sku mt-2">
                                                        <label class="small text-muted mb-0">Vendor SKU</label>
                                                        <b-input-group size="sm">
                                                            <b-input 
                                                                v-model="group.editable_vendor_sku"
                                                                placeholder="Enter Vendor SKU">
                                                            </b-input>
                                                            <b-input-group-append>
                                                                <b-button 
                                                                    size="sm" 
                                                                    variant="outline-secondary"
                                                                    @click="resetGroupVendorSku(group)"
                                                                    title="Reset to original">
                                                                    <b-icon-arrow-repeat></b-icon-arrow-repeat>
                                                                </b-button>
                                                            </b-input-group-append>
                                                        </b-input-group>
                                                        <small class="text-muted" v-if="group.original_vendor_sku !== group.editable_vendor_sku">
                                                            Original: {{ group.original_vendor_sku }}
                                                        </small>
                                                    </div>
                                                    
                                                    <!-- Editable Spiff Field -->
                                                    <div class="group-editable-spiff mt-2">
                                                        <label class="small text-muted mb-0">Spiff ($)</label>
                                                        <b-input-group size="sm">
                                                            <b-input 
                                                                v-model="group.editable_spiff"
                                                                type="number"
                                                                step="0.01"
                                                                min="0"
                                                                placeholder="0.00">
                                                            </b-input>
                                                            <b-input-group-append>
                                                                <b-button 
                                                                    size="sm" 
                                                                    variant="outline-secondary"
                                                                    @click="resetGroupSpiff(group)"
                                                                    title="Reset to original">
                                                                    <b-icon-arrow-repeat></b-icon-arrow-repeat>
                                                                </b-button>
                                                            </b-input-group-append>
                                                        </b-input-group>
                                                        <small class="text-muted" v-if="group.original_spiff !== group.editable_spiff">
                                                            Original: ${{ formatNumber(group.original_spiff) }}
                                                        </small>
                                                    </div>
                                                    
                                                    <div class="group-subcategories mt-2">
                                                        <b-badge 
                                                            v-for="subcat in group.subcategories" 
                                                            :key="subcat"
                                                            variant="light"
                                                            class="mr-1">
                                                            {{ subcat }}
                                                        </b-badge>
                                                    </div>
                                                    
                                                    <div class="group-products small text-muted mt-1">
                                                        {{ group.productSkus.join(', ') }}
                                                    </div>
                                                    
                                                    <div class="group-pricing mt-1">
                                                        <span class="mr-2">Total FOB: {{ group.editable_fob_price | currency }}</span>
                                                        <span class="mr-2">Total Selling: {{ group.editable_selling_price | currency }}</span>
                                                        <span class="mr-2">Total Cubes: {{ group.cubes }}</span>
                                                        <span>Spiff: {{ group.editable_spiff | currency }}</span>
                                                    </div>
                                                    
                                                    <div class="group-images mt-2" v-if="group.children.some(c => c.image_url)">
                                                        <div class="d-flex align-items-center">
                                                            <div v-for="(child, childIdx) in group.children.slice(0, 3)" :key="childIdx" class="mr-1">
                                                                <img v-if="child.image_url" :src="child.image_url" 
                                                                     style="width: 30px; height: 30px; object-fit: cover; border-radius: 4px;"
                                                                     @click="openImagePreview(child.image_url)">
                                                            </div>
                                                            <small class="text-muted" v-if="group.children.length > 3">
                                                                +{{ group.children.length - 3 }} items
                                                            </small>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Add to Queue Button -->
                                    <b-button 
                                        block 
                                        variant="success"
                                        @click="addGroupsToQueue"
                                        :disabled="selectedGroupsCount === 0"
                                        class="mt-3">
                                        <b-icon-plus-circle></b-icon-plus-circle>
                                        Add {{ selectedGroupsCount }} Group(s) to Queue
                                    </b-button>
                                </div>
                            </div>
                        </div>

                        <!-- Validation Messages -->
                        <div v-if="groupValidationError" class="validation-error mt-3">
                            <b-alert variant="danger" show class="mb-0">
                                <b-icon-exclamation-triangle-fill class="mr-2"></b-icon-exclamation-triangle-fill>
                                {{ groupValidationError }}
                            </b-alert>
                        </div>
                    </div>

                    <!-- Queue Section -->
                    <div class="queue-section mt-4" v-if="groupsToCreate.length > 0">
                        <div class="queue-header">
                            <h6 class="mb-0 d-flex align-items-center">
                                <b-icon-list-check class="mr-2"></b-icon-list-check>
                                Queue ({{ groupsToCreate.length }})
                            </h6>
                            <b-button size="sm" variant="outline-danger" @click="groupsToCreate = []">
                                <b-icon-trash></b-icon-trash> Clear All
                            </b-button>
                        </div>
                        
                        <div class="queue-list">
                            <div v-for="(group, idx) in groupsToCreate" :key="idx" 
                                 class="queue-item">
                                <div class="d-flex align-items-start">
                                    <!-- Group Image -->
                                    <div v-if="group.image_url" class="mr-2">
                                        <img :src="group.image_url" 
                                             style="width: 40px; height: 40px; object-fit: cover; border-radius: 4px;">
                                    </div>
                                    <div class="flex-grow-1">
                                        <div class="d-flex justify-content-between align-items-center">
                                            <div>
                                                <span class="font-weight-bold">{{ group.product_name }}</span>
                                                <small class="d-block text-muted">{{ group.product_sku }}</small>
                                                <small class="d-block text-info" v-if="group.subcategoryWithQuantities">
                                                    {{ group.subcategoryWithQuantities.join(' + ') }}
                                                </small>
                                            </div>
                                            <b-button size="xs" variant="outline-danger" @click="removeGroup(idx)">
                                                <b-icon-x></b-icon-x>
                                            </b-button>
                                        </div>
                                        <div class="queue-item-details mt-1">
                                            <small><strong>FOB Price:</strong> ${{ formatNumber(group.fob_price_total) }}</small><br>
                                            <small><strong>Selling Price:</strong> ${{ formatNumber(group.selling_price_total) }}</small><br>
                                            <small v-if="group.vendor_sku"><strong>Vendor SKU:</strong> {{ group.vendor_sku }}</small><br>
                                            <small v-if="group.spiff"><strong>Spiff:</strong> ${{ formatNumber(group.spiff) }}</small>
                                        </div>
                                        <div class="queue-item-products mt-1">
                                            <b-badge 
                                                v-for="child in group.children.slice(0, 3)" 
                                                :key="child.product_sku"
                                                variant="secondary"
                                                class="mr-1">
                                                {{ child.product_sku }}
                                            </b-badge>
                                            <b-badge v-if="group.children.length > 3" variant="secondary">
                                                +{{ group.children.length - 3 }} more
                                            </b-badge>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <b-button 
                            block 
                            variant="warning"
                            @click="submitAllGroups"
                            :disabled="submittingGroups"
                            class="mt-3">
                            <b-spinner small v-if="submittingGroups"></b-spinner>
                            <b-icon-cloud-upload v-else></b-icon-cloud-upload>
                            {{ submittingGroups ? 'Submitting...' : `Submit ${groupsToCreate.length} Group(s)` }}
                        </b-button>
                    </div>
                </b-card>
            </div>
        </div>

        <!-- Floating Action Button -->
        <div class="floating-action">
            <b-button 
                variant="primary" 
                size="lg" 
                @click="submitAllData" 
                :disabled="submittingAll || !hasUnsavedItems"
                class="shadow-lg rounded-circle p-3"
                v-b-tooltip.hover.right="'Submit All Data'">
                <b-spinner small v-if="submittingAll"></b-spinner>
                <b-icon-cloud-upload v-else font-scale="1.5"></b-icon-cloud-upload>
            </b-button>
            <span v-if="totalUnsavedItems > 0" class="floating-badge">{{ totalUnsavedItems }}</span>
        </div>
    </div>
</template>

<script>
import Loading from 'vue-loading-overlay';
import QuaggaScanner from "@/components/importables/QuaggaScanner.vue";
import 'vue-loading-overlay/dist/vue-loading.css';
import Requestor from "@/utilities/Requestor";
import { 
    BIconCollection, BIconCollectionFill, BIconBox, BIconBoxSeam, BIconBoxSeamFill, BIconBoxes,
    BIconX, BIconTrash, BIconPlusCircle, BIconPlus, 
    BIconEye, BIconEyeSlash, BIconCheck, BIconCheckCircle,
    BIconLayers, BIconLayersFill, BIconCloudUpload, BIconInfoCircle,
    BIconArrowRepeat, BIconChevronDown, BIconChevronUp, BIconChevronRight,
    BIconThreeDotsVertical, BIconFiles, BIconArrowCounterclockwise,
    BIconLightningChargeFill, BIconBarChart, BIconMagic, BIconListCheck,
    BIconExclamationTriangleFill, BIconImage, BIconRulers, BIconBell, 
    BIconExclamationTriangle, BIconExclamationTriangleFill as BIconExclamationTriangleFillIcon,
    BIconTag, BIconUpcScan, BIconCamera, BIconSearch, BIconRulers as BIconRulersIcon
} from 'bootstrap-vue'

export default {
    name: "HierarchicalProductManagement",
    components: { 
        Loading,
        BIconCollection, BIconCollectionFill, BIconBox, BIconBoxSeam, BIconBoxSeamFill, BIconBoxes,
        BIconX, BIconTrash, BIconPlusCircle, BIconPlus, 
        BIconEye, BIconEyeSlash, BIconCheck, BIconCheckCircle,
        BIconLayers, BIconLayersFill, BIconCloudUpload, BIconInfoCircle,
        BIconArrowRepeat, BIconChevronDown, BIconChevronUp, BIconChevronRight,
        BIconThreeDotsVertical, BIconFiles, BIconArrowCounterclockwise,
        BIconLightningChargeFill, BIconBarChart, BIconMagic, BIconListCheck,
        BIconExclamationTriangleFill, BIconImage, BIconRulers, BIconBell, 
        BIconExclamationTriangle, BIconExclamationTriangleFillIcon,
        BIconTag, BIconUpcScan, BIconCamera, BIconSearch, BIconRulersIcon, QuaggaScanner 
    },
    data: function() {
        return {
            packet: {},
            vendors: [],
            categories: [],
            productCurrentCamera: 'environment',
            currentProductForScan: null,
            batchScanMode: false,
            batchScannedItems: [],
            reminderOptions: [
                { text: 'Weekly', value: 'weekly' },
                { text: 'Bi-weekly', value: 'biweekly' },
                { text: 'Monthly', value: 'monthly' }
            ],
            // Collections data
            collections: [],
            collectionCounter: 0,
            selectedFunctionality: null,
            
            // Simple products
            productEntryMode: 'collection',
            simpleProducts: [],
            simpleProductCounter: 0,
            
            // Low Inventory Alert
            showLowInventoryModal: false,
            currentAlertProduct: null,
            currentAlertProductType: null,
            alertConfig: {
                alert_type: 'threshold',
                threshold_quantity: 10,
                notification_methods: ['email', 'dashboard'],
                alert_emails: '',
                notes: '',
                reminder_frequency: 'weekly'
            },
            currentInventoryLevel: null,
            alertTypeOptions: [
                { text: 'Threshold Alert', value: 'threshold' },
                { text: 'Percentage Alert', value: 'percentage' },
                { text: 'Reorder Point', value: 'reorder_point' }
            ],
            
            // Group quantity configuration
            groupQuantityConfig: {
                products: [],
                variations: [],
                totalFob: 0,
                totalSelling: 0,
                totalCubes: 0
            },
            currentGroupCombination: null,
            
            // Caches for options
            categoryTypes: {},
            categoryColors: {},
            categorySizes: {},
            categoryMaterials: {},
            categoryFunctionalities: {},
            categorySubcategories: {},
            categoryBedTypes: {},
            categoryStyles: {},
            categorySectionalShapes: {},
            subsubcategoryCache: {},
            
            // Code mappings for SKU generation
            typeCodes: {},
            colorCodes: {},
            materialCodes: {},
            sizeCodes: {},
            subcategoryCodes: {},
            functionalityCodes: {},
            subsubcategoryCodes: {},
            bedTypeCodes: {},
            styleCodes: {},
            sectionalShapeCodes: {},
            
            // SKU counters
            skuCounter: 100,
            groupSkuCounter: 1000,
            
            // Group creation variables
            showGroupPanel: true,
            selectedForGrouping: [],
            selectedGroupSize: 3,
            generatedGroups: [],
            groupsToCreate: [],
            generatingGroups: false,
            groupValidationError: null,
            groupFilter: {
                collectionId: null
            },
            expandedCollections: {},
            
            // Success modal
            showSuccessModal: false,
            savedCollectionsCount: 0,
            savedParentProductsCount: 0,
            savedChildProductsCount: 0,
            savedGroupsCount: 0,
            savedSimpleProductsCount: 0,
            
            // Loading states
            items_being_submitted: false,
            submittingAll: false,
            submittingGroups: false,
            updateChildren: false,
            collectionCounters: {}
        }
    },
    filters: {
        currency(value) {
            if (!value && value !== 0) return '$0.00';
            return '$' + parseFloat(value).toFixed(2);
        }
    },
    computed: {
        isAdmin() {
            const user = this.packet.userDetails;
            if (user.userrole === 'admin') return true;
            const isAdminByName = user.fullname && user.fullname.toLowerCase().includes('admin');
            const isAdminByEmail = user.email && user.email.toLowerCase().includes('admin');
            return user.role === 'admin' || 
                   user.is_admin === true ||
                   user.user_type === 'admin' ||
                   user.is_superuser === true ||
                   user.user_role === 'admin' ||
                   isAdminByName ||
                   isAdminByEmail;
        },
        
        userLocationId() {
            const user = this.packet.userDetails;
            return user.properties.location_id || 
                   user.location_id || 
                   user.assigned_location || 
                   null;
        },
        
        totalParentProducts() {
            return this.collections.reduce((total, collection) => {
                return total + collection.products.filter(p => !p.parent_product_id).length;
            }, 0);
        },
        
        totalChildProducts() {
            return this.collections.reduce((total, collection) => {
                return total + collection.products.reduce((childTotal, product) => {
                    return childTotal + (product.child_products ? product.child_products.length : 0);
                }, 0);
            }, 0);
        },
        
        totalUnsavedItems() {
            let count = 0;
            
            this.collections.forEach(collection => {
                if (!collection.saved) count++;
                collection.products.forEach(product => {
                    if (!product.saved) count++;
                    if (product.child_products) {
                        product.child_products.forEach(child => {
                            if (!child.saved) count++;
                        });
                    }
                });
            });
            
            this.simpleProducts.forEach(product => {
                if (!product.saved) count++;
            });
            
            count += this.groupsToCreate.length;
            return count;
        },
        
        hasUnsavedItems() {
            return this.totalUnsavedItems > 0;
        },
        
        selectedGroupsCount() {
            return this.generatedGroups.filter(group => group.selected).length;
        },
        
        isAlertConfigValid() {
            return this.currentAlertProduct && 
                   this.alertConfig.threshold_quantity > 0 && 
                   this.alertConfig.alert_type &&
                   this.alertConfig.threshold_quantity !== null &&
                   this.alertConfig.threshold_quantity !== '';
        },
        
        isGroupQuantityValid() {
            const config = this.groupQuantityConfig;
            if (!config.variations || config.variations.length === 0) return false;
            
            // Check that all products have quantities
            return config.products.every(p => p.quantity > 0);
        }
    },
    methods: {
        formatNumber(value) {
            if (!value && value !== 0) return '0.00';
            return parseFloat(value).toFixed(2);
        },
        
        formatReminderDate(dateString) {
            if (!dateString) return 'Not sent yet';
            const date = new Date(dateString);
            return date.toLocaleDateString();
        },
        
        resetGroupFobPrice(group) {
            group.editable_fob_price = group.original_fob_price;
        },
        
        resetGroupSellingPrice(group) {
            group.editable_selling_price = group.original_selling_price;
        },
        
        resetGroupVendorSku(group) {
            group.editable_vendor_sku = group.original_vendor_sku;
        },
        
        resetGroupSpiff(group) {
            group.editable_spiff = group.original_spiff;
        },
        
        uploadGroupImage(group) {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = 'image/*';
            
            input.onchange = async (e) => {
                const file = e.target.files[0];
                if (!file) return;
                
                if (!file.type.startsWith('image/')) {
                    this.$bvToast.toast('Please select an image file', {
                        title: 'Invalid File',
                        variant: 'danger',
                        autoHideDelay: 3000
                    });
                    return;
                }
                
                if (file.size > 5 * 1024 * 1024) {
                    this.$bvToast.toast('Image size should be less than 5MB', {
                        title: 'File Too Large',
                        variant: 'danger',
                        autoHideDelay: 3000
                    });
                    return;
                }
                
                group.uploading = true;
                
                try {
                    const base64 = await this.fileToBase64(file);
                    group.editable_image_url = base64;
                    group.image_url = base64;
                    
                    this.$bvToast.toast('Image added to group', {
                        title: 'Success',
                        variant: 'success',
                        autoHideDelay: 2000
                    });
                } catch (error) {
                    console.error('Upload error:', error);
                    this.$bvToast.toast('Failed to load image', {
                        title: 'Error',
                        variant: 'danger',
                        autoHideDelay: 3000
                    });
                } finally {
                    group.uploading = false;
                }
            };
            
            input.click();
        },

        // ============================================
        // GROUP QUANTITY CONFIGURATION METHODS
        // ============================================
        
        /**
         * Override the generateGroups method to show quantity configuration
         */
        generateGroups() {
            if (!this.validateGroupSelections()) {
                this.$bvToast.toast(this.groupValidationError, {
                    title: "Validation Error",
                    variant: "danger",
                    autoHideDelay: 3000
                });
                return;
            }
            
            if (this.selectedForGrouping.length < 2) {
                this.$bvToast.toast("Select at least 2 products to create groups", {
                    title: "Warning",
                    variant: "warning",
                    autoHideDelay: 2000
                });
                return;
            }
            
            this.generatingGroups = true;
            this.generatedGroups = [];
            
            try {
                const collection = this.collections.find(c => c.id === this.selectedForGrouping[0].collection_id);
                if (!collection) {
                    throw new Error("Collection not found");
                }
                
                let comboSize = this.selectedGroupSize;
                if (this.selectedGroupSize === 'all') {
                    comboSize = this.selectedForGrouping.length;
                }
                
                let combinations = [];
                
                if (this.isLivingRoomCategory(collection.category_id)) {
                    combinations = this.generateLivingRoomCombinations(collection);
                } else {
                    combinations = this.generateCombinations(this.selectedForGrouping, comboSize);
                }
                
                // For each combination, show quantity configuration modal
                if (combinations.length > 0) {
                    // Store the first combination for configuration
                    this.currentGroupCombination = {
                        collection: collection,
                        products: combinations[0],
                        allCombinations: combinations
                    };
                    
                    // Show the quantity configuration modal
                    this.openQuantityConfig(combinations[0], collection);
                }
                
            } catch (error) {
                console.error("Error generating groups:", error);
                this.$bvToast.toast("Error generating groups", {
                    title: "Error",
                    variant: "danger",
                    autoHideDelay: 3000
                });
            } finally {
                this.generatingGroups = false;
            }
        },
        
        /**
         * Generate living room combinations
         */
        generateLivingRoomCombinations(collection) {
            const sofa = this.selectedForGrouping.find(p => {
                const subcatName = this.getSubcategoryName(p.subcategory_id).toUpperCase();
                return subcatName.includes('SOFA');
            });
            const loveseat = this.selectedForGrouping.find(p => {
                const subcatName = this.getSubcategoryName(p.subcategory_id).toUpperCase();
                return subcatName.includes('LOVESEAT') || subcatName.includes('LOVE SEAT');
            });
            const chair = this.selectedForGrouping.find(p => {
                const subcatName = this.getSubcategoryName(p.subcategory_id).toUpperCase();
                return subcatName.includes('CHAIR');
            });
            const ottoman = this.selectedForGrouping.find(p => {
                const subcatName = this.getSubcategoryName(p.subcategory_id).toUpperCase();
                return subcatName.includes('OTTOMAN');
            });
            
            const combinations = [];
            
            if (this.selectedGroupSize === 2 && sofa && loveseat) {
                combinations.push([sofa, loveseat]);
            } else if (this.selectedGroupSize === 3 && sofa && loveseat && chair) {
                combinations.push([sofa, loveseat, chair]);
            } else if (this.selectedGroupSize === 4 && sofa && loveseat && chair && ottoman) {
                combinations.push([sofa, loveseat, chair, ottoman]);
            }
            
            return combinations;
        },
        
        /**
         * Open the quantity configuration modal
         */
        openQuantityConfig(products, collection) {
            // Initialize the config with products
            this.groupQuantityConfig.products = products.map(p => ({
                ...p,
                subcategory_name: this.getSubcategoryName(p.subcategory_id),
                quantity: 1, // Default quantity
                fob_price: Number(p.fob_price) || 0,
                selling_price: Number(p.selling_price) || 0,
                cubes: Number(p.cubes) || 0
            }));
            
            // Create initial variation with default quantities
            this.groupQuantityConfig.variations = [{
                quantities: this.groupQuantityConfig.products.map(p => p.quantity),
                description: this.generateVariationDescription(this.groupQuantityConfig.products)
            }];
            
            this.calculateQuantityTotals();
            
            // Show the modal
            this.$bvModal.show('modal-group-quantity-config');
        },
        
        /**
         * Calculate totals based on current quantities
         */
        calculateQuantityTotals() {
            const config = this.groupQuantityConfig;
            let totalFob = 0;
            let totalSelling = 0;
            let totalCubes = 0;
            
            config.products.forEach(product => {
                const qty = product.quantity || 0;
                totalFob += product.fob_price * qty;
                totalSelling += product.selling_price * qty;
                totalCubes += product.cubes * qty;
            });
            
            config.totalFob = totalFob;
            config.totalSelling = totalSelling;
            config.totalCubes = totalCubes.toFixed(3);
        },
        
        /**
         * Update totals when quantities change
         */
        updateQuantityTotals() {
            this.calculateQuantityTotals();
            
            // Update the current variation
            if (this.groupQuantityConfig.variations.length > 0) {
                this.groupQuantityConfig.variations[0].quantities = 
                    this.groupQuantityConfig.products.map(p => p.quantity);
                this.groupQuantityConfig.variations[0].description = 
                    this.generateVariationDescription(this.groupQuantityConfig.products);
            }
        },
        
        /**
         * Add a new variation
         */
        addVariation() {
            this.groupQuantityConfig.variations.push({
                quantities: this.groupQuantityConfig.products.map(p => p.quantity),
                description: this.generateVariationDescription(this.groupQuantityConfig.products)
            });
        },
        
        /**
         * Duplicate a variation
         */
        duplicateVariation(index) {
            const variation = this.groupQuantityConfig.variations[index];
            this.groupQuantityConfig.variations.push({
                quantities: [...variation.quantities],
                description: variation.description
            });
        },
        
        /**
         * Remove a variation
         */
        removeVariation(index) {
            this.groupQuantityConfig.variations.splice(index, 1);
        },
        
        /**
         * Generate description for a variation
         */
        generateVariationDescription(products) {
            return products.map(p => `${p.subcategory_name}: ${p.quantity}`).join(', ');
        },
        
        /**
         * Get variation description for display
         */
        getVariationDescription(variation) {
            const products = this.groupQuantityConfig.products;
            const parts = products.map((p, idx) => {
                const qty = variation.quantities[idx] || 0;
                return `${p.subcategory_name} (${qty})`;
            });
            return parts.join(' | ');
        },
        
        /**
         * Apply the quantity configurations and create groups
         */
        applyGroupQuantities() {
            const config = this.groupQuantityConfig;
            
            if (!this.isGroupQuantityValid) {
                this.$bvToast.toast("Please ensure all products have quantities", {
                    title: "Validation Error",
                    variant: "warning",
                    autoHideDelay: 3000
                });
                return;
            }
            
            // Create a group for each variation
            config.variations.forEach((variation, varIdx) => {
                // Create the product list with quantities
                const productsWithQty = config.products.map((product, idx) => {
                    const qty = variation.quantities[idx] || 0;
                    return {
                        ...product,
                        quantity: qty,
                        fob_price_total: product.fob_price * qty,
                        selling_price_total: product.selling_price * qty,
                        cubes_total: product.cubes * qty
                    };
                });
                
                // Filter out products with 0 quantity
                const filteredProducts = productsWithQty.filter(p => p.quantity > 0);
                
                if (filteredProducts.length > 0) {
                    const group = this.createGroupFromCombinationWithQuantities(
                        this.currentGroupCombination.collection,
                        filteredProducts,
                        varIdx
                    );
                    this.generatedGroups.push(group);
                }
            });
            
            // Select all generated groups
            this.generatedGroups.forEach(group => group.selected = true);
            
            // Close the modal
            this.$bvModal.hide('modal-group-quantity-config');
            
            this.$bvToast.toast(`Created ${config.variations.length} group variation(s)`, {
                title: "Success",
                variant: "success",
                autoHideDelay: 3000
            });
            
            // Reset the config
            this.resetGroupQuantityModal();
        },
        
        /**
         * Reset the group quantity modal state
         */
        resetGroupQuantityModal() {
            this.groupQuantityConfig = {
                products: [],
                variations: [],
                totalFob: 0,
                totalSelling: 0,
                totalCubes: 0
            };
            this.currentGroupCombination = null;
        },
        
        /**
         * Create group from combination with quantities
         * Following naming convention based on category:
         * - Living Room: MONTAGE 2PC SOFA & LOVESEAT IN ANTHRACITE
         * - Dining: MONTAGE 2PC DINING SET IN ANTHRACITE
         * - Bedroom: MONTAGE 3PC BEDROOM SET IN ANTHRACITE
         * 
         * SKU: 2PC-165-06-SF-LS (using same counter and color code as parent products)
         */
        createGroupFromCombinationWithQuantities(collection, products, index) {
            const productCount = products.reduce((sum, p) => sum + p.quantity, 0);
            
            // Get subcategory names and codes
            const subcategoryNames = products.map(p => p.subcategory_name);
            const subcategoryCodes = products.map(p => {
                const code = this.subcategoryCodes[p.subcategory_id];
                return code ? code.code : "XXX";
            });
            
            // Get color name and color code
            const colorName = collection.color_id && this.colorCodes[collection.color_id] 
                ? this.colorCodes[collection.color_id].name 
                : '';
            const colorCode = collection.color_id && this.colorCodes[collection.color_id]
                ? this.colorCodes[collection.color_id].code
                : '00';
            
            // Determine category type
            const categoryId = collection.category_id;
            const isLivingRoom = this.isLivingRoomCategory(categoryId);
            const isDiningRoom = this.isDiningRoomCategory(categoryId);
            const isBedroom = this.isBedroomCategory(categoryId);
            
            // Build product name based on category
            let productName = `${collection.name} ${productCount}PC `;
            let setType = '';
            
            // Determine subcategory types for description
            const hasSofa = subcategoryNames.some(name => 
                name.toUpperCase().includes('SOFA')
            );
            const hasLoveseat = subcategoryNames.some(name => 
                name.toUpperCase().includes('LOVESEAT') || name.toUpperCase().includes('LOVE SEAT')
            );
            const hasChair = subcategoryNames.some(name => 
                name.toUpperCase().includes('CHAIR') && 
                !name.toUpperCase().includes('LOVESEAT') && 
                !name.toUpperCase().includes('LOVE SEAT')
            );
            const hasOttoman = subcategoryNames.some(name => 
                name.toUpperCase().includes('OTTOMAN')
            );
            const hasSectional = subcategoryNames.some(name => 
                name.toUpperCase().includes('SECTIONAL')
            );
            const hasDiningTable = subcategoryNames.some(name => 
                name.toUpperCase().includes('DINING') || 
                name.toUpperCase().includes('TABLE')
            );
            const hasBed = subcategoryNames.some(name => 
                name.toUpperCase().includes('BED')
            );
            const hasDresser = subcategoryNames.some(name => 
                name.toUpperCase().includes('DRESSER')
            );
            const hasMirror = subcategoryNames.some(name => 
                name.toUpperCase().includes('MIRROR')
            );
            const hasNightstand = subcategoryNames.some(name => 
                name.toUpperCase().includes('NIGHTSTAND') || name.toUpperCase().includes('NIGHT STAND')
            );
            const hasChest = subcategoryNames.some(name => 
                name.toUpperCase().includes('CHEST')
            );
            
            // Build description based on category
            if (isLivingRoom) {
                // Living Room: SOFA SET or specific combination
                if (hasSofa && hasLoveseat) {
                    setType = 'SOFA & LOVESEAT';
                } else if (hasSofa && hasChair && hasOttoman) {
                    setType = 'SOFA, CHAIR & OTTOMAN';
                } else if (hasSofa && hasChair) {
                    setType = 'SOFA & CHAIR';
                } else if (hasSofa && hasOttoman) {
                    setType = 'SOFA & OTTOMAN';
                } else if (hasSofa && hasLoveseat && hasChair) {
                    setType = 'SOFA, LOVESEAT & CHAIR';
                } else if (hasSofa && hasLoveseat && hasChair && hasOttoman) {
                    setType = 'SOFA, LOVESEAT, CHAIR & OTTOMAN';
                } else if (hasSectional) {
                    setType = 'SECTIONAL';
                } else if (hasSofa) {
                    setType = 'SOFA';
                } else if (hasLoveseat) {
                    setType = 'LOVESEAT';
                } else if (hasChair) {
                    setType = 'CHAIR';
                } else {
                    setType = 'LIVING ROOM SET';
                }
                
                // Add "SET" suffix if multiple items
                if (!setType.includes('&') && !setType.includes(',') && productCount > 1) {
                    setType = `${setType} SET`;
                }
                
                // Add color
                if (colorName) {
                    productName = `${collection.name} ${productCount}PC ${setType} IN ${colorName.toUpperCase()}`;
                } else {
                    productName = `${collection.name} ${productCount}PC ${setType}`;
                }
                
            } else if (isDiningRoom) {
                // Dining Room: DINING SET
                let diningType = 'DINING';
                if (hasDiningTable && hasChair) {
                    if (productCount === 2) {
                        diningType = 'DINING SET';
                    } else if (productCount === 5) {
                        diningType = '5PC DINING SET';
                    } else {
                        diningType = `${productCount}PC DINING SET`;
                    }
                } else {
                    diningType = 'DINING SET';
                }
                
                if (colorName) {
                    productName = `${collection.name} ${diningType} IN ${colorName.toUpperCase()}`;
                } else {
                    productName = `${collection.name} ${diningType}`;
                }
                setType = diningType;
                
            } else if (isBedroom) {
                // Bedroom: BEDROOM SET
                let bedroomType = 'BEDROOM';
                if (hasBed) {
                    if (hasDresser && hasMirror) {
                        bedroomType = 'BEDROOM SET';
                    } else if (hasDresser) {
                        bedroomType = 'BED & DRESSER';
                    } else if (hasNightstand) {
                        bedroomType = 'BED & NIGHTSTAND';
                    } else {
                        bedroomType = 'BEDROOM SET';
                    }
                } else {
                    bedroomType = 'BEDROOM SET';
                }
                
                if (colorName) {
                    productName = `${collection.name} ${productCount}PC ${bedroomType} IN ${colorName.toUpperCase()}`;
                } else {
                    productName = `${collection.name} ${productCount}PC ${bedroomType}`;
                }
                setType = bedroomType;
                
            } else {
                // Default: Use subcategory names
                const uniqueSubcats = [...new Set(subcategoryNames)];
                const combinedSubcats = uniqueSubcats.join(' & ');
                if (colorName) {
                    productName = `${collection.name} ${productCount}PC ${combinedSubcats} IN ${colorName.toUpperCase()}`;
                } else {
                    productName = `${collection.name} ${productCount}PC ${combinedSubcats}`;
                }
                setType = combinedSubcats;
            }
            
            // =============================================
            // GENERATE SKU WITH SAME COUNTER AS PARENT PRODUCTS
            // Format: 3PC-330-14-BD-DR-MR
            // Use the base_sku_number (same as parent products)
            // =============================================
            
            // Get the counter from the collection's base_sku_number
            // This is the same counter used by all parent products
            const counter = collection.base_sku_number;
            // DO NOT increment the counter - groups share the same counter as parent products
            
            // ORDER PRODUCTS FOR SKU
            const orderedProducts = [];
            
            if (isLivingRoom) {
                // Living Room: Sofa first, then Loveseat, then Chair, then Ottoman
                if (hasSofa) {
                    const sofaProduct = products.find(p => 
                        p.subcategory_name.toUpperCase().includes('SOFA')
                    );
                    if (sofaProduct) orderedProducts.push(sofaProduct);
                }
                if (hasLoveseat) {
                    const loveseatProduct = products.find(p => 
                        p.subcategory_name.toUpperCase().includes('LOVESEAT') || 
                        p.subcategory_name.toUpperCase().includes('LOVE SEAT')
                    );
                    if (loveseatProduct) orderedProducts.push(loveseatProduct);
                }
                if (hasChair) {
                    const chairProducts = products.filter(p => 
                        p.subcategory_name.toUpperCase().includes('CHAIR') &&
                        !p.subcategory_name.toUpperCase().includes('LOVESEAT') &&
                        !p.subcategory_name.toUpperCase().includes('LOVE SEAT') &&
                        !p.subcategory_name.toUpperCase().includes('DINING')
                    );
                    orderedProducts.push(...chairProducts);
                }
                if (hasOttoman) {
                    const ottomanProduct = products.find(p => 
                        p.subcategory_name.toUpperCase().includes('OTTOMAN')
                    );
                    if (ottomanProduct) orderedProducts.push(ottomanProduct);
                }
                if (hasSectional) {
                    const sectionalProduct = products.find(p => 
                        p.subcategory_name.toUpperCase().includes('SECTIONAL')
                    );
                    if (sectionalProduct) orderedProducts.push(sectionalProduct);
                }
            } else if (isDiningRoom) {
                // Dining: Dining Table first, then Chairs
                if (hasDiningTable) {
                    const diningProducts = products.filter(p => 
                        p.subcategory_name.toUpperCase().includes('DINING') || 
                        p.subcategory_name.toUpperCase().includes('TABLE')
                    );
                    orderedProducts.push(...diningProducts);
                }
                const chairProducts = products.filter(p => 
                    p.subcategory_name.toUpperCase().includes('CHAIR') &&
                    !p.subcategory_name.toUpperCase().includes('DINING') &&
                    !p.subcategory_name.toUpperCase().includes('LOVESEAT') &&
                    !p.subcategory_name.toUpperCase().includes('LOVE SEAT')
                );
                orderedProducts.push(...chairProducts);
            } else if (isBedroom) {
                // Bedroom: Bed first, then Dresser, then Mirror, then Nightstand, then Chest
                if (hasBed) {
                    const bedProduct = products.find(p => 
                        p.subcategory_name.toUpperCase().includes('BED')
                    );
                    if (bedProduct) orderedProducts.push(bedProduct);
                }
                if (hasDresser) {
                    const dresserProduct = products.find(p => 
                        p.subcategory_name.toUpperCase().includes('DRESSER')
                    );
                    if (dresserProduct) orderedProducts.push(dresserProduct);
                }
                if (hasMirror) {
                    const mirrorProduct = products.find(p => 
                        p.subcategory_name.toUpperCase().includes('MIRROR')
                    );
                    if (mirrorProduct) orderedProducts.push(mirrorProduct);
                }
                if (hasNightstand) {
                    const nightstandProducts = products.filter(p => 
                        p.subcategory_name.toUpperCase().includes('NIGHTSTAND') || 
                        p.subcategory_name.toUpperCase().includes('NIGHT STAND')
                    );
                    orderedProducts.push(...nightstandProducts);
                }
                if (hasChest) {
                    const chestProduct = products.find(p => 
                        p.subcategory_name.toUpperCase().includes('CHEST')
                    );
                    if (chestProduct) orderedProducts.push(chestProduct);
                }
            } else {
                // Default order
                orderedProducts.push(...products);
            }
            
            // Generate SKU codes based on subcategory
            const skuCodes = orderedProducts.map(p => {
                const name = p.subcategory_name.toUpperCase();
                
                // LIVING ROOM CODES
                if (name.includes('SOFA') && !name.includes('LOVESEAT') && !name.includes('LOVE SEAT')) {
                    return 'SF'; // Sofa
                } else if (name.includes('LOVESEAT') || name.includes('LOVE SEAT')) {
                    return 'LS'; // Loveseat
                } else if (name.includes('OTTOMAN')) {
                    return 'OT'; // Ottoman
                } else if (name.includes('SECTIONAL')) {
                    return 'SE'; // Sectional
                    
                // DINING ROOM CODES
                } else if (name.includes('DINING') || name.includes('TABLE')) {
                    return 'DT'; // Dining Table
                } else if (name.includes('CHAIR') && !name.includes('LOVESEAT') && !name.includes('LOVE SEAT')) {
                    return 'CH'; // Chair
                    
                // BEDROOM CODES
                } else if (name.includes('BED')) {
                    return 'BD'; // Bed
                } else if (name.includes('NIGHTSTAND') || name.includes('NIGHT STAND')) {
                    return 'NS'; // Nightstand
                } else if (name.includes('DRESSER')) {
                    return 'DR'; // Dresser
                } else if (name.includes('MIRROR')) {
                    return 'MR'; // Mirror
                } else if (name.includes('CHEST')) {
                    return 'CT'; // Chest
                    
                // DEFAULT
                } else {
                    // Use first 2 letters of subcategory
                    return name.substring(0, 2);
                }
            });
            
            // Remove duplicates but maintain order
            const uniqueSkuCodes = [];
            const seen = new Set();
            for (const code of skuCodes) {
                if (!seen.has(code)) {
                    seen.add(code);
                    uniqueSkuCodes.push(code);
                }
            }
            
            // Build SKU: 3PC-330-14-BD-DR-MR
            // Format: {ProductCount}PC-{Counter}-{ColorCode}-{SubcategoryCodes}
            const quantityCode = `${productCount}PC`;
            const sku = `${quantityCode}-${counter}-${colorCode}-${uniqueSkuCodes.join('-')}`;
            
            // Save the counter back to collection (though we didn't change it)
            this.saveCollectionCounters();
            
            // Calculate totals
            const totalFob = products.reduce((sum, p) => sum + p.fob_price_total, 0);
            const totalSelling = products.reduce((sum, p) => sum + p.selling_price_total, 0);
            const totalCubes = products.reduce((sum, p) => sum + (p.cubes * p.quantity), 0);
            
            // Calculate spiff - use the sum of all product spiffs or a default
            const totalSpiff = products.reduce((sum, p) => sum + (p.spiff || 0), 0);
            
            // Get the first product's image for the group
            const groupImage = products[0].image_url || null;
            const combinedVendorSkus = products.map(p => p.vendor_sku || 'N/A').join(', ');
            
            // Build subcategory list with quantities for display
            const subcategoryWithQuantities = products.map(p => 
                `${p.quantity}×${p.subcategory_name}`
            );
            
            // Determine category type for reference
            let categoryType = 'MISC';
            if (isLivingRoom) categoryType = 'LIVING ROOM';
            else if (isDiningRoom) categoryType = 'DINING';
            else if (isBedroom) categoryType = 'BEDROOM';
            
            return {
                selected: true,
                productCount,
                collectionName: collection.name,
                subcategories: subcategoryNames,
                subcategoryCodes: subcategoryCodes,
                sku,
                product_name: productName,
                product_description: productName,
                category_id: collection.category_id,
                category_type: categoryType,
                vendor_id: collection.vendor_id,
                type_id: collection.type_id,
                subcategory_id: products[0].subcategory_id,
                color_id: collection.color_id,
                color_name: colorName,
                color_code: colorCode,
                material_id: collection.material_id,
                functionality_id: products[0].functionality_ids ? products[0].functionality_ids[0] : null,
                features: products[0].features || "",
                fob_price_total: totalFob,
                selling_price_total: totalSelling,
                cubes: Number(totalCubes).toFixed(3),
                spiff: totalSpiff,
                is_box: false,
                productSkus: products.map(p => p.product_sku),
                vendor_skus: combinedVendorSkus,
                image_url: groupImage,
                // Editable fields for group
                editable_fob_price: totalFob,
                editable_selling_price: totalSelling,
                editable_vendor_sku: combinedVendorSkus,
                editable_image_url: groupImage,
                editable_spiff: totalSpiff,
                original_fob_price: totalFob,
                original_selling_price: totalSelling,
                original_vendor_sku: combinedVendorSkus,
                original_image_url: groupImage,
                original_spiff: totalSpiff,
                // Store the set type for display
                setType: setType,
                categoryType: categoryType,
                subcategoryWithQuantities: subcategoryWithQuantities,
                children: products.map(product => ({
                    product_id: product.product_id,
                    product_sku: product.product_sku,
                    product_name: product.product_description,
                    fob_price: Number(product.fob_price) || 0,
                    selling_price: Number(product.selling_price) || 0,
                    quantity: product.quantity || 1,
                    cubes: Number(product.cubes) || 0,
                    image_url: product.image_url,
                    vendor_sku: product.vendor_sku || '',
                    subcategory_name: product.subcategory_name,
                    spiff: product.spiff || 0
                }))
            };
        },
        
        isDiningRoomCategory(categoryId) {
            const category = this.categories.find(c => c.value === categoryId);
            return category && (
                category.text.toUpperCase().includes('DINING') ||
                category.text.toUpperCase().includes('TABLE')
            );
        },
        
        // ============================================
        // LOW INVENTORY ALERT METHODS
        // ============================================
        
        showLowInventoryAlert(product, productType) {
            console.log('Opening alert modal for:', product, productType);
            
            if (!product) {
                this.$bvToast.toast("Invalid product data", {
                    title: "Error",
                    variant: "danger",
                    autoHideDelay: 3000
                });
                return;
            }
            
            if (!product.saved || !product.product_id) {
                this.$bvToast.toast("Please save the product before setting up inventory alerts", {
                    title: "Product Not Saved",
                    variant: "warning",
                    autoHideDelay: 3000
                });
                return;
            }
            
            this.currentAlertProduct = product;
            this.currentAlertProductType = productType;
            
            this.alertConfig = {
                alert_type: 'threshold',
                threshold_quantity: 10,
                notification_methods: ['email', 'dashboard'],
                alert_emails: '',
                notes: '',
                reminder_frequency: 'weekly'
            };
            
            this.currentInventoryLevel = null;
            this.fetchCurrentInventory(product.product_id);
            
            this.showLowInventoryModal = true;
        },
        
        fetchCurrentInventory(productId) {
            if (!productId) {
                console.warn("No product ID provided for inventory fetch");
                this.currentInventoryLevel = null;
                return;
            }
            
            const path = "/api/product/get_product_inventory/";
            const data = { product_id: productId };
            const that = this;
            
            const successCallback = (response) => {
                if (response.data && response.data.result && response.data.result.current_stock !== undefined) {
                    that.currentInventoryLevel = response.data.result.current_stock;
                } else {
                    that.currentInventoryLevel = 0;
                }
            };
            
            const errorCallback = (error) => {
                console.error("Failed to fetch inventory:", error);
                that.currentInventoryLevel = null;
            };
            
            Requestor.postSyncP(path, data, this, successCallback, errorCallback);
        },
        
        resetLowInventoryModal() {
            this.alertConfig = {
                alert_type: 'threshold',
                threshold_quantity: 10,
                notification_methods: ['email', 'dashboard'],
                alert_emails: '',
                notes: '',
                reminder_frequency: 'weekly'
            };
            this.currentInventoryLevel = null;
            setTimeout(() => {
                if (!this.showLowInventoryModal) {
                    this.currentAlertProduct = null;
                    this.currentAlertProductType = null;
                }
            }, 300);
        },
        
        saveLowInventoryAlert() {
            if (!this.currentAlertProduct) {
                this.$bvToast.toast("No product selected", {
                    title: "Error",
                    variant: "danger",
                    autoHideDelay: 3000
                });
                this.showLowInventoryModal = false;
                return;
            }
            
            if (!this.isAlertConfigValid) {
                this.$bvToast.toast("Please fill all required fields", {
                    title: "Validation Error",
                    variant: "warning",
                    autoHideDelay: 3000
                });
                return;
            }
            
            const alertData = {
                product_id: this.currentAlertProduct.product_id,
                product_sku: this.currentAlertProduct.product_sku,
                product_type: this.currentAlertProductType,
                alert_type: this.alertConfig.alert_type,
                threshold_quantity: this.alertConfig.threshold_quantity,
                notification_methods: this.alertConfig.notification_methods,
                alert_emails: this.alertConfig.alert_emails,
                notes: this.alertConfig.notes,
                reminder_frequency: this.alertConfig.reminder_frequency,
                created_by: this.packet.userDetails.username
            };
            
            const path = "/api/product/set_low_inventory_alert/";
            const that = this;
            
            const successCallback = function(response) {
                if (response.data && response.data.error) {
                    that.$bvToast.toast(response.data.error, {
                        title: "Error",
                        variant: "danger",
                        autoHideDelay: 3000
                    });
                } else {
                    that.$bvToast.toast(`Low inventory alert configured for ${that.currentAlertProduct.product_sku}`, {
                        title: "Alert Set",
                        variant: "success",
                        autoHideDelay: 3000
                    });
                    
                    if (that.currentAlertProduct) {
                        that.$set(that.currentAlertProduct, 'low_inventory_alert', alertData);
                    }
                    
                    that.showLowInventoryModal = false;
                    that.currentAlertProduct = null;
                }
            };
            
            const errorCallback = function(error) {
                console.error("Save alert error:", error);
                that.$bvToast.toast(error.message || "Failed to set low inventory alert", {
                    title: "Error",
                    variant: "danger",
                    autoHideDelay: 3000
                });
            };
            
            Requestor.postSyncP(path, alertData, this, successCallback, errorCallback);
        },
        
        // ============================================
        // BARCODE SCANNER METHODS
        // ============================================
        
        openScannerForProduct(product) {
            this.currentProductForScan = product;
            this.$bvModal.show('modal-product-barcode-scanner');
        },
        
        startProductScanner() {
            if (this.$refs.productQuaggaScanner) {
                this.$refs.productQuaggaScanner.startScanner();
            }
        },
        
        stopProductScanner() {
            if (this.$refs.productQuaggaScanner) {
                this.$refs.productQuaggaScanner.stopScanner();
            }
            this.$bvModal.hide('modal-product-barcode-scanner');
            this.currentProductForScan = null;
        },
        
        toggleProductCamera() {
            this.productCurrentCamera = this.productCurrentCamera === 'environment' ? 'user' : 'environment';
        },
        
        handleProductBarcodeScan(barcodeValue) {
            console.log('Scanned barcode for product:', barcodeValue);
            if (this.currentProductForScan) {
                this.currentProductForScan.scannedBarcode = barcodeValue;
                this.lookupBarcodeForProduct(this.currentProductForScan);
                this.stopProductScanner();
            }
        },
        
        lookupBarcodeForProduct(product) {
            if (!product.scannedBarcode || product.scannedBarcode.trim() === '') {
                this.$bvToast.toast("Please enter or scan a barcode", {
                    title: "Info",
                    variant: "warning",
                    autoHideDelay: 2000
                });
                return;
            }
            
            const barcode = product.scannedBarcode.trim();
            product.lookingUpBarcode = true;
            
            const path = "/api/product/search_by_barcode/";
            const data = {
                barcode: barcode,
                location_id: null,
                include_sublocation_breakdown: false,
                include_damage_info: false
            };
            
            const that = this;
            
            const successCallback = function(response) {
                product.lookingUpBarcode = false;
                
                if (response.data.error) {
                    that.$bvToast.toast(response.data.error, {
                        title: "Barcode Not Found",
                        variant: "warning",
                        autoHideDelay: 2000
                    });
                } else if (response.data.result && response.data.result.length > 0) {
                    const foundProduct = response.data.result[0];
                    
                    if (!product.saved) {
                        product.product_sku = foundProduct.product_sku;
                        product.product_description = foundProduct.product_name;
                        product.fob_price = foundProduct.selling_price;
                        product.cubes = foundProduct.cubes;
                        product.quantity_per_box = foundProduct.quantity_per_box || 1;
                        product.vendor_id = foundProduct.vendor_id;
                        product.category_id = foundProduct.category_id;
                        
                        if (foundProduct.category_id) {
                            that.loadSimpleProductCategoryAttributes(product);
                            
                            if (foundProduct.subcategory_id) {
                                product.subcategory_id = foundProduct.subcategory_id;
                            }
                        }
                        
                        if (foundProduct.image_url) {
                            product.image_url = foundProduct.image_url;
                        }
                        
                        that.$bvToast.toast(`Product details loaded from barcode: ${foundProduct.product_sku}`, {
                            title: "Success",
                            variant: "success",
                            autoHideDelay: 2000
                        });
                    }
                } else {
                    that.$bvModal.msgBoxConfirm(
                        `No product found with barcode: ${barcode}. Would you like to create a new product with this SKU?`,
                        {
                            title: 'Product Not Found',
                            size: 'sm',
                            buttonSize: 'sm',
                            okVariant: 'primary',
                            okTitle: 'Yes, Use SKU',
                            cancelTitle: 'Cancel'
                        }
                    ).then(value => {
                        if (value && !product.saved) {
                            product.product_sku = barcode;
                            that.$bvToast.toast(`SKU set to: ${barcode}. Please fill in other details manually.`, {
                                title: "SKU Set",
                                variant: "info",
                                autoHideDelay: 2000
                            });
                        }
                    });
                }
            };
            
            const errorCallback = function(error) {
                console.error("Barcode lookup error:", error);
                product.lookingUpBarcode = false;
                that.$bvToast.toast("Error looking up barcode. Please try again.", {
                    title: "Error",
                    variant: "danger",
                    autoHideDelay: 2000
                });
            };
            
            Requestor.getSyncP(path, data, this, successCallback, errorCallback);
        },
        
        // ============================================
        // LOCATION METHODS
        // ============================================
        
        getLocationOptions() {
            if (!this.packet.locsubs || !Array.isArray(this.packet.locsubs) || this.packet.locsubs.length === 0) {
                return [];
            }
            
            let availableLocations = [...this.packet.locsubs];
            
            if (!this.isAdmin && this.userLocationId) {
                availableLocations = availableLocations.filter(loc => {
                    const locId = loc.location_id || loc.id;
                    return locId === this.userLocationId;
                });
            }
            
            return availableLocations.map(loc => {
                const locationId = loc.location_id;
                const sublocationId = loc.sublocation_id;
                const locationName = loc.location || 'Unknown';
                const sublocationName = loc.sublocation || 'Main';
                const displayText = `${locationName} - ${sublocationName}`;
                
                return {
                    value: `${locationId}|${sublocationId}`,
                    text: displayText
                };
            });
        },
        
        canAddInventory(product) {
            return product.saved && 
                   product.product_id && 
                   product.selectedLocation && 
                   product.inventoryQuantity > 0;
        },
        
        async addInventoryToProduct(product) {
            if (!this.canAddInventory(product)) {
                this.$bvToast.toast("Please fill all inventory fields", {
                    title: "Validation Error",
                    variant: "warning",
                    autoHideDelay: 3000
                });
                return;
            }
            
            product.addingInventory = true;
            
            const locParts = product.selectedLocation.split('|');
            const locationId = parseInt(locParts[0]);
            const sublocationId = parseInt(locParts[1]);
            
            const inventoryData = {
                product_id: product.product_id,
                product_sku: product.product_sku,
                location_id: locationId,
                sublocation_id: sublocationId,
                nitems: parseInt(product.inventoryQuantity),
                username: this.packet.userDetails.username,
                add_log: true,
                source_locsub: "-1|-1",
                target_locsub: product.selectedLocation
            };
            
            const path = "/api/inventory/add_single_product_to_inventory/";
            const that = this;
            
            const successCallback = function(response) {
                if (response.data.error) {
                    that.$bvToast.toast(response.data.error, {
                        title: "Error",
                        variant: "danger",
                        autoHideDelay: 3000
                    });
                } else {
                    if (!product.inventoryHistory) product.inventoryHistory = [];
                    product.inventoryHistory.unshift({
                        quantity: product.inventoryQuantity,
                        location_name: that.getLocationName(product.selectedLocation),
                        created_at: new Date().toISOString()
                    });
                    
                    that.$bvToast.toast(`Added ${product.inventoryQuantity} units to inventory`, {
                        title: "Success",
                        variant: "success",
                        autoHideDelay: 2000
                    });
                    
                    product.inventoryQuantity = 1;
                    
                    if (response.data.result && response.data.result.current_stock) {
                        product.current_stock = response.data.result.current_stock;
                    }
                }
                product.addingInventory = false;
            };
            
            const errorCallback = function(error) {
                console.error("Inventory add error:", error);
                that.$bvToast.toast("Failed to add inventory", {
                    title: "Error",
                    variant: "danger",
                    autoHideDelay: 3000
                });
                product.addingInventory = false;
            };
            
            Requestor.postAsyncP(path, inventoryData, this, successCallback, errorCallback);
        },
        
        getLocationName(locsubValue) {
            if (!locsubValue) return 'Unknown';
            
            const locParts = locsubValue.split('|');
            const locationId = parseInt(locParts[0]);
            const sublocationId = parseInt(locParts[1]);
            
            const location = this.packet.locsubs.find(loc => 
                loc.location_id === locationId && loc.id === sublocationId
            );
            
            return location ? `${location.location_name} - ${location.sublocation_name || 'Main'}` : 'Unknown';
        },
        
        formatDate(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            return date.toLocaleDateString();
        },
        
        // ============================================
        // IMAGE UPLOAD METHODS
        // ============================================
        
        uploadProductImage(product, type = 'parent') {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = 'image/*';
            
            input.onchange = async (e) => {
                const file = e.target.files[0];
                if (!file) return;
                
                if (!file.type.startsWith('image/')) {
                    this.$bvToast.toast('Please select an image file', {
                        title: 'Invalid File',
                        variant: 'danger',
                        autoHideDelay: 3000
                    });
                    return;
                }
                
                if (file.size > 5 * 1024 * 1024) {
                    this.$bvToast.toast('Image size should be less than 5MB', {
                        title: 'File Too Large',
                        variant: 'danger',
                        autoHideDelay: 3000
                    });
                    return;
                }
                
                product.uploading = true;
                product.upload_error = null;
                
                try {
                    const base64 = await this.fileToBase64(file);
                    
                    const uploadData = {
                        image_data: base64
                    };
                    
                    if (product.product_id) {
                        uploadData.product_id = product.product_id;
                    } else if (product.product_sku) {
                        uploadData.product_sku = product.product_sku;
                    } else {
                        throw new Error('Product must be saved before uploading image');
                    }
                    
                    const path = "/api/product/upload_product_image/";
                    const that = this;
                    
                    const successCallback = function(response) {
                        if (response.data.error) {
                            that.$bvToast.toast(response.data.error, {
                                title: "Error",
                                variant: "danger",
                                autoHideDelay: 3000
                            });
                            product.upload_error = response.data.error;
                        } else {
                            product.image_url = response.data.result.image_url;
                            that.$bvToast.toast('Image uploaded successfully', {
                                title: 'Success',
                                variant: 'success',
                                autoHideDelay: 2000
                            });
                        }
                        product.uploading = false;
                    };
                    
                    const errorCallback = function(error) {
                        console.error('Upload error:', error);
                        product.upload_error = error.message || "Upload failed";
                        that.$bvToast.toast(product.upload_error, {
                            title: 'Upload Failed',
                            variant: 'danger',
                            autoHideDelay: 3000
                        });
                        product.uploading = false;
                    };
                    
                    Requestor.postAsyncP(path, uploadData, this, successCallback, errorCallback);
                    
                } catch (error) {
                    console.error('Upload error:', error);
                    product.upload_error = error.message;
                    this.$bvToast.toast(error.message || 'Failed to upload image', {
                        title: 'Upload Failed',
                        variant: 'danger',
                        autoHideDelay: 3000
                    });
                    product.uploading = false;
                }
            };
            
            input.click();
        },
        
        deleteProductImage(product) {
            if (!product.image_url) return;
            
            this.$bvModal.msgBoxConfirm('Remove this image?', {
                title: 'Confirm Delete',
                size: 'sm',
                buttonSize: 'sm',
                okVariant: 'danger',
                okTitle: 'Yes',
                cancelTitle: 'No',
                centered: true
            }).then((value) => {
                if (value) {
                    product.uploading = true;
                    
                    const deleteData = {};
                    if (product.product_id) {
                        deleteData.product_id = product.product_id;
                    } else if (product.product_sku) {
                        deleteData.product_sku = product.product_sku;
                    } else {
                        this.$bvToast.toast('Product not saved', {
                            title: 'Error',
                            variant: 'danger',
                            autoHideDelay: 2000
                        });
                        product.uploading = false;
                        return;
                    }
                    
                    const path = "/api/product/delete_product_image/";
                    const that = this;
                    
                    const successCallback = function(response) {
                        if (response.data.error) {
                            that.$bvToast.toast(response.data.error, {
                                title: "Error",
                                variant: "danger",
                                autoHideDelay: 3000
                            });
                        } else {
                            product.image_url = null;
                            that.$bvToast.toast('Image removed successfully', {
                                title: 'Success',
                                variant: 'success',
                                autoHideDelay: 2000
                            });
                        }
                        product.uploading = false;
                    };
                    
                    const errorCallback = function(error) {
                        console.error('Delete error:', error);
                        that.$bvToast.toast(error.message || 'Failed to remove image', {
                            title: 'Error',
                            variant: 'danger',
                            autoHideDelay: 3000
                        });
                        product.uploading = false;
                    };
                    
                    Requestor.postAsyncP(path, deleteData, this, successCallback, errorCallback);
                }
            });
        },
        
        fileToBase64(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });
        },
        
        openImagePreview(imageUrl) {
            this.$bvModal.msgBoxOk(`
                <div class="text-center">
                    <img src="${imageUrl}" style="max-width: 100%; max-height: 80vh;">
                </div>
            `, {
                title: 'Product Image',
                size: 'xl',
                centered: true,
                okTitle: 'Close',
                html: true
            });
        },
        
        // ============================================
        // FUNCTIONALITY METHODS
        // ============================================
        
        getFunctionalityText(collection, functionalityId) {
            if (!collection.functionalityOptions) return functionalityId;
            const option = collection.functionalityOptions.find(opt => opt.value === functionalityId);
            return option ? option.text : functionalityId;
        },
        
        addFunctionality(parentProduct) {
            if (this.selectedFunctionality && parentProduct) {
                if (!parentProduct.functionality_ids) {
                    parentProduct.functionality_ids = [];
                }
                
                if (!parentProduct.functionality_ids.includes(this.selectedFunctionality)) {
                    parentProduct.functionality_ids.push(this.selectedFunctionality);
                    this.updateFeaturesFromFunctionalities(parentProduct);
                    
                    this.$bvToast.toast('Functionality added', {
                        title: 'Success',
                        variant: 'success',
                        autoHideDelay: 1000
                    });
                } else {
                    this.$bvToast.toast('Functionality already added', {
                        title: 'Info',
                        variant: 'info',
                        autoHideDelay: 1000
                    });
                }
                
                this.selectedFunctionality = null;
            }
        },

        removeFunctionality(parentProduct, index) {
            if (parentProduct.functionality_ids) {
                parentProduct.functionality_ids.splice(index, 1);
                this.updateFeaturesFromFunctionalities(parentProduct);
                
                this.$bvToast.toast('Functionality removed', {
                    title: 'Success',
                    variant: 'warning',
                    autoHideDelay: 1000
                });
            }
        },

        updateFeaturesFromFunctionalities(parentProduct) {
            if (parentProduct.functionality_ids && parentProduct.functionality_ids.length > 0) {
                const collection = this.findCollectionForProduct(parentProduct);
                if (collection && collection.functionalityOptions) {
                    const selectedTexts = parentProduct.functionality_ids
                        .map(id => {
                            const opt = collection.functionalityOptions.find(o => o.value === id);
                            return opt ? opt.text : null;
                        })
                        .filter(text => text !== null);
                    
                    if (selectedTexts.length > 0) {
                        parentProduct.features = selectedTexts.join(', ');
                    }
                }
            }
        },

        findCollectionForProduct(product) {
            for (const collection of this.collections) {
                if (collection.products.includes(product)) {
                    return collection;
                }
            }
            return null;
        },
        
        isSectionalSubcategory(subcategoryId) {
            if (!subcategoryId) return false;
            const subcategory = this.getSubcategoryName(subcategoryId);
            return subcategory && subcategory.toUpperCase() === 'SECTIONAL';
        },
        
        isBedSubcategory(subcategoryId) {
            if (!subcategoryId) return false;
            const subcategory = this.getSubcategoryName(subcategoryId);
            return subcategory && subcategory.toUpperCase() === 'BED';
        },
        
        // ============================================
        // SIMPLE PRODUCT METHODS
        // ============================================
        
        addSimpleProduct() {
            const product = {
                id: `simple-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                vendor_id: null,
                category_id: null,
                subcategory_id: null,
                type_id: null,
                color_id: null,
                material_id: null,
                size_id: null,
                style_id: null,
                product_sku: '',
                product_description: '',
                fob_price: null,
                selling_price: null,
                wholesale_price: null,
                special_discount_price: null,
                cubes: null,
                spiff: 0.00,
                quantity_per_box: 1,
                vendor_sku: '',
                saved: false,
                image_url: null,
                uploading: false,
                upload_error: null,
                selectedLocation: null,
                inventoryQuantity: 1,
                inventoryHistory: [],
                addingInventory: false,
                typeOptions: [],
                subcategoryOptions: [],
                colorOptions: [],
                materialOptions: [],
                sizeOptions: [],
                styleOptions: []
            };
            
            this.simpleProducts.push(product);
        },
        
        removeSimpleProduct(index) {
            if (!this.simpleProducts[index].saved || confirm('Remove this product?')) {
                this.simpleProducts.splice(index, 1);
            }
        },
        
        isSimpleProductValid(product) {
            return product.vendor_id &&
                   product.category_id &&
                   product.product_sku &&
                   product.product_description &&
                   product.fob_price > 0 &&
                   product.cubes > 0;
        },
        
        loadSimpleProductCategoryAttributes(product) {
            const categoryId = product.category_id;
            
            if (!categoryId) {
                product.typeOptions = [];
                product.subcategoryOptions = [];
                product.colorOptions = [];
                product.materialOptions = [];
                product.sizeOptions = [];
                product.styleOptions = [];
                return;
            }
            
            // Load types
            if (!this.categoryTypes[categoryId]) {
                const path = "/api/product/get_category_types/";
                const data = { category_id: categoryId };
                const that = this;
                
                const successCallback = function(response) {
                    if (!response.data.error) {
                        const options = response.data.result.map(type => ({
                            value: type.id,
                            text: type.type_name
                        }));
                        options.unshift({ value: null, text: "Select Type" });
                        that.categoryTypes[categoryId] = options;
                        product.typeOptions = options;
                    }
                };
                
                const errorCallback = function(error) {
                    console.error("Failed to load types:", error);
                };
                
                Requestor.postSyncP(path, data, this, successCallback, errorCallback);
            } else {
                product.typeOptions = this.categoryTypes[categoryId];
            }
            
            // Load subcategories
            if (!this.categorySubcategories[categoryId]) {
                const path = "/api/product/get_subcategories/";
                const data = { category_id: categoryId };
                const that = this;
                
                const successCallback = function(response) {
                    if (!response.data.error) {
                        const options = response.data.result.map(subcat => ({
                            value: subcat.id,
                            text: subcat.subcategory
                        }));
                        options.unshift({ value: null, text: "Select Subcategory" });
                        that.categorySubcategories[categoryId] = options;
                        product.subcategoryOptions = options;
                        
                        response.data.result.forEach(subcat => {
                            that.subcategoryCodes[subcat.id] = {
                                code: subcat.code || subcat.subcategory_code || subcat.subcategory.substring(0, 3).toUpperCase(),
                                name: subcat.subcategory
                            };
                        });
                    }
                };
                
                const errorCallback = function(error) {
                    console.error("Failed to load subcategories:", error);
                };
                
                Requestor.postSyncP(path, data, this, successCallback, errorCallback);
            } else {
                product.subcategoryOptions = this.categorySubcategories[categoryId];
            }
            
            // Load colors
            if (!this.categoryColors[categoryId]) {
                const path = "/api/product/get_category_colors/";
                const data = { category_id: categoryId };
                const that = this;
                
                const successCallback = function(response) {
                    if (!response.data.error) {
                        const options = response.data.result.map(color => ({
                            value: color.id,
                            text: color.color_name
                        }));
                        options.unshift({ value: null, text: "Select Color" });
                        that.categoryColors[categoryId] = options;
                        product.colorOptions = options;
                    }
                };
                
                const errorCallback = function(error) {
                    console.error("Failed to load colors:", error);
                };
                
                Requestor.postSyncP(path, data, this, successCallback, errorCallback);
            } else {
                product.colorOptions = this.categoryColors[categoryId];
            }
            
            // Load materials
            if (!this.categoryMaterials[categoryId]) {
                const path = "/api/product/get_category_materials/";
                const data = { category_id: categoryId };
                const that = this;
                
                const successCallback = function(response) {
                    if (!response.data.error) {
                        const options = response.data.result.map(material => ({
                            value: material.id,
                            text: material.material_name
                        }));
                        options.unshift({ value: null, text: "Select Material" });
                        that.categoryMaterials[categoryId] = options;
                        product.materialOptions = options;
                    }
                };
                
                const errorCallback = function(error) {
                    console.error("Failed to load materials:", error);
                };
                
                Requestor.postSyncP(path, data, this, successCallback, errorCallback);
            } else {
                product.materialOptions = this.categoryMaterials[categoryId];
            }
            
            // Load sizes
            if (!this.categorySizes[categoryId]) {
                const path = "/api/product/get_category_sizes/";
                const data = { category_id: categoryId };
                const that = this;
                
                const successCallback = function(response) {
                    if (!response.data.error) {
                        const options = response.data.result.map(size => ({
                            value: size.id,
                            text: size.size_name
                        }));
                        options.unshift({ value: null, text: "Select Size" });
                        that.categorySizes[categoryId] = options;
                        product.sizeOptions = options;
                    }
                };
                
                const errorCallback = function(error) {
                    console.error("Failed to load sizes:", error);
                };
                
                Requestor.postSyncP(path, data, this, successCallback, errorCallback);
            } else {
                product.sizeOptions = this.categorySizes[categoryId];
            }
            
            // Load styles
            if (!this.categoryStyles[categoryId]) {
                const path = "/api/product/get_category_styles/";
                const data = { category_id: categoryId };
                const that = this;
                
                const successCallback = function(response) {
                    if (!response.data.error) {
                        const options = response.data.result.map(style => ({
                            value: style.id,
                            text: style.style_name
                        }));
                        options.unshift({ value: null, text: "Select Style" });
                        that.categoryStyles[categoryId] = options;
                        product.styleOptions = options;
                    }
                };
                
                const errorCallback = function(error) {
                    console.error("Failed to load styles:", error);
                };
                
                Requestor.postSyncP(path, data, this, successCallback, errorCallback);
            } else {
                product.styleOptions = this.categoryStyles[categoryId];
            }
        },
        
        saveSimpleProduct(product) {
            this.items_being_submitted = true;
            
            const properties = {
                quantity_per_box: parseInt(product.quantity_per_box) || 1,
                low_inventory: false,
                is_parent: false,
                has_child_products: false,
                type: product.type_id,
                color: product.color_id,
                size: product.size_id,
                material: product.material_id,
                style: product.style_id,
                vendor_sku: product.vendor_sku || ""
            };
            
            const apiItem = {
                product_sku: product.product_sku.toUpperCase().trim(),
                product_name: product.product_description.toUpperCase().trim(),
                product_display_name: product.product_description,
                category_id: product.category_id,
                vendor_id: product.vendor_id,
                subcategory_id: product.subcategory_id,
                type_id: product.type_id,
                color_id: product.color_id,
                size_id: product.size_id,
                material_id: product.material_id,
                style_id: product.style_id,
                fob_price: parseFloat(product.fob_price) || 0,
                selling_price: parseFloat(product.selling_price) || 0,
                wholesale_price: parseFloat(product.wholesale_price) || 0,
                special_discount_price: parseFloat(product.special_discount_price) || 0,
                cubes: parseFloat(product.cubes) || 0,
                spiff: parseFloat(product.spiff) || 0,
                product_type: 1,
                status: "active",
                properties: properties,
                image_url: product.image_url || null
            };
            
            const path = "/api/product/add_batch_products/";
            const data = { items: [apiItem] };
            const that = this;
            
            const successCallback = function(response) {
                if (response.data.error) {
                    that.$bvToast.toast(response.data.error, {
                        title: "Error",
                        variant: "danger",
                        autoHideDelay: 3000
                    });
                } else {
                    product.saved = true;
                    product.product_id = response.data.result[0].id;
                    that.savedSimpleProductsCount++;
                    
                    that.$bvToast.toast("Simple product saved successfully. You can now add inventory.", {
                        title: "Success",
                        variant: "success",
                        autoHideDelay: 3000
                    });
                }
                that.items_being_submitted = false;
            };
            
            const errorCallback = function(error) {
                console.error("Save error:", error);
                that.$bvToast.toast("Failed to save simple product", {
                    title: "Error",
                    variant: "danger",
                    autoHideDelay: 3000
                });
                that.items_being_submitted = false;
            };
            
            Requestor.postSyncP(path, data, this, successCallback, errorCallback);
        },
        
        // ============================================
        // UTILITY METHODS
        // ============================================
        
        getOptionText(options, value) {
            if (!options || !value) return value;
            const option = options.find(opt => opt.value === value);
            return option ? option.text : value;
        },
        
        getFilteredProducts(collection, isParent = true) {
            return collection.products.filter(p => !p.parent_product_id === isParent);
        },
        
        getCollectionOptions() {
            return this.collections
                .filter(c => c.saved)
                .map(c => ({ value: c.id, text: c.name }));
        },
        
        getFilteredCollections() {
            if (!this.groupFilter.collectionId) {
                return this.collections.filter(c => c.saved);
            }
            return this.collections.filter(c => c.id === this.groupFilter.collectionId && c.saved);
        },
        
        getAvailableProductsCount(collection) {
            return collection.products.filter(p => !p.parent_product_id && p.saved).length;
        },
        
        getAvailableProducts(collection) {
            return collection.products.filter(p => !p.parent_product_id && p.saved);
        },
        
        toggleCollectionGroup(collectionId) {
            this.$set(this.expandedCollections, collectionId, !this.expandedCollections[collectionId]);
        },
        
        getCombinationCount() {
            if (this.selectedForGrouping.length < 2) return 0;
            let size = this.selectedGroupSize;
            if (size === 'all') size = this.selectedForGrouping.length;
            return this.combinationCount(this.selectedForGrouping.length, size);
        },
        
        combinationCount(n, k) {
            if (k > n) return 0;
            if (k === 0 || k === n) return 1;
            if (k > n - k) k = n - k;
            let result = 1;
            for (let i = 1; i <= k; i++) {
                result *= (n - (k - i)) / i;
            }
            return Math.round(result);
        },
        
        // ============================================
        // CATEGORY VALIDATION METHODS
        // ============================================
        
        isBedroomCategory(categoryId) {
            const category = this.categories.find(c => c.value === categoryId);
            return category && category.text.toUpperCase().includes('BEDROOM');
        },
        
        isLivingRoomCategory(categoryId) {
            const category = this.categories.find(c => c.value === categoryId);
            return category && category.text.toUpperCase().includes('LIVING ROOM');
        },
        
        isFurnitureCategory(categoryId) {
            const category = this.categories.find(c => c.value === categoryId);
            return category && (
                category.text.toUpperCase().includes('SOFA') ||
                category.text.toUpperCase().includes('SECTIONAL') ||
                category.text.toUpperCase().includes('FURNITURE') ||
                category.text.toUpperCase().includes('COUCH') ||
                category.text.toUpperCase().includes('LIVING ROOM')
            );
        },
        
        getCategoryName(categoryId) {
            const category = this.categories.find(c => c.value === categoryId);
            return category ? category.text : 'Unknown';
        },
        
        getSubcategoryName(subcategoryId) {
            if (!subcategoryId) return 'Unknown';
            const code = this.subcategoryCodes[subcategoryId];
            return code ? code.name : 'Unknown';
        },
        
        getCurrentCategory() {
            if (this.selectedForGrouping.length === 0) return null;
            return this.selectedForGrouping[0].category_id;
        },
        
        isMixedCategory() {
            if (this.selectedForGrouping.length === 0) return false;
            const categoryId = this.selectedForGrouping[0].category_id;
            return this.selectedForGrouping.some(p => p.category_id !== categoryId);
        },
        
        getGroupSizeOptions() {
            const categoryId = this.getCurrentCategory();
            
            if (this.isBedroomCategory(categoryId)) {
                return [
                    {text: '2 Products', value: 2},
                    {text: '3 Products', value: 3},
                    {text: '4 Products', value: 4},
                    {text: '5 Products', value: 5}
                ];
            } else if (this.isLivingRoomCategory(categoryId)) {
                return [
                    {text: '2 Products (SOFA+LOVESEAT)', value: 2},
                    {text: '3 Products (SOFA+LOVESEAT+CHAIR)', value: 3},
                    {text: '4 Products (SOFA+LOVESEAT+CHAIR+OTTOMAN)', value: 4}
                ];
            } else {
                return [
                    {text: '2 Products', value: 2},
                    {text: '3 Products', value: 3},
                    {text: '4 Products', value: 4},
                    {text: '5 Products', value: 5},
                    {text: 'All', value: 'all'}
                ];
            }
        },
        
        canGenerateGroups() {
            if (this.selectedForGrouping.length < 2) return false;
            if (this.isMixedCategory()) return false;
            
            const categoryId = this.getCurrentCategory();
            
            if (this.isLivingRoomCategory(categoryId)) {
                const subcategories = this.selectedForGrouping.map(p => 
                    this.getSubcategoryName(p.subcategory_id).toUpperCase()
                );
                
                const hasSofa = subcategories.some(s => s.includes('SOFA'));
                const hasLoveseat = subcategories.some(s => s.includes('LOVESEAT') || s.includes('LOVE SEAT'));
                const hasChair = subcategories.some(s => s.includes('CHAIR'));
                const hasOttoman = subcategories.some(s => s.includes('OTTOMAN'));
                
                if (this.selectedGroupSize === 2) {
                    return hasSofa && hasLoveseat;
                } else if (this.selectedGroupSize === 3) {
                    return hasSofa && hasLoveseat && hasChair;
                } else if (this.selectedGroupSize === 4) {
                    return hasSofa && hasLoveseat && hasChair && hasOttoman;
                }
                return false;
            }
            
            return true;
        },
        
        // ============================================
        // COLLECTION METHODS
        // ============================================
        
        addNewCollection() {
            const collection = {
                id: `col-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                name: '',
                vendor_id: null,
                category_id: null,
                type_id: null,
                color_id: null,
                material_id: null,
                saved: false,
                showProducts: true,
                sku_counter: 100,
                base_sku_number: 100,
                typeOptions: [],
                subcategoryOptions: [],
                subsubcategoryOptions: [],
                colorOptions: [],
                sizeOptions: [],
                materialOptions: [],
                functionalityOptions: [],
                bedTypeOptions: [],
                styleOptions: [],
                sectionalShapeOptions: [],
                products: []
            };
            
            this.initializeCollectionCounter(collection);
            this.collections.unshift(collection);
        },
        
        duplicateCollection(index) {
            const original = this.collections[index];
            const duplicate = JSON.parse(JSON.stringify(original));
            duplicate.id = `col-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            duplicate.name = `${original.name} (Copy)`;
            duplicate.saved = false;
            duplicate.showProducts = true;
            duplicate.products = [];
            
            const randomNumber = this.generateRandomThreeDigit();
            duplicate.sku_counter = randomNumber;
            duplicate.base_sku_number = randomNumber;
            
            this.collections.splice(index + 1, 0, duplicate);
            
            this.$bvToast.toast(`Collection "${original.name}" duplicated`, {
                title: "Success",
                variant: "success",
                autoHideDelay: 2000
            });
        },
        
        resetCollection(collection) {
            collection.name = '';
            collection.vendor_id = null;
            collection.category_id = null;
            collection.type_id = null;
            collection.color_id = null;
            collection.material_id = null;
        },
        
        isCollectionValid(collection) {
            return collection.name && 
                   collection.vendor_id && 
                   collection.category_id &&
                   collection.type_id;
        },
        
        updateCollectionName(collection) {
            if (collection.saved) {
                collection.products.forEach(product => {
                    if (!product.parent_product_id) {
                        this.generateProductSku(collection, product);
                        if (product.child_products) {
                            product.child_products.forEach(child => {
                                this.updateChildProductSku(collection, product, child);
                            });
                        }
                    }
                });
            }
        },
        
        async loadCollectionCategoryAttributes(collection) {
            const categoryId = collection.category_id;
            
            if (!categoryId) {
                this.resetCollectionCategoryAttributes(collection);
                return;
            }
            
            collection.type_id = null;
            collection.color_id = null;
            collection.material_id = null;
            
            // Load types
            if (!this.categoryTypes[categoryId]) {
                const path = "/api/product/get_category_types/";
                const data = { category_id: categoryId };
                const that = this;
                
                const successCallback = function(response) {
                    if (!response.data.error) {
                        const options = response.data.result.map(type => ({
                            value: type.id,
                            text: type.type_name
                        }));
                        options.unshift({ value: null, text: "Select Type" });
                        that.categoryTypes[categoryId] = options;
                        collection.typeOptions = options;
                        
                        response.data.result.forEach(type => {
                            that.typeCodes[type.id] = {
                                code: type.code || type.type_code || type.type_name.substring(0, 3).toUpperCase(),
                                name: type.type_name
                            };
                        });
                    } else {
                        collection.typeOptions = [];
                    }
                };
                
                const errorCallback = function(error) {
                    console.error("Failed to load types:", error);
                    collection.typeOptions = [];
                };
                
                Requestor.postSyncP(path, data, this, successCallback, errorCallback);
            } else {
                collection.typeOptions = this.categoryTypes[categoryId];
            }
            
            // Load subcategories
            if (!this.categorySubcategories[categoryId]) {
                const path = "/api/product/get_subcategories/";
                const data = { category_id: categoryId };
                const that = this;
                
                const successCallback = function(response) {
                    if (!response.data.error) {
                        const options = response.data.result.map(subcat => ({
                            value: subcat.id,
                            text: subcat.subcategory
                        }));
                        options.unshift({ value: null, text: "Select Subcategory" });
                        that.categorySubcategories[categoryId] = options;
                        collection.subcategoryOptions = options;
                        
                        response.data.result.forEach(subcat => {
                            that.subcategoryCodes[subcat.id] = {
                                code: subcat.code || subcat.subcategory_code || subcat.subcategory.substring(0, 3).toUpperCase(),
                                name: subcat.subcategory
                            };
                        });
                    } else {
                        collection.subcategoryOptions = [];
                    }
                };
                
                const errorCallback = function(error) {
                    console.error("Failed to load subcategories:", error);
                    collection.subcategoryOptions = [];
                };
                
                Requestor.postSyncP(path, data, this, successCallback, errorCallback);
            } else {
                collection.subcategoryOptions = this.categorySubcategories[categoryId];
            }
            
            // Load colors
            if (!this.categoryColors[categoryId]) {
                const path = "/api/product/get_category_colors/";
                const data = { category_id: categoryId };
                const that = this;
                
                const successCallback = function(response) {
                    if (!response.data.error) {
                        const options = response.data.result.map(color => ({
                            value: color.id,
                            text: color.color_name
                        }));
                        options.unshift({ value: null, text: "Select Color (Optional)" });
                        that.categoryColors[categoryId] = options;
                        collection.colorOptions = options;
                        
                        response.data.result.forEach(color => {
                            that.colorCodes[color.id] = {
                                code: color.code || color.color_code || "00",
                                name: color.color_name
                            };
                        });
                    } else {
                        collection.colorOptions = [];
                    }
                };
                
                const errorCallback = function(error) {
                    console.error("Failed to load colors:", error);
                    collection.colorOptions = [];
                };
                
                Requestor.postSyncP(path, data, this, successCallback, errorCallback);
            } else {
                collection.colorOptions = this.categoryColors[categoryId];
            }
            
            // Load sizes
            if (!this.categorySizes[categoryId]) {
                const path = "/api/product/get_category_sizes/";
                const data = { category_id: categoryId };
                const that = this;
                
                const successCallback = function(response) {
                    if (!response.data.error) {
                        const options = response.data.result.map(size => ({
                            value: size.id,
                            text: size.size_name
                        }));
                        options.unshift({ value: null, text: "Select Size" });
                        that.categorySizes[categoryId] = options;
                        collection.sizeOptions = options;
                        
                        response.data.result.forEach(size => {
                            that.sizeCodes[size.id] = {
                                code: size.code || size.size_code || size.size_name.substring(0, 2).toUpperCase(),
                                name: size.size_name
                            };
                        });
                    } else {
                        collection.sizeOptions = [];
                    }
                };
                
                const errorCallback = function(error) {
                    console.error("Failed to load sizes:", error);
                    collection.sizeOptions = [];
                };
                
                Requestor.postSyncP(path, data, this, successCallback, errorCallback);
            } else {
                collection.sizeOptions = this.categorySizes[categoryId];
            }
            
            // Load materials
            if (!this.categoryMaterials[categoryId]) {
                const path = "/api/product/get_category_materials/";
                const data = { category_id: categoryId };
                const that = this;
                
                const successCallback = function(response) {
                    if (!response.data.error) {
                        const options = response.data.result.map(material => ({
                            value: material.id,
                            text: material.material_name
                        }));
                        options.unshift({ value: null, text: "Select Material (Optional)" });
                        that.categoryMaterials[categoryId] = options;
                        collection.materialOptions = options;
                        
                        response.data.result.forEach(material => {
                            that.materialCodes[material.id] = {
                                code: material.code || material.material_code || "00",
                                name: material.material_name
                            };
                        });
                    } else {
                        collection.materialOptions = [];
                    }
                };
                
                const errorCallback = function(error) {
                    console.error("Failed to load materials:", error);
                    collection.materialOptions = [];
                };
                
                Requestor.postSyncP(path, data, this, successCallback, errorCallback);
            } else {
                collection.materialOptions = this.categoryMaterials[categoryId];
            }
            
            // Load functionalities
            if (!this.categoryFunctionalities[categoryId]) {
                const path = "/api/product/get_category_functionalities/";
                const data = { category_id: categoryId };
                const that = this;
                
                const successCallback = function(response) {
                    if (!response.data.error) {
                        const options = response.data.result.map(func => ({
                            value: func.id,
                            text: func.functionality_name
                        }));
                        options.unshift({ value: null, text: "Select Functionality (Optional)" });
                        that.categoryFunctionalities[categoryId] = options;
                        collection.functionalityOptions = options;
                        
                        response.data.result.forEach(func => {
                            that.functionalityCodes[func.id] = {
                                code: func.code || func.functionality_code || func.functionality_name.substring(0, 3).toUpperCase(),
                                name: func.functionality_name
                            };
                        });
                    } else {
                        collection.functionalityOptions = [];
                    }
                };
                
                const errorCallback = function(error) {
                    console.error("Failed to load functionalities:", error);
                    collection.functionalityOptions = [];
                };
                
                Requestor.postSyncP(path, data, this, successCallback, errorCallback);
            } else {
                collection.functionalityOptions = this.categoryFunctionalities[categoryId];
            }
            
            // Load bed types
            if (!this.categoryBedTypes[categoryId]) {
                const path = "/api/product/get_category_bed_types/";
                const data = { category_id: categoryId };
                const that = this;
                
                const successCallback = function(response) {
                    if (!response.data.error) {
                        const options = response.data.result.map(bedType => ({
                            value: bedType.id,
                            text: bedType.style_name
                        }));
                        options.unshift({ value: null, text: "Select Bed Type" });
                        that.categoryBedTypes[categoryId] = options;
                        collection.bedTypeOptions = options;
                        
                        response.data.result.forEach(bedType => {
                            that.bedTypeCodes[bedType.id] = {
                                code: bedType.style_code || bedType.style_name.substring(0, 3).toUpperCase(),
                                name: bedType.style_name
                            };
                        });
                    } else {
                        collection.bedTypeOptions = [];
                    }
                };
                
                const errorCallback = function(error) {
                    console.error("Failed to load bed types:", error);
                    collection.bedTypeOptions = [];
                };
                
                Requestor.postSyncP(path, data, this, successCallback, errorCallback);
            } else {
                collection.bedTypeOptions = this.categoryBedTypes[categoryId];
            }
            
            // Load styles
            if (!this.categoryStyles[categoryId]) {
                const path = "/api/product/get_category_styles/";
                const data = { category_id: categoryId };
                const that = this;
                
                const successCallback = function(response) {
                    if (!response.data.error) {
                        const options = response.data.result.map(style => ({
                            value: style.id,
                            text: style.style_name
                        }));
                        options.unshift({ value: null, text: "Select Style" });
                        that.categoryStyles[categoryId] = options;
                        collection.styleOptions = options;
                        
                        response.data.result.forEach(style => {
                            that.styleCodes[style.id] = {
                                code: style.style_code || style.style_name.substring(0, 3).toUpperCase(),
                                name: style.style_name
                            };
                        });
                    } else {
                        collection.styleOptions = [];
                    }
                };
                
                const errorCallback = function(error) {
                    console.error("Failed to load styles:", error);
                    collection.styleOptions = [];
                };
                
                Requestor.postSyncP(path, data, this, successCallback, errorCallback);
            } else {
                collection.styleOptions = this.categoryStyles[categoryId];
            }
            
            // Load sectional shapes
            if (!this.categorySectionalShapes[categoryId]) {
                const path = "/api/product/get_category_sectional_shapes/";
                const data = { category_id: categoryId };
                const that = this;
                
                const successCallback = function(response) {
                    if (!response.data.error) {
                        const options = response.data.result.map(shape => ({
                            value: shape.id,
                            text: shape.shape_name
                        }));
                        options.unshift({ value: null, text: "Select Shape (Optional)" });
                        that.categorySectionalShapes[categoryId] = options;
                        collection.sectionalShapeOptions = options;
                        
                        response.data.result.forEach(shape => {
                            that.sectionalShapeCodes[shape.id] = {
                                code: shape.shape_code || shape.shape_name.substring(0, 3).toUpperCase(),
                                name: shape.shape_name
                            };
                        });
                    } else {
                        collection.sectionalShapeOptions = [];
                    }
                };
                
                const errorCallback = function(error) {
                    console.error("Failed to load sectional shapes:", error);
                    collection.sectionalShapeOptions = [];
                };
                
                Requestor.postSyncP(path, data, this, successCallback, errorCallback);
            } else {
                collection.sectionalShapeOptions = this.categorySectionalShapes[categoryId];
            }
            
            this.updateCollectionAutoGeneratedFields(collection);
        },
        
        resetCollectionCategoryAttributes(collection) {
            collection.typeOptions = [];
            collection.subcategoryOptions = [];
            collection.subsubcategoryOptions = [];
            collection.colorOptions = [];
            collection.sizeOptions = [];
            collection.materialOptions = [];
            collection.functionalityOptions = [];
            collection.bedTypeOptions = [];
            collection.styleOptions = [];
            collection.sectionalShapeOptions = [];
            
            collection.type_id = null;
            collection.color_id = null;
            collection.material_id = null;
        },
        
        updateCollectionAutoGeneratedFields(collection) {
            if (collection.saved) {
                collection.products.filter(p => !p.parent_product_id).forEach(product => {
                    this.generateProductSku(collection, product);
                });
            }
        },
        
        toggleCollectionProducts(collection) {
            collection.showProducts = !collection.showProducts;
        },
        
        saveCollection(collection) {
            collection.saved = true;
            
            if (!collection.base_sku_number) {
                collection.base_sku_number = this.generateRandomThreeDigit();
                collection.sku_counter = collection.base_sku_number;
            }
            
            this.savedCollectionsCount++;
            this.saveCollectionCounters();
            
            this.$bvToast.toast(`Collection "${collection.name}" saved successfully`, {
                title: "Success",
                variant: "success",
                autoHideDelay: 2000
            });
        },
        
        removeCollection(index) {
            const collection = this.collections[index];
            if (collection.products.length > 0) {
                if (!confirm(`Collection "${collection.name}" has ${collection.products.length} products. Remove anyway?`)) {
                    return;
                }
            }
            this.collections.splice(index, 1);
            this.saveCollectionCounters();
        },
        
        getParentProductsCount(collection) {
            return collection.products.filter(p => !p.parent_product_id).length;
        },
        
        // ============================================
        // PARENT PRODUCT METHODS
        // ============================================
        
        addParentProductToCollection(collection) {
            const parentProduct = {
                id: `prod-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                subcategory_id: null,
                size_id: null,
                functionality_ids: [],
                bed_type_id: null,
                style_id: null,
                sectional_shape_id: null,
                has_child_products: false,
                product_sku: '',
                product_description: '',
                detail_description: '',
                product_display_name: collection.name,
                is_parent: false,
                parent_product_id: null,
                saved: false,
                showChildProducts: false,
                child_products: [],
                includeInGroup: false,
                features: '',
                height: null,
                length: null,
                depth: null,
                measurement_unit: 'in',
                weight: null,
                assembly_time: null,
                vendor_sku: '',
                quantity_per_box: 1,
                fob_price: null,
                selling_price: null,
                wholesale_price: null,
                special_discount_price: null,
                cubes: null,
                spiff: 0.00,
                image_url: null,
                uploading: false,
                upload_error: null
            };
            collection.products.push(parentProduct);
        },
        
        generateDetailDescription(collection, product) {
            if (!collection.category_id) return '';
            
            const parts = [];
            
            if (collection.name) {
                parts.push(collection.name.toUpperCase());
            } else {
                parts.push('NOCOLLECTION');
            }
            
            if (collection.color_id && this.colorCodes[collection.color_id]) {
                const color = this.colorCodes[collection.color_id].name;
                if (color) parts.push(color.toUpperCase());
            } else {
                parts.push('NOCOLOR');
            }
            
            if (collection.material_id && this.materialCodes[collection.material_id]) {
                const material = this.materialCodes[collection.material_id].name;
                if (material) parts.push(material.toUpperCase());
            } else {
                parts.push('NOMATERIAL');
            }
            
            if (product.size_id && this.sizeCodes[product.size_id]) {
                const size = this.sizeCodes[product.size_id].name;
                if (size) parts.push(size.toUpperCase());
            } else {
                parts.push('NOSIZE');
            }
            
            if (product.subcategory_id && this.subcategoryCodes[product.subcategory_id]) {
                const subcategory = this.subcategoryCodes[product.subcategory_id].name;
                if (subcategory) parts.push(subcategory.toUpperCase());
            } else {
                parts.push('NOSUBCAT');
            }
            
            if (product.style_id && this.styleCodes[product.style_id]) {
                const style = this.styleCodes[product.style_id].name;
                if (style) parts.push(style.toUpperCase());
            } else {
                parts.push('NOSTYLE');
            }
            
            if (this.isBedroomCategory(collection.category_id)) {
                if (product.bed_type_id && this.bedTypeCodes[product.bed_type_id]) {
                    const bedType = this.bedTypeCodes[product.bed_type_id].name;
                    if (bedType) parts.push(bedType.toUpperCase());
                } else {
                    parts.push('NOBEDTYPE');
                }
            }
            
            if (product.features) {
                const cleanFeatures = product.features
                    .toUpperCase()
                    .replace(/[^\w\s-]/g, '')
                    .replace(/\s+/g, '-')
                    .replace(/-+/g, '-');
                
                if (cleanFeatures) {
                    parts.push(cleanFeatures);
                } else {
                    parts.push('NOFEATURES');
                }
            } else {
                parts.push('NOFEATURES');
            }
            
            const description = parts.join('-');
            product.detail_description = description;
            return description;
        },
        
        isParentProductValid(product) {
            return product.product_sku && 
                   product.product_description &&
                   product.subcategory_id &&
                   product.quantity_per_box > 0 &&
                   product.fob_price > 0 &&
                   product.cubes > 0;
        },
        
        initializeCollectionCounter(collection) {
            if (this.collectionCounters && this.collectionCounters[collection.id]) {
                const saved = this.collectionCounters[collection.id];
                collection.sku_counter = saved.sku_counter || this.generateRandomThreeDigit();
                collection.base_sku_number = saved.base_sku_number || collection.sku_counter;
            } else {
                const randomNumber = this.generateRandomThreeDigit();
                collection.sku_counter = randomNumber;
                collection.base_sku_number = randomNumber;
            }
        },
        
        generateRandomThreeDigit() {
            return Math.floor(Math.random() * 900) + 100;
        },  
        
        generateProductSku(collection, product) {
            if (!collection.category_id || !collection.type_id) {
                this.$bvToast.toast("Collection must have Category and Type first", {
                    title: "Warning",
                    variant: "warning",
                    autoHideDelay: 2000
                });
                return;
            }
            
            if (!product.subcategory_id) {
                this.$bvToast.toast("Select Subcategory for this product", {
                    title: "Warning",
                    variant: "warning",
                    autoHideDelay: 2000
                });
                return;
            }
            
            const category = this.categories.find(c => c.value === collection.category_id);
            const categoryCode = category ? category.text.substring(0, 1).toUpperCase() : "X";
            
            const typeCode = this.typeCodes[collection.type_id].code || "A";
            
            if (!collection.base_sku_number) {
                if (this.collectionCounters && this.collectionCounters[collection.id]) {
                    const saved = this.collectionCounters[collection.id];
                    collection.base_sku_number = saved.base_sku_number;
                    collection.sku_counter = saved.sku_counter;
                } else {
                    const randomNumber = this.generateRandomThreeDigit();
                    collection.base_sku_number = randomNumber;
                    collection.sku_counter = randomNumber;
                }
            }
            
            let autoNumber = collection.base_sku_number;
            collection.sku_counter++;
            
            const colorCode = collection.color_id ? (this.colorCodes[collection.color_id].code || "00") : "00";
            const materialCode = collection.material_id ? (this.materialCodes[collection.material_id].code || "00") : "00";
            const subcategoryCode = product.subcategory_id ? 
                (this.subcategoryCodes[product.subcategory_id].code || "X").substring(0, 1).toUpperCase() : "X";
            
            let sku = `${categoryCode}${typeCode}-${autoNumber}-${colorCode}-${materialCode}${subcategoryCode}`;
            
            product.product_sku = sku;
            this.generateProductDescription(collection, product);
            this.generateDetailDescription(collection, product);
            
            this.saveCollectionCounters();
        },
        
        saveCollectionCounters() {
            const collectionCounters = {};
            this.collections.forEach(collection => {
                collectionCounters[collection.id] = {
                    sku_counter: collection.sku_counter,
                    base_sku_number: collection.base_sku_number
                };
            });
            localStorage.setItem('collectionSkuCounters', JSON.stringify(collectionCounters));
        },
        
        generateProductDescription(collection, product) {
            if (!collection.category_id) return;
            
            const parts = [];
            
            if (collection.name) {
                parts.push(collection.name.toUpperCase());
            }
            
            if (collection.color_id && this.colorCodes[collection.color_id]) {
                const color = this.colorCodes[collection.color_id].name;
                if (color) parts.push(color.toUpperCase());
            }
            
            if (collection.material_id && this.materialCodes[collection.material_id]) {
                const material = this.materialCodes[collection.material_id].name;
                if (material) parts.push(material.toUpperCase());
            }
            
            if (product.size_id && this.sizeCodes[product.size_id]) {
                const size = this.sizeCodes[product.size_id].name;
                if (size) parts.push(size.toUpperCase());
            }
            
            if (product.subcategory_id && this.subcategoryCodes[product.subcategory_id]) {
                const subcategory = this.subcategoryCodes[product.subcategory_id].name;
                if (subcategory) parts.push(subcategory.toUpperCase());
            }
            
            product.product_description = parts.join('-');
        },
        
        updateProductAutoGeneratedFields(collection, product) {
            if (product.subcategory_id) {
                this.loadSubsubcategories(collection, product.subcategory_id);
            }
            
            if (collection.saved) {
                this.generateProductSku(collection, product);
                this.generateDetailDescription(collection, product);
            }
        },
        
        toggleChildProductsConfiguration(parentProduct) {
            parentProduct.showChildProducts = parentProduct.has_child_products;
            if (!parentProduct.has_child_products) {
                parentProduct.child_products = [];
            }
        },
        
        saveParentProduct(collection, parentProduct) {
            this.items_being_submitted = true;
            
            const measurements = {};
            if (parentProduct.height) measurements.height = parseFloat(parentProduct.height);
            if (parentProduct.length) measurements.length = parseFloat(parentProduct.length);
            if (parentProduct.depth) measurements.depth = parseFloat(parentProduct.depth);
            if (parentProduct.measurement_unit) measurements.unit = parentProduct.measurement_unit;
            
            const properties = {
                quantity_per_box: parseInt(parentProduct.quantity_per_box) || 1,
                low_inventory: false,
                is_parent: true,
                has_child_products: parentProduct.has_child_products,
                bed_type: parentProduct.bed_type_id,
                style: parentProduct.style_id,
                sectional_shape: parentProduct.sectional_shape_id,
                features: parentProduct.features || "",
                measurements: measurements,
                weight: parentProduct.weight ? parseFloat(parentProduct.weight) : null,
                assembly_time: parentProduct.assembly_time ? parseInt(parentProduct.assembly_time) : null,
                vendor_sku: parentProduct.vendor_sku || "",
                functionality_ids: parentProduct.functionality_ids || [],
                detail_description: parentProduct.detail_description || ""
            };
            
            const apiItem = {
                product_sku: parentProduct.product_sku,
                product_name: parentProduct.product_description,
                product_display_name: collection.name,
                category_id: collection.category_id,
                vendor_id: collection.vendor_id,
                type_id: collection.type_id,
                subcategory_id: parentProduct.subcategory_id,
                color_id: collection.color_id,
                size_id: parentProduct.size_id,
                material_id: collection.material_id,
                functionality_ids: parentProduct.functionality_ids,
                bed_type_id: parentProduct.bed_type_id,
                style_id: parentProduct.style_id,
                sectional_shape_id: parentProduct.sectional_shape_id,
                fob_price: parentProduct.fob_price ? parseFloat(parentProduct.fob_price) : 0.0,
                selling_price: parentProduct.selling_price ? parseFloat(parentProduct.selling_price) : 0.0,
                wholesale_price: parentProduct.wholesale_price ? parseFloat(parentProduct.wholesale_price) : 0.0,
                special_discount_price: parentProduct.special_discount_price ? parseFloat(parentProduct.special_discount_price) : 0.0,
                cubes: parentProduct.cubes ? parseFloat(parentProduct.cubes) : 0.0,
                spiff: parentProduct.spiff ? parseFloat(parentProduct.spiff) : 0.0,
                features: parentProduct.features || "",
                weight: parentProduct.weight ? parseFloat(parentProduct.weight) : null,
                assembly_time: parentProduct.assembly_time ? parseInt(parentProduct.assembly_time) : null,
                vendor_sku: parentProduct.vendor_sku || "",
                product_type: 1,
                status: "active",
                properties: properties,
                detail_description: parentProduct.detail_description || "",
                image_url: parentProduct.image_url || null
            };
            
            const path = "/api/product/add_batch_products/";
            const data = { items: [apiItem] };
            const that = this;
            
            const successCallback = function(response) {
                if (response.data.error) {
                    that.$bvToast.toast(response.data.error, {
                        title: "Error",
                        variant: "danger",
                        autoHideDelay: 3000
                    });
                } else {
                    parentProduct.saved = true;
                    parentProduct.product_id = response.data.result[0].id;
                    parentProduct.is_parent = true;
                    that.savedParentProductsCount++;
                    
                    that.$bvToast.toast("Parent product saved successfully", {
                        title: "Success",
                        variant: "success",
                        autoHideDelay: 2000
                    });
                }
                that.items_being_submitted = false;
            };
            
            const errorCallback = function(error) {
                console.error("Save error:", error);
                that.$bvToast.toast("Failed to save parent product", {
                    title: "Error",
                    variant: "danger",
                    autoHideDelay: 3000
                });
                that.items_being_submitted = false;
            };
            
            Requestor.postSyncP(path, data, this, successCallback, errorCallback);
        },
        
        removeParentProduct(collection, parentIndex) {
            const parentProduct = collection.products.filter(p => !p.parent_product_id)[parentIndex];
            if (parentProduct.child_products.length > 0) {
                if (!confirm(`This parent product has ${parentProduct.child_products.length} child products. Remove them all?`)) {
                    return;
                }
            }
            
            const actualIndex = collection.products.indexOf(parentProduct);
            if (actualIndex !== -1) {
                collection.products.splice(actualIndex, 1);
            }
        },
        
        // ============================================
        // CHILD PRODUCT METHODS
        // ============================================
        
        addChildProductToParent(collection, parentProduct) {
            const childProduct = {
                id: `child-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                parent_product_id: parentProduct.product_id,
                subsubcategory_id: null,
                size_id: parentProduct.size_id,
                quantity_per_box: 1,
                fob_price: null,
                selling_price: null,
                wholesale_price: null,
                special_discount_price: null,
                cubes: null,
                spiff: 0.00,
                low_inventory_notify: false,
                product_sku: '',
                product_description: '',
                saved: false,
                features: '',
                height: null,
                length: null,
                depth: null,
                measurement_unit: 'in',
                weight: null,
                assembly_time: null,
                vendor_sku: '',
                image_url: null,
                uploading: false,
                upload_error: null
            };
            parentProduct.child_products.push(childProduct);
            this.updateChildProductSku(collection, parentProduct, childProduct);
        },
        
        isChildProductValid(childProduct) {
            return childProduct.product_sku && 
                   childProduct.product_description &&
                   childProduct.subsubcategory_id &&
                   childProduct.quantity_per_box > 0 &&
                   childProduct.fob_price > 0 &&
                   childProduct.cubes > 0;
        },
        
        updateChildProductSku(collection, parentProduct, childProduct) {
            if (!parentProduct.product_sku) return;
            
            const parentSkuParts = parentProduct.product_sku.split('-');
            if (parentSkuParts.length < 4) return;
            
            const parentBase = parentSkuParts.slice(0, 3).join('-');
            
            let subsubcategoryCode = "000";
            if (childProduct.subsubcategory_id && this.subsubcategoryCodes[childProduct.subsubcategory_id]) {
                subsubcategoryCode = this.subsubcategoryCodes[childProduct.subsubcategory_id].code || "000";
            }
            
            let sizeCode = "00";
            if (childProduct.size_id && this.sizeCodes[childProduct.size_id]) {
                sizeCode = this.sizeCodes[childProduct.size_id].code || "00";
            } else if (parentProduct.size_id && this.sizeCodes[parentProduct.size_id]) {
                sizeCode = this.sizeCodes[parentProduct.size_id].code || "00";
            }
            
            const childNumber = parentProduct.child_products.indexOf(childProduct) + 1;
            
            childProduct.product_sku = `${parentBase}-${subsubcategoryCode}-${sizeCode}-${childNumber}`;
            this.generateChildProductDescription(collection, parentProduct, childProduct);
        },
        
        generateChildProductDescription(collection, parentProduct, childProduct) {
            const parts = [];
            
            if (collection.name) {
                parts.push(collection.name.toUpperCase());
            }
            
            if (childProduct.subsubcategory_id && this.subsubcategoryCodes[childProduct.subsubcategory_id]) {
                const subsubcategory = this.subsubcategoryCodes[childProduct.subsubcategory_id].name;
                if (subsubcategory) parts.push(subsubcategory.toUpperCase());
            }
            
            if (collection.color_id && this.colorCodes[collection.color_id]) {
                const color = this.colorCodes[collection.color_id].name;
                if (color) parts.push(color.toUpperCase());
            }
            
            if (collection.material_id && this.materialCodes[collection.material_id]) {
                const material = this.materialCodes[collection.material_id].name;
                if (material) parts.push(material.toUpperCase());
            }
            
            if (childProduct.size_id && this.sizeCodes[childProduct.size_id]) {
                const size = this.sizeCodes[childProduct.size_id].name;
                if (size) parts.push(size.toUpperCase());
            } else if (parentProduct.size_id && this.sizeCodes[parentProduct.size_id]) {
                const size = this.sizeCodes[parentProduct.size_id].name;
                if (size) parts.push(size.toUpperCase());
            }
            
            childProduct.product_description = parts.join("-");
        },
        
        removeChildProduct(parentProduct, childIndex) {
            parentProduct.child_products.splice(childIndex, 1);
            
            parentProduct.child_products.forEach((child, idx) => {
                this.updateChildProductSkuForIndex(parentProduct, child, idx + 1);
            });
        },
        
        updateChildProductSkuForIndex(parentProduct, childProduct, newIndex) {
            const skuParts = childProduct.product_sku.split('-');
            if (skuParts.length >= 4) {
                skuParts[skuParts.length - 1] = newIndex.toString();
                childProduct.product_sku = skuParts.join('-');
            }
        },
        
        saveChildProduct(collection, parentProduct, childProduct) {
            this.items_being_submitted = true;
            
            const measurements = {};
            if (childProduct.height) measurements.height = parseFloat(childProduct.height);
            if (childProduct.length) measurements.length = parseFloat(childProduct.length);
            if (childProduct.depth) measurements.depth = parseFloat(childProduct.depth);
            if (childProduct.measurement_unit) measurements.unit = childProduct.measurement_unit;
            
            const properties = {
                quantity_per_box: parseInt(childProduct.quantity_per_box) || 1,
                low_inventory: childProduct.low_inventory_notify || false,
                is_parent: false,
                has_child_products: false,
                parent_product_id: parentProduct.product_id,
                subcategory: parentProduct.subcategory_id,
                type: collection.type_id,
                color: collection.color_id,
                size: childProduct.size_id || parentProduct.size_id,
                material: collection.material_id,
                functionality_ids: parentProduct.functionality_ids,
                bed_type: parentProduct.bed_type_id,
                style: parentProduct.style_id,
                sectional_shape: parentProduct.sectional_shape_id,
                features: childProduct.features || "",
                measurements: measurements,
                weight: childProduct.weight ? parseFloat(childProduct.weight) : null,
                assembly_time: childProduct.assembly_time ? parseInt(childProduct.assembly_time) : null,
                vendor_sku: childProduct.vendor_sku || ""
            };
            
            const apiItem = {
                product_sku: childProduct.product_sku,
                product_name: childProduct.product_description,
                product_display_name: `${collection.name} - Component`,
                category_id: collection.category_id,
                vendor_id: collection.vendor_id,
                type_id: collection.type_id,
                subcategory_id: parentProduct.subcategory_id,
                subsubcategory_id: childProduct.subsubcategory_id,
                color_id: collection.color_id,
                size_id: childProduct.size_id || parentProduct.size_id,
                material_id: collection.material_id,
                functionality_ids: parentProduct.functionality_ids,
                bed_type_id: parentProduct.bed_type_id,
                style_id: parentProduct.style_id,
                sectional_shape_id: parentProduct.sectional_shape_id,
                fob_price: childProduct.fob_price ? parseFloat(childProduct.fob_price) : 0.0,
                selling_price: childProduct.selling_price ? parseFloat(childProduct.selling_price) : 0.0,
                wholesale_price: childProduct.wholesale_price ? parseFloat(childProduct.wholesale_price) : 0.0,
                special_discount_price: childProduct.special_discount_price ? parseFloat(childProduct.special_discount_price) : 0.0,
                cubes: childProduct.cubes ? parseFloat(childProduct.cubes) : 0.0,
                spiff: childProduct.spiff ? parseFloat(childProduct.spiff) : 0.0,
                features: childProduct.features || "",
                weight: childProduct.weight ? parseFloat(childProduct.weight) : null,
                assembly_time: childProduct.assembly_time ? parseInt(childProduct.assembly_time) : null,
                vendor_sku: childProduct.vendor_sku || "",
                product_type: 1,
                status: "active",
                properties: properties,
                parent_product_id: parentProduct.product_id,
                image_url: childProduct.image_url || null
            };
            
            const path = "/api/product/add_batch_products/";
            const data = { items: [apiItem] };
            const that = this;
            
            const successCallback = function(response) {
                if (response.data.error) {
                    that.$bvToast.toast(response.data.error, {
                        title: "Error",
                        variant: "danger",
                        autoHideDelay: 3000
                    });
                } else {
                    childProduct.saved = true;
                    childProduct.product_id = response.data.result[0].id;
                    that.savedChildProductsCount++;
                    
                    that.$bvToast.toast("Component saved successfully", {
                        title: "Success",
                        variant: "success",
                        autoHideDelay: 2000
                    });
                }
                that.items_being_submitted = false;
            };
            
            const errorCallback = function(error) {
                console.error("Save error:", error);
                that.$bvToast.toast("Failed to save component", {
                    title: "Error",
                    variant: "danger",
                    autoHideDelay: 3000
                });
                that.items_being_submitted = false;
            };
            
            Requestor.postSyncP(path, data, this, successCallback, errorCallback);
        },
        
        // ============================================
        // GROUP CREATION METHODS
        // ============================================
        
        updateGroupSelections() {
            this.selectedForGrouping = [];
            this.groupValidationError = null;
            
            const targetCollections = this.getFilteredCollections();
            
            targetCollections.forEach(collection => {
                collection.products.filter(p => !p.parent_product_id && p.saved).forEach(product => {
                    if (product.includeInGroup) {
                        this.selectedForGrouping.push({
                            ...product,
                            collection_id: collection.id,
                            collection_name: collection.name,
                            category_id: collection.category_id,
                            vendor_id: collection.vendor_id,
                            type_id: collection.type_id,
                            color_id: collection.color_id,
                            material_id: collection.material_id,
                            bed_type_id: product.bed_type_id,
                            style_id: product.style_id,
                            sectional_shape_id: product.sectional_shape_id,
                            features: product.features,
                            weight: product.weight,
                            assembly_time: product.assembly_time,
                            vendor_sku: product.vendor_sku
                        });
                    }
                });
            });
            
            this.validateGroupSelections();
        },
        
        validateGroupSelections() {
            if (this.selectedForGrouping.length === 0) {
                this.groupValidationError = null;
                return true;
            }
            
            const collectionIds = [...new Set(this.selectedForGrouping.map(p => p.collection_id))];
            if (collectionIds.length > 1) {
                this.groupValidationError = "All products must be from the same collection.";
                return false;
            }
            
            const categoryIds = [...new Set(this.selectedForGrouping.map(p => p.category_id))];
            if (categoryIds.length > 1) {
                this.groupValidationError = "All products must be from the same category.";
                return false;
            }
            
            const categoryId = categoryIds[0];
            
            const subcategoryIds = this.selectedForGrouping.map(p => p.subcategory_id);
            const uniqueSubcategories = [...new Set(subcategoryIds)];
            if (uniqueSubcategories.length !== subcategoryIds.length) {
                this.groupValidationError = "Duplicate subcategories are not allowed in a group.";
                return false;
            }
            
            if (this.isBedroomCategory(categoryId)) {
                const bedSubcategories = ['BED', 'FULL BED', 'QUEEN BED', 'KING BED', 'TWIN BED', 'DOUBLE BED'];
                const selectedBedSubcategories = this.selectedForGrouping.filter(p => {
                    const subcategoryName = this.getSubcategoryName(p.subcategory_id).toUpperCase();
                    return bedSubcategories.some(bed => subcategoryName.includes(bed));
                });
                
                if (selectedBedSubcategories.length > 1) {
                    this.groupValidationError = "For BEDROOM category, you cannot select multiple beds in the same group.";
                    return false;
                }
            }
            
            if (this.isLivingRoomCategory(categoryId)) {
                const subcategories = this.selectedForGrouping.map(p => 
                    this.getSubcategoryName(p.subcategory_id).toUpperCase()
                );
                
                const hasSofa = subcategories.some(s => s.includes('SOFA'));
                const hasLoveseat = subcategories.some(s => s.includes('LOVESEAT') || s.includes('LOVE SEAT'));
                const hasChair = subcategories.some(s => s.includes('CHAIR'));
                const hasOttoman = subcategories.some(s => s.includes('OTTOMAN'));
                
                if (!hasSofa || !hasLoveseat) {
                    this.groupValidationError = "Living room groups must include at least SOFA and LOVESEAT.";
                    return false;
                }
                
                if (this.selectedGroupSize === 2) {
                    if (subcategories.length !== 2 || !hasSofa || !hasLoveseat) {
                        this.groupValidationError = "2PC group must be exactly SOFA + LOVESEAT.";
                        return false;
                    }
                } else if (this.selectedGroupSize === 3) {
                    if (!hasChair) {
                        this.groupValidationError = "3PC group must include SOFA, LOVESEAT, and CHAIR.";
                        return false;
                    }
                } else if (this.selectedGroupSize === 4) {
                    if (!hasChair || !hasOttoman) {
                        this.groupValidationError = "4PC group must include SOFA, LOVESEAT, CHAIR, and OTTOMAN.";
                        return false;
                    }
                }
            }
            
            this.groupValidationError = null;
            return true;
        },
        
        generateCombinations(arr, size) {
            const results = [];
            
            const combine = (start, combo) => {
                if (combo.length === size) {
                    results.push([...combo]);
                    return;
                }
                
                for (let i = start; i < arr.length; i++) {
                    combo.push(arr[i]);
                    combine(i + 1, combo);
                    combo.pop();
                }
            };
            
            combine(0, []);
            return results;
        },
        
        selectAllCombinations() {
            this.generatedGroups.forEach(group => group.selected = true);
        },
        
        deselectAllCombinations() {
            this.generatedGroups.forEach(group => group.selected = false);
        },
        
        addGroupsToQueue() {
            const selectedGroups = this.generatedGroups.filter(group => group.selected);
            
            if (selectedGroups.length === 0) {
                this.$bvToast.toast("No groups selected", {
                    title: "Warning",
                    variant: "warning",
                    autoHideDelay: 2000
                });
                return;
            }
            
            selectedGroups.forEach(group => {
                const exists = this.groupsToCreate.some(g => g.product_sku === group.sku);
                if (!exists) {
                    this.groupsToCreate.push({
                        product_sku: group.sku,
                        product_name: group.product_name,
                        product_description: group.product_description || group.product_name,
                        category_id: group.category_id,
                        vendor_id: group.vendor_id,
                        subcategory_id: group.subcategory_id,
                        fob_price_total: group.editable_fob_price,
                        selling_price_total: group.editable_selling_price,
                        type_id: group.type_id,           
                        color_id: group.color_id,        
                        material_id: group.material_id,
                        cubes: group.cubes,
                        spiff: group.editable_spiff || 0,
                        is_box: group.is_box,
                        image_url: group.editable_image_url,
                        vendor_sku: group.editable_vendor_sku,
                        original_fob_price: group.fob_price_total,
                        original_selling_price: group.selling_price_total,
                        original_vendor_sku: group.vendor_skus,
                        original_image_url: group.image_url,
                        original_spiff: group.spiff,
                        setType: group.setType,
                        subcategoryWithQuantities: group.subcategoryWithQuantities,
                        children: group.children.map(child => ({
                            ...child,
                        }))
                    });
                }
            });
            
            this.generatedGroups = [];
            
            this.collections.forEach(collection => {
                collection.products.forEach(product => {
                    product.includeInGroup = false;
                });
            });
            
            this.selectedForGrouping = [];
            this.groupValidationError = null;
            
            this.$bvToast.toast(`Added ${selectedGroups.length} group(s) to queue`, {
                title: "Success",
                variant: "success",
                autoHideDelay: 2000
            });
        },
        
        removeGroup(index) {
            this.groupsToCreate.splice(index, 1);
        },
        
        async submitAllGroups() {
            if (this.groupsToCreate.length === 0) {
                this.$bvToast.toast("No groups to submit", {
                    title: "Warning",
                    variant: "warning",
                    autoHideDelay: 3000
                });
                return;
            }
            
            this.submittingGroups = true;
            
            try {
                let successCount = 0;
                let errorCount = 0;
                
                for (const group of this.groupsToCreate) {
                    try {
                        await this.saveGroupProduct(group);
                        successCount++;
                        this.savedGroupsCount++;
                    } catch (error) {
                        console.error("Error saving group:", error);
                        errorCount++;
                    }
                }
                
                if (successCount > 0) {
                    this.$bvToast.toast(`Successfully saved ${successCount} group(s)`, {
                        title: "Success",
                        variant: "success",
                        autoHideDelay: 3000
                    });
                    
                    this.groupsToCreate = this.groupsToCreate.filter((group, index) => {
                        return index >= successCount;
                    });
                }
                
                if (errorCount > 0) {
                    this.$bvToast.toast(`Failed to save ${errorCount} group(s)`, {
                        title: "Error",
                        variant: "danger",
                        autoHideDelay: 3000
                    });
                }
                
            } catch (error) {
                this.$bvToast.toast(`Error submitting groups: ${error.message}`, {
                    title: "Error",
                    variant: "danger",
                    autoHideDelay: 3000
                });
            } finally {
                this.submittingGroups = false;
            }
        },
        
        saveGroupProduct(group) {
            return new Promise((resolve, reject) => {
                const groupData = {
                    product_sku: group.product_sku,
                    product_name: group.product_name,
                    product_description: group.product_description || group.product_name,
                    category_id: group.category_id,
                    subcategory_id: group.subcategory_id,
                    type_id: group.type_id,        
                    color_id: group.color_id,       
                    material_id: group.material_id, 
                    vendor_id: group.vendor_id,
                    fob_price_total: group.fob_price_total,
                    selling_price_total: group.selling_price_total,
                    cubes: group.cubes,
                    spiff: group.spiff || 0,
                    is_box: group.is_box,
                    image_url: group.image_url || null,
                    vendor_sku: group.vendor_sku || '',
                    children: group.children.map(child => ({
                        product_id: child.product_id,
                        product_sku: child.product_sku,
                        product_name: child.product_name,
                        fob_price: child.fob_price,
                        quantity: child.quantity,
                        cubes: child.cubes,
                        image_url: child.image_url
                    }))
                };
                
                const path = "/api/product/add_single_group/";
                const that = this;
                
                const successCallback = function(response) {
                    if (response.data.error) {
                        reject(new Error(response.data.error));
                    } else {
                        resolve(response.data);
                    }
                };
                
                const errorCallback = function(error) {
                    reject(new Error(error.message || "Failed to save group"));
                };
                
                Requestor.postSyncP(path, groupData, this, successCallback, errorCallback);
            });
        },
        
        // ============================================
        // BULK SUBMISSION
        // ============================================
        
        async submitAllData() {
            const totalItems = this.totalUnsavedItems;
            if (totalItems === 0) return;
            
            if (!confirm(`Submit ${totalItems} items?`)) return;
            
            this.submittingAll = true;
            this.items_being_submitted = true;
            
            try {
                const allItems = [];
                
                // Add collection products
                this.collections.forEach(collection => {
                    if (!collection.saved && this.isCollectionValid(collection)) {
                        collection.saved = true;
                        this.savedCollectionsCount++;
                    }
                    
                    collection.products.filter(p => !p.parent_product_id && !p.saved).forEach(parentProduct => {
                        if (this.isParentProductValid(parentProduct)) {
                            const measurements = {};
                            if (parentProduct.height) measurements.height = parseFloat(parentProduct.height);
                            if (parentProduct.length) measurements.length = parseFloat(parentProduct.length);
                            if (parentProduct.depth) measurements.depth = parseFloat(parentProduct.depth);
                            if (parentProduct.measurement_unit) measurements.unit = parentProduct.measurement_unit;
                            
                            const properties = {
                                quantity_per_box: parseInt(parentProduct.quantity_per_box) || 1,
                                low_inventory: false,
                                is_parent: true,
                                has_child_products: parentProduct.has_child_products,
                                bed_type: parentProduct.bed_type_id,
                                style: parentProduct.style_id,
                                sectional_shape: parentProduct.sectional_shape_id,
                                features: parentProduct.features || "",
                                measurements: measurements,
                                weight: parentProduct.weight ? parseFloat(parentProduct.weight) : null,
                                assembly_time: parentProduct.assembly_time ? parseInt(parentProduct.assembly_time) : null,
                                vendor_sku: parentProduct.vendor_sku || "",
                                functionality_ids: parentProduct.functionality_ids || [],
                                detail_description: parentProduct.detail_description || ""
                            };
                            
                            allItems.push({
                                product_sku: parentProduct.product_sku,
                                product_name: parentProduct.product_description,
                                product_display_name: collection.name,
                                category_id: collection.category_id,
                                vendor_id: collection.vendor_id,
                                type_id: collection.type_id,
                                subcategory_id: parentProduct.subcategory_id,
                                color_id: collection.color_id,
                                size_id: parentProduct.size_id,
                                material_id: collection.material_id,
                                functionality_ids: parentProduct.functionality_ids,
                                bed_type_id: parentProduct.bed_type_id,
                                style_id: parentProduct.style_id,
                                sectional_shape_id: parentProduct.sectional_shape_id,
                                fob_price: parentProduct.fob_price ? parseFloat(parentProduct.fob_price) : 0.0,
                                selling_price: parentProduct.selling_price ? parseFloat(parentProduct.selling_price) : 0.0,
                                wholesale_price: parentProduct.wholesale_price ? parseFloat(parentProduct.wholesale_price) : 0.0,
                                special_discount_price: parentProduct.special_discount_price ? parseFloat(parentProduct.special_discount_price) : 0.0,
                                cubes: parentProduct.cubes ? parseFloat(parentProduct.cubes) : 0.0,
                                spiff: parentProduct.spiff ? parseFloat(parentProduct.spiff) : 0.0,
                                features: parentProduct.features || "",
                                weight: parentProduct.weight ? parseFloat(parentProduct.weight) : null,
                                assembly_time: parentProduct.assembly_time ? parseInt(parentProduct.assembly_time) : null,
                                vendor_sku: parentProduct.vendor_sku || "",
                                product_type: 1,
                                status: "active",
                                properties: properties,
                                detail_description: parentProduct.detail_description || "",
                                image_url: parentProduct.image_url || null
                            });
                        }
                    });
                    
                    collection.products.forEach(parentProduct => {
                        if (parentProduct.saved && parentProduct.has_child_products) {
                            parentProduct.child_products.filter(c => !c.saved).forEach(childProduct => {
                                if (this.isChildProductValid(childProduct)) {
                                    const measurements = {};
                                    if (childProduct.height) measurements.height = parseFloat(childProduct.height);
                                    if (childProduct.length) measurements.length = parseFloat(childProduct.length);
                                    if (childProduct.depth) measurements.depth = parseFloat(childProduct.depth);
                                    if (childProduct.measurement_unit) measurements.unit = childProduct.measurement_unit;
                                    
                                    const properties = {
                                        quantity_per_box: parseInt(childProduct.quantity_per_box) || 1,
                                        low_inventory: childProduct.low_inventory_notify || false,
                                        is_parent: false,
                                        has_child_products: false,
                                        parent_product_id: parentProduct.product_id,
                                        subcategory: parentProduct.subcategory_id,
                                        type: collection.type_id,
                                        color: collection.color_id,
                                        size: childProduct.size_id || parentProduct.size_id,
                                        material: collection.material_id,
                                        functionality_ids: parentProduct.functionality_ids,
                                        bed_type: parentProduct.bed_type_id,
                                        style: parentProduct.style_id,
                                        sectional_shape: parentProduct.sectional_shape_id,
                                        features: childProduct.features || "",
                                        measurements: measurements,
                                        weight: childProduct.weight ? parseFloat(childProduct.weight) : null,
                                        assembly_time: childProduct.assembly_time ? parseInt(childProduct.assembly_time) : null,
                                        vendor_sku: childProduct.vendor_sku || ""
                                    };
                                    
                                    allItems.push({
                                        product_sku: childProduct.product_sku,
                                        product_name: childProduct.product_description,
                                        product_display_name: `${collection.name} - Component`,
                                        category_id: collection.category_id,
                                        vendor_id: collection.vendor_id,
                                        type_id: collection.type_id,
                                        subcategory_id: parentProduct.subcategory_id,
                                        subsubcategory_id: childProduct.subsubcategory_id,
                                        color_id: collection.color_id,
                                        size_id: childProduct.size_id || parentProduct.size_id,
                                        material_id: collection.material_id,
                                        functionality_ids: parentProduct.functionality_ids,
                                        bed_type_id: parentProduct.bed_type_id,
                                        style_id: parentProduct.style_id,
                                        sectional_shape_id: parentProduct.sectional_shape_id,
                                        fob_price: childProduct.fob_price ? parseFloat(childProduct.fob_price) : 0.0,
                                        selling_price: childProduct.selling_price ? parseFloat(childProduct.selling_price) : 0.0,
                                        wholesale_price: childProduct.wholesale_price ? parseFloat(childProduct.wholesale_price) : 0.0,
                                        special_discount_price: childProduct.special_discount_price ? parseFloat(childProduct.special_discount_price) : 0.0,
                                        cubes: childProduct.cubes ? parseFloat(childProduct.cubes) : 0.0,
                                        spiff: childProduct.spiff ? parseFloat(childProduct.spiff) : 0.0,
                                        features: childProduct.features || "",
                                        weight: childProduct.weight ? parseFloat(childProduct.weight) : null,
                                        assembly_time: childProduct.assembly_time ? parseInt(childProduct.assembly_time) : null,
                                        vendor_sku: childProduct.vendor_sku || "",
                                        product_type: 1,
                                        status: "active",
                                        properties: properties,
                                        parent_product_id: parentProduct.product_id,
                                        image_url: childProduct.image_url || null
                                    });
                                }
                            });
                        }
                    });
                });
                
                // Add simple products
                this.simpleProducts.filter(p => !p.saved).forEach(product => {
                    if (this.isSimpleProductValid(product)) {
                        const properties = {
                            quantity_per_box: parseInt(product.quantity_per_box) || 1,
                            low_inventory: false,
                            is_parent: false,
                            has_child_products: false,
                            type: product.type_id,
                            color: product.color_id,
                            size: product.size_id,
                            material: product.material_id,
                            style: product.style_id,
                            vendor_sku: product.vendor_sku || ""
                        };
                        
                        allItems.push({
                            product_sku: product.product_sku.toUpperCase().trim(),
                            product_name: product.product_description.toUpperCase().trim(),
                            product_display_name: product.product_description,
                            category_id: product.category_id,
                            vendor_id: product.vendor_id,
                            subcategory_id: product.subcategory_id,
                            type_id: product.type_id,
                            color_id: product.color_id,
                            size_id: product.size_id,
                            material_id: product.material_id,
                            style_id: product.style_id,
                            fob_price: parseFloat(product.fob_price) || 0,
                            selling_price: parseFloat(product.selling_price) || 0,
                            wholesale_price: parseFloat(product.wholesale_price) || 0,
                            special_discount_price: parseFloat(product.special_discount_price) || 0,
                            cubes: parseFloat(product.cubes) || 0,
                            spiff: parseFloat(product.spiff) || 0,
                            product_type: 1,
                            status: "active",
                            properties: properties,
                            image_url: product.image_url || null
                        });
                    }
                });
                
                if (allItems.length > 0) {
                    await this.submitBatchProducts(allItems);
                }
                
                if (this.groupsToCreate.length > 0) {
                    await this.submitAllGroups();
                }
                
                this.showSuccessModal = true;
                
            } catch (error) {
                console.error("Submission error:", error);
                this.$bvToast.toast("Error submitting data", {
                    title: "Error",
                    variant: "danger",
                    autoHideDelay: 3000
                });
            } finally {
                this.submittingAll = false;
                this.items_being_submitted = false;
            }
        },
        
        submitBatchProducts(items) {
            return new Promise((resolve, reject) => {
                const path = "/api/product/add_batch_products/";
                const data = { items: items };
                const that = this;
                
                const successCallback = function(response) {
                    if (response.data.error) {
                        reject(new Error(response.data.error));
                    } else {
                        that.markItemsAsSaved(response.data.result);
                        resolve(response.data);
                    }
                };
                
                const errorCallback = function(error) {
                    reject(error);
                };
                
                Requestor.postSyncP(path, data, this, successCallback, errorCallback);
            });
        },
        
        markItemsAsSaved(savedItems) {
            savedItems.forEach(savedItem => {
                this.collections.forEach(collection => {
                    collection.products.forEach(product => {
                        if (product.product_sku === savedItem.sku) {
                            product.saved = true;
                            product.product_id = savedItem.id;
                            product.is_parent = savedItem.parent_product_id === null;
                        }
                        
                        if (product.child_products) {
                            product.child_products.forEach(child => {
                                if (child.product_sku === savedItem.sku) {
                                    child.saved = true;
                                    child.product_id = savedItem.id;
                                }
                            });
                        }
                    });
                });
                
                this.simpleProducts.forEach(product => {
                    if (product.product_sku === savedItem.sku) {
                        product.saved = true;
                        product.product_id = savedItem.id;
                    }
                });
            });
            
            this.savedParentProductsCount = this.collections.reduce((count, collection) => {
                return count + collection.products.filter(p => !p.parent_product_id && p.saved).length;
            }, 0);
            
            this.savedChildProductsCount = this.collections.reduce((count, collection) => {
                return count + collection.products.reduce((childCount, product) => {
                    return childCount + (product.child_products ? product.child_products.filter(c => c.saved).length : 0);
                }, 0);
            }, 0);
            
            this.savedSimpleProductsCount = this.simpleProducts.filter(p => p.saved).length;
        },
        
        resetAfterSuccess() {
            this.savedCollectionsCount = 0;
            this.savedParentProductsCount = 0;
            this.savedChildProductsCount = 0;
            this.savedGroupsCount = 0;
            this.savedSimpleProductsCount = 0;
            
            this.groupsToCreate = [];
            this.simpleProducts = [];
            
            this.addNewCollection();
        },
        
        // ============================================
        // HELPER METHODS
        // ============================================
        
        loadSubsubcategories(collection, subcategoryId) {
            return new Promise((resolve, reject) => {
                const cacheKey = `subsubcat_${subcategoryId}`;
                if (this.subsubcategoryCache[cacheKey]) {
                    collection.subsubcategoryOptions = this.subsubcategoryCache[cacheKey];
                    resolve();
                    return;
                }
                
                const path = "/api/category/get_subsubcategories/";
                const data = { subcategory_id: subcategoryId };
                const that = this;
                
                const successCallback = function(response) {
                    if (response.data.error) {
                        collection.subsubcategoryOptions = [];
                        resolve();
                        return;
                    }
                    
                    const options = response.data.result.map(subsubcat => ({
                        value: subsubcat.id,
                        text: subsubcat.subsubcategory_name,
                        code: subsubcat.subsubcategory_code
                    }));
                    
                    options.unshift({ value: null, text: "Select Component Type" });
                    
                    that.subsubcategoryCache[cacheKey] = options;
                    collection.subsubcategoryOptions = options;
                    
                    if (response.data.result && response.data.result.length > 0) {
                        response.data.result.forEach(subsubcat => {
                            that.subsubcategoryCodes[subsubcat.id] = {
                                code: subsubcat.subsubcategory_code || "000",
                                name: subsubcat.subsubcategory_name
                            };
                        });
                    }
                    
                    resolve();
                };
                
                const errorCallback = function(error) {
                    console.error("Failed to load sub-subcategories:", error);
                    collection.subsubcategoryOptions = [];
                    reject(error);
                };
                
                Requestor.postSyncP(path, data, this, successCallback, errorCallback);
            });
        }
    },
    
    created: function() {
        this.packet = JSON.parse(localStorage.packet);
        this.packet.userDetails = JSON.parse(localStorage.userDetails);
        this.vendors = this.packet.vendors;
        this.categories = this.packet.categories;
        
        if (!this.packet.locsubs) {
            this.packet.locsubs = [];
        }
        
        this.typeCodes = {};
        this.colorCodes = {};
        this.materialCodes = {};
        this.sizeCodes = {};
        this.subcategoryCodes = {}; 
        this.functionalityCodes = {};
        this.subsubcategoryCodes = {};
        this.bedTypeCodes = {};
        this.styleCodes = {};
        this.sectionalShapeCodes = {};
        this.subsubcategoryCache = {};
        
        const savedCollectionCounters = localStorage.getItem('collectionSkuCounters');
        if (savedCollectionCounters) {
            this.collectionCounters = JSON.parse(savedCollectionCounters);
        } else {
            this.collectionCounters = {};
        }
        
        this.addNewCollection();
        
        this.expandedCollections = {};
    }
}
</script>

<style scoped>
/* =========================================
   MODERN UI STYLES - COMPLETELY REDESIGNED
   ========================================= */

.mode-selector .btn {
    min-width: 180px;
    font-weight: 600;
    padding: 10px 20px;
    border-radius: 8px;
    transition: all 0.2s;
}

.mode-selector .btn.active {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-color: transparent;
}

.mode-selector .btn:not(.active):hover {
    background: #f8f9fa;
    transform: translateY(-1px);
}

.simple-product-card {
    border: 2px solid #ffc107;
    border-radius: 12px;
    overflow: hidden;
    transition: all 0.3s;
    background: white;
    animation: slideIn 0.3s ease;
}

.simple-product-card:hover {
    box-shadow: 0 8px 24px rgba(255, 193, 7, 0.2);
    transform: translateY(-2px);
}

.simple-product-card .card-header {
    background: #fff3cd;
    border-bottom: 2px solid #ffc107;
    padding: 12px 16px;
}

.simple-product-card .card-header h6 {
    font-weight: 600;
    color: #856404;
}

.simple-product-card .card-body {
    padding: 20px;
}

.simple-products-container {
    animation: slideIn 0.3s ease;
}

.functionality-chip {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border: none;
    color: white;
    font-size: 0.85rem;
    transition: all 0.2s;
}

.functionality-chip:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(102, 126, 234, 0.3);
}

.functionality-chip .b-icon-x:hover {
    transform: scale(1.2);
    color: #ffc107 !important;
}

.functionality-selector {
    background: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e9ecef;
}

.collection-card {
    border-radius: 12px;
    transition: all 0.3s;
    overflow: hidden;
}

.collection-card:hover {
    box-shadow: 0 8px 24px rgba(0,0,0,0.1);
}

.collection-card .card-header {
    background: white;
    border-bottom: 2px solid #f0f0f0;
    padding: 16px;
}

.collection-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    background: rgba(102, 126, 234, 0.1);
    border-radius: 12px;
}

.collection-form {
    padding: 20px;
}

.form-label {
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #495057;
    margin-bottom: 4px;
}

.form-label.required:after {
    content: " *";
    color: #dc3545;
}

.form-control-modern {
    border-radius: 8px;
    border: 1px solid #e0e0e0;
    transition: all 0.2s;
}

.form-control-modern:focus {
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.badge-counter {
    display: inline-block;
    min-width: 24px;
    height: 24px;
    background: #667eea;
    color: white;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
    line-height: 24px;
    text-align: center;
    padding: 0 8px;
    margin-left: 8px;
}

.empty-state {
    text-align: center;
    background: #f8f9fa;
    border-radius: 12px;
    padding: 32px;
}

.empty-state .empty-icon {
    font-size: 48px;
    color: #adb5bd;
    margin-bottom: 16px;
}

.parent-product-card {
    border: 1px solid #d4edda;
    border-radius: 12px;
    background: white;
    margin-bottom: 16px;
    overflow: hidden;
}

.product-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    background: #f8f9fa;
    border-bottom: 1px solid #dee2e6;
}

.product-status {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    background: rgba(40, 167, 69, 0.1);
    border-radius: 8px;
}

.product-details {
    padding: 16px;
}

.child-product-card {
    border: 1px solid #bee5eb;
    border-radius: 8px;
    background: white;
    margin-bottom: 12px;
}

.groups-panel {
    position: sticky;
    top: 20px;
    border-radius: 12px;
    border: none;
    box-shadow: 0 4px 20px rgba(0,0,0,0.05);
    max-height: calc(100vh - 40px);
    overflow-y: auto;
}

.groups-panel .card-header {
    background: linear-gradient(135deg, #ffc107 0%, #fd7e14 100%);
    color: white;
    border: none;
    padding: 16px;
}

.group-wizard .wizard-step {
    margin-bottom: 24px;
}

.step-indicator {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
}

.step-indicator .step-number {
    width: 28px;
    height: 28px;
    background: #e9ecef;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 14px;
    margin-right: 12px;
    color: #495057;
}

.step-indicator .step-title {
    font-weight: 600;
    font-size: 14px;
    color: #495057;
}

.product-select-item {
    padding: 8px;
    border-radius: 8px;
    transition: all 0.2s;
    margin-bottom: 4px;
}

.product-select-item:hover {
    background: #f8f9fa;
}

.product-select-item.selected {
    background: rgba(102, 126, 234, 0.05);
    border-left: 3px solid #667eea;
}

.collection-header {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    background: #f8f9fa;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
}

.collection-header:hover {
    background: #e9ecef;
}

.groups-list {
    max-height: 500px;
    overflow-y: auto;
}

.group-item {
    padding: 12px;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    margin-bottom: 8px;
    transition: all 0.2s;
}

.group-item:hover {
    border-color: #667eea;
    background: rgba(102, 126, 234, 0.02);
}

.group-item.selected {
    border-color: #28a745;
    background: rgba(40, 167, 69, 0.05);
}

.group-subcategories {
    margin-top: 4px;
}

.group-subcategories .badge {
    background: #e9ecef;
    color: #495057;
    font-weight: normal;
    padding: 4px 8px;
}

.group-pricing {
    font-size: 12px;
    color: #666;
}

.queue-section {
    border-top: 2px solid #ffc107;
    margin-top: 24px;
    padding-top: 16px;
}

.queue-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
}

.queue-list {
    max-height: 200px;
    overflow-y: auto;
}

.queue-item {
    padding: 12px;
    background: #fff3cd;
    border-left: 4px solid #ffc107;
    border-radius: 4px;
    margin-bottom: 8px;
}

.queue-item-products {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}

.queue-item-products .badge {
    background: rgba(255, 193, 7, 0.2);
    color: #856404;
}

.validation-error {
    margin-top: 16px;
}

.floating-action {
    position: fixed;
    bottom: 30px;
    right: 30px;
    z-index: 1000;
}

.floating-action .btn {
    width: 60px;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border: none;
    transition: all 0.3s;
}

.floating-action .btn:hover {
    transform: scale(1.1);
    box-shadow: 0 8px 24px rgba(102, 126, 234, 0.3) !important;
}

.floating-badge {
    position: absolute;
    top: -5px;
    right: -5px;
    min-width: 24px;
    height: 24px;
    background: #dc3545;
    color: white;
    border-radius: 12px;
    font-size: 12px;
    font-weight: bold;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 6px;
    animation: pulse 2s infinite;
}

.product-image-section,
.simple-product-image-section,
.child-product-image-section {
    position: relative;
}

.product-image-preview {
    position: relative;
    flex-shrink: 0;
}

.product-image-preview .img-thumbnail {
    cursor: pointer;
    transition: transform 0.2s;
}

.product-image-preview .img-thumbnail:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.image-placeholder {
    cursor: pointer;
    transition: all 0.2s;
}

.image-placeholder:hover {
    background: #e9ecef !important;
    transform: scale(1.02);
}

.image-upload-buttons {
    flex: 1;
}

.child-image-preview {
    position: relative;
    flex-shrink: 0;
}

.child-image-preview .img-thumbnail {
    cursor: pointer;
    transition: transform 0.2s;
}

.child-image-preview .img-thumbnail:hover {
    transform: scale(1.05);
}

.top-0 { top: 0; }
.right-0 { right: 0; }

@keyframes fadeIn {
    from { opacity: 0; transform: scale(0.9); }
    to { opacity: 1; transform: scale(1); }
}

.product-image-preview img {
    animation: fadeIn 0.3s ease;
}

.success-animation {
    animation: scaleUp 0.5s ease;
}

@keyframes scaleUp {
    0% { transform: scale(0); }
    100% { transform: scale(1); }
}

.stats-card {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 16px;
    text-align: center;
}

.stats-card h2 {
    margin-bottom: 4px;
    font-weight: 700;
}

.stats-card small {
    color: #666;
    text-transform: uppercase;
    font-size: 11px;
    font-weight: 600;
}

@keyframes slideIn {
    from {
        transform: translateY(20px);
        opacity: 0;
    }
    to {
        transform: translateY(0);
        opacity: 1;
    }
}

.collection-card,
.simple-product-card {
    animation: slideIn 0.3s ease;
}

@media (max-width: 992px) {
    .groups-panel {
        position: static;
        margin-top: 24px;
        max-height: none;
    }
    
    .floating-action {
        bottom: 20px;
        right: 20px;
    }
    
    .mode-selector .btn {
        min-width: 120px;
        font-size: 14px;
    }
}

@media (max-width: 768px) {
    .collection-card .card-header {
        flex-direction: column;
        align-items: flex-start;
    }
    
    .collection-card .card-header > div:last-child {
        margin-top: 12px;
        align-self: flex-end;
    }
    
    .product-header {
        flex-direction: column;
        align-items: flex-start;
    }
    
    .product-actions {
        margin-top: 12px;
        align-self: flex-end;
    }
    
    .mode-selector {
        display: flex;
        flex-direction: column;
    }
    
    .mode-selector .btn {
        margin-bottom: 5px;
    }
}

::-webkit-scrollbar {
    width: 8px;
    height: 8px;
}

::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
}

::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 10px;
}

::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
}

@media print {
    .floating-action,
    .groups-panel,
    .quick-actions,
    .mode-selector {
        display: none;
    }
}

.vld-overlay {
    background: rgba(255,255,255,0.9);
}

.vld-icon {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
}

.is-invalid {
    border-color: #dc3545;
}

.is-invalid:focus {
    box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.1);
}

.is-valid {
    border-color: #28a745;
}

.is-valid:focus {
    box-shadow: 0 0 0 3px rgba(40, 167, 69, 0.1);
}

.inventory-section {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 16px;
    margin-top: 16px;
}

.inventory-section .section-title {
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 12px;
    color: #28a745;
}

.inventory-history {
    max-height: 100px;
    overflow-y: auto;
    font-size: 12px;
}

.inventory-history div {
    padding: 4px 0;
}

.inventory-history div:last-child {
    border-bottom: none;
}

.low-inventory-form {
    padding: 10px;
}

@keyframes pulse {
    0% {
        transform: scale(1);
        opacity: 1;
    }
    50% {
        transform: scale(1.1);
        opacity: 0.8;
    }
    100% {
        transform: scale(1);
        opacity: 1;
    }
}

.group-editable-fob,
.group-editable-selling,
.group-editable-vendor-sku,
.group-editable-spiff,
.group-editable-image {
    border-top: 1px solid #e9ecef;
    padding-top: 8px;
}

.group-editable-fob:first-child,
.group-editable-selling:first-child,
.group-editable-vendor-sku:first-child,
.group-editable-spiff:first-child,
.group-editable-image:first-child {
    border-top: none;
    padding-top: 0;
}

/* Quantity Configuration Modal Styles */
.product-quantity-item {
    transition: all 0.2s;
}

.product-quantity-item:hover {
    background: #f8f9fa;
}

.variation-item {
    transition: all 0.2s;
    background: #f8f9fa;
}

.variation-item:hover {
    background: #e9ecef;
}

.totals-section {
    background: #f8f9fa;
    border: 1px solid #dee2e6;
}

.quantity-config-container {
    max-height: 70vh;
    overflow-y: auto;
}

.quantity-config-container .product-quantity-item .b-input {
    text-align: center;
}
</style>


// WEBPACK FOOTER //
// src/components/Products/AddProducts.vue